<template>
  <div class="email-builder-wrapper">
    <!-- Breadcrumbs Section (outside template-builder) -->
    <div class="breadcrumb-section">
      <breadcrumb style="margin-left: -15px">
        <breadcrumb-item>
          <a href="/mail/custom/landing">Email templates</a>
        </breadcrumb-item>
        <breadcrumb-item>
          <span>{{ templateData.id ? 'Edit template' : 'New template' }}</span>
        </breadcrumb-item>
      </breadcrumb>
    </div>

    <div class="template-builder">
      <!-- Loading Panel -->
      <loading-panel :loading="loading"></loading-panel>

      <!-- Builder Header -->
      <div class="builder-header">
        <div class="header-left">
          <div class="template-info">
            <h2 class="template-title">{{ templateData.name || 'New Template' }}</h2>
            <div class="row mt-2">
              <div class="col-auto pr-0" v-if="templateData.template_type?.id">
                <span
                  :class="['badge', 'badge-default']"
                  :style="{ backgroundColor: templateData.template_type?.color, color: '#fff' }"
                >
                  <i :class="'tim-icons icon-' + templateData.template_type?.icon" class="mr-1"></i>
                  {{ templateData.template_type?.name }}
                 </span>
              </div>
              <div class="col-auto pr-0" v-else>
                <span
                  :class="['badge', 'badge-default']"
                  :style="{ backgroundColor: templateData.template_type_color, color: '#fff' }"
                >
                  <i :class="'tim-icons icon-' + templateData.template_type_icon" class="mr-1"></i>
                  {{ templateData.template_type }}
                 </span>
              </div>
              <div class="col-auto d-flex align-items-center">
                <save-status :dirty="hasUnsavedChanges" :saved="!!templateData.id" />
              </div>
            </div>
          </div>
        </div>
        <div class="header-actions">
          <el-dropdown @command="handleMenuAction">
            <base-button type="default" size="sm">
              <i class="tim-icons icon-settings-gear-63 mr-2"></i>
              Settings
              <i class="tim-icons icon-minimal-down ml-2"></i>
            </base-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="template-settings">
                  <i class="tim-icons icon-single-copy-04 mr-2"></i>
                  Template settings
                </el-dropdown-item>
                <el-dropdown-item command="template-availability">
                  <i class="tim-icons el-icon-view mr-2"></i>
                  Template availability
                </el-dropdown-item>
                <el-dropdown-item divided command="test-email">
                  <i class="tim-icons icon-send mr-2"></i>
                  Send test email
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <base-button type="info" size="sm" @click="saveAsDraft()" :disabled="loading">
            <i class="tim-icons icon-cloud-upload-94 mr-2"></i>
            Save draft
          </base-button>
          <base-button type="primary" size="sm" @click="saveAndActivate()" :disabled="loading">
            <i class="tim-icons icon-check-2 mr-2"></i>
            Save & activate
          </base-button>
        </div>
      </div>



      <!-- Main Builder Interface -->
      <div class="builder-container">
        <!-- Left Sidebar - Components -->
        <div class="components-sidebar">
          <!-- Components Library -->
          <div class="sidebar-section">
            <h4 class="section-title">Standard Components</h4>
            <div class="components-grid">
              <div
                class="component-item columns-item"
                @click="addRow()"
              >
                <i class="el-icon-s-grid"></i>
                <span>Columns</span>
              </div>
              <div
                v-for="component in availableComponents"
                :key="component.type"
                class="component-item"
                draggable="true"
                @dragstart="onComponentDragStart($event, component)"
                @click="addComponent(component)"
              >
                <i :class="component.icon"></i>
                <span>{{ gameToEventLabel(component.name) }}</span>
              </div>
            </div>
          </div>

          <div class="mt-3">
            <h4 class="section-title">Custom Components</h4>
            <div class="components-grid">
              <div
                v-for="component in customComponents"
                :key="component.type"
                class="component-item"
                draggable="true"
                @dragstart="onComponentDragStart($event, component)"
                @click="addComponent(component)"
              >
                <i :class="component.icon"></i>
                <span>{{ gameToEventLabel(component.name) }}</span>
              </div>
            </div>
          </div>

          <div class="mt-3">
            <h4 class="section-title">Attachments</h4>
            <div class="components-grid">
              <div
                v-for="component in attachmentComponents"
                :key="component.type"
                class="component-item"
                :class="{ 'selected': isAttachmentSelected(component.type) }"
                draggable="true"
                @dragstart="onComponentDragStart($event, component)"
                @click="addComponent(component)"
              >
                <i :class="component.icon"></i>
                <span>{{ gameToEventLabel(component.name) }}</span>
                <div v-if="isAttachmentSelected(component.type)" class="attachment-checkmark">
                  <i class="tim-icons icon-check-2"></i>
                </div>
              </div>
            </div>
          </div>

          <!--          <div class="sidebar-section">-->
          <!--            <h4 class="section-title">Variables</h4>-->
          <!--            <div class="variables-list">-->
          <!--              <div-->
          <!--                v-for="variable in availableVariables"-->
          <!--                :key="variable.key"-->
          <!--                class="variable-item"-->
          <!--                @click="insertVariable(variable)"-->
          <!--              >-->
          <!--                <code>{{ variable.syntax }}</code>-->
          <!--                <span>{{ variable.description }}</span>-->
          <!--              </div>-->
          <!--            </div>-->
          <!--          </div>-->

        </div>

        <!-- Center Canvas Area -->
        <div class="canvas-area">
          <div class="canvas-header">
<!--            <div class="canvas-tabs">-->
<!--            <span-->
<!--              :class="['tab', { active: activeTab === 'design' }]"-->
<!--              @click="activeTab = 'design'"-->
<!--            >-->
<!--              Design-->
<!--            </span>-->
<!--              <span-->
<!--                :class="['tab', { active: activeTab === 'preview' }]"-->
<!--                @click="activeTab = 'preview'"-->
<!--              >-->
<!--              Preview-->
<!--            </span>-->
<!--              <span-->
<!--                :class="['tab', { active: activeTab === 'html' }]"-->
<!--                @click="activeTab = 'html'"-->
<!--              >-->
<!--              HTML-->
<!--            </span>-->
<!--            </div>-->
            <div class="canvas-tools">
              <svg
                class="ai-toggle"
                :class="{ active: aiPanelOpen }"
                title="Generate with Biku"
                @click="aiPanelOpen = !aiPanelOpen"
                viewBox="0 0 24 24"
                fill="currentColor"
                role="button"
                aria-label="Generate with Biku"
              >
                <path d="M9 3.5c.2 0 .38.13.44.32l1.06 3.6c.33 1.1 1.19 1.96 2.29 2.29l3.6 1.06a.46.46 0 0 1 0 .88l-3.6 1.06a3.44 3.44 0 0 0-2.29 2.29l-1.06 3.6a.46.46 0 0 1-.88 0l-1.06-3.6a3.44 3.44 0 0 0-2.29-2.29l-3.6-1.06a.46.46 0 0 1 0-.88l3.6-1.06a3.44 3.44 0 0 0 2.29-2.29l1.06-3.6A.46.46 0 0 1 9 3.5Z" />
                <path d="M18 2c.14 0 .27.09.31.23l.5 1.7c.19.63.68 1.12 1.31 1.31l1.7.5a.33.33 0 0 1 0 .62l-1.7.5c-.63.19-1.12.68-1.31 1.31l-.5 1.7a.33.33 0 0 1-.62 0l-.5-1.7a1.97 1.97 0 0 0-1.31-1.31l-1.7-.5a.33.33 0 0 1 0-.62l1.7-.5c.63-.19 1.12-.68 1.31-1.31l.5-1.7A.33.33 0 0 1 18 2Z" />
              </svg>
              <i class="tim-icons icon-tablet-2 device-icons-start" :class="{ active: deviceView === 'desktop' }" @click="setDeviceView('desktop')"></i>
              <i class="tim-icons icon-mobile" :class="{ active: deviceView === 'mobile' }" @click="setDeviceView('mobile')"></i>
            </div>
          </div>

          <!-- Design Tab -->
          <!-- AI generation panel: lives on the dark chrome, not the light canvas -->
          <ai-template-prompt
            v-if="activeTab === 'design' && showAiPrompt"
            :has-content="!canvasIsEmpty"
            :loading="aiLoading"
            :error="aiError"
            class="canvas-ai-prompt"
            @close="closeAiPrompt"
            @submit="onAiSubmit" />

          <div v-if="activeTab === 'design'" class="canvas-content" :class="{ generating: aiLoading }">
            <!-- Local lock while AI modifies the saved template: edits made now
                 would be lost when the canvas reloads on completion -->
            <div v-if="aiLoading" class="canvas-generating-veil">
              <span class="canvas-generating-veil-pill">
                <i class="el-icon-loading"></i>
                Designing your content…
              </span>
            </div>
            <div
              :class="['email-canvas', `canvas-${deviceView}`]"
              @drop="onCanvasDrop($event)"
              @dragover.prevent
              @dragenter.prevent
              :style="{ backgroundColor: templateData.layout?.backgroundColor }"
            >
              <!-- Email Container -->
              <div class="email-container" :style="getEmailContainerStyles()">
                <!-- Email Header -->
                <div class="email-header-section">
                  <div
                    class="drag-zone"
                    @drop="onSectionDrop($event, 'header')"
                    @dragover.prevent
                    @dragenter.prevent
                  >
                    <draggable
                      v-model="templateComponents.header"
                      :options="{ animation: 200, handle: '.drag-handle', ghostClass: 'ghost' }"
                      @start="drag = true"
                      @end="drag = false"
                    >
                      <template v-for="(item, index) in templateComponents.header">
                        <div
                          v-if="item.type === 'row'"
                          :key="item.id"
                          :class="['email-row', { selected: selectedComponent === item.id }]"
                          @click.stop="selectComponent(item.id)"
                        >
                          <div class="row-columns" :style="{ display: 'grid', gridTemplateColumns: item.layout.gridTemplate }">
                            <div
                              v-for="(column, colIndex) in item.columns"
                              :key="colIndex"
                              class="row-column"
                              @drop="onColumnDrop($event, item.id, colIndex)"
                              @dragover.prevent
                              @dragenter.prevent
                            >
                              <div v-if="column.length === 0" class="empty-column">
                                <span>Drop component</span>
                              </div>
                              <div
                                v-for="(component, compIndex) in column"
                                :key="component.id"
                                :class="['column-component-wrapper', { selected: selectedComponent === component.id }]"
                                @click.stop="selectComponent(component.id)"
                              >
                                <div class="column-component-controls">
                                  <button class="column-control-btn" @click.stop="deleteFromColumn(item.id, colIndex, compIndex)" title="Delete">
                                    <i class="tim-icons icon-trash-simple"></i>
                                  </button>
                                </div>
                                <component
                                  :is="getComponentType(component.type)"
                                  :component-data="component"
                                  :selected="selectedComponent === component.id"
                                  @edit="editComponent(component)"
                                />
                              </div>
                            </div>
                          </div>
                          <div class="row-controls">
                            <button class="row-control-btn" @click.stop="deleteRow('header', index)">
                              <i class="tim-icons icon-trash-simple"></i>
                            </button>
                          </div>
                        </div>
                        <div
                          v-else
                          :key="item.id"
                          :class="['component-wrapper', { selected: selectedComponent === item.id }]"
                          @click="handleComponentClick(item.id, $event)"
                        >
                          <div class="component-controls">
                            <button class="component-control-btn" @click.stop="deleteComponent('header', index)">
                              <i class="tim-icons icon-trash-simple"></i>
                            </button>
                          </div>
                          <component
                            :is="getComponentType(item.type)"
                            :component-data="item"
                            :selected="selectedComponent === item.id"
                            @delete="deleteComponent('header', index)"
                            @edit="editComponent(item)"
                          />
                        </div>
                      </template>
                    </draggable>
                  </div>
                </div>

                <!-- Email Body -->
                <div class="email-body-section">
                  <div
                    class="drag-zone"
                    @drop="onSectionDrop($event, 'body')"
                    @dragover.prevent
                    @dragenter.prevent
                  >
                    <div v-if="templateComponents.body.length === 0" class="empty-zone main-empty">
                      <i class="tim-icons icon-email-85"></i>
                      <h4>Start Building Your Email</h4>
                      <p>Add rows and drag components into columns, or add components directly</p>
                    </div>
                    <draggable
                      v-model="templateComponents.body"
                      :options="{ animation: 200, handle: '.drag-handle', ghostClass: 'ghost' }"
                      @start="drag = true"
                      @end="drag = false"
                    >
                      <template v-for="(item, index) in templateComponents.body">
                        <div
                          v-if="item.type === 'row'"
                          :key="item.id"
                          :class="['email-row', { selected: selectedComponent === item.id }]"
                          @click.stop="selectComponent(item.id)"
                        >
                          <div class="row-columns" :style="{ display: 'grid', gridTemplateColumns: item.layout.gridTemplate }">
                            <div
                              v-for="(column, colIndex) in item.columns"
                              :key="colIndex"
                              class="row-column"
                              @drop="onColumnDrop($event, item.id, colIndex)"
                              @dragover.prevent
                              @dragenter.prevent
                            >
                              <div v-if="column.length === 0" class="empty-column">
                                <span>Drop component</span>
                              </div>
                              <div
                                v-for="(component, compIndex) in column"
                                :key="component.id"
                                :class="['column-component-wrapper', { selected: selectedComponent === component.id }]"
                                @click.stop="selectComponent(component.id)"
                              >
                                <div class="column-component-controls">
                                  <button class="column-control-btn" @click.stop="deleteFromColumn(item.id, colIndex, compIndex)" title="Delete">
                                    <i class="tim-icons icon-trash-simple"></i>
                                  </button>
                                </div>
                                <component
                                  :is="getComponentType(component.type)"
                                  :component-data="component"
                                  :selected="selectedComponent === component.id"
                                  @edit="editComponent(component)"
                                />
                              </div>
                            </div>
                          </div>
                          <div class="row-controls">
                            <button class="row-control-btn drag-handle" title="Drag to reorder">
                              <i class="el-icon-rank"></i>
                            </button>
                            <button class="row-control-btn" @click.stop="deleteRow('body', index)">
                              <i class="tim-icons icon-trash-simple"></i>
                            </button>
                          </div>
                        </div>
                        <div
                          v-else
                          :key="item.id"
                          :class="['component-wrapper', { selected: selectedComponent === item.id }]"
                          @click="handleComponentClick(item.id, $event)"
                        >
                          <div class="component-controls">
                            <button class="component-control-btn drag-handle" title="Drag to reorder">
                              <i class="el-icon-rank"></i>
                            </button>
                            <button class="component-control-btn" @click.stop="deleteComponent('body', index)">
                              <i class="tim-icons icon-trash-simple"></i>
                            </button>
                          </div>
                          <component
                            :is="getComponentType(item.type)"
                            :component-data="item"
                            :selected="selectedComponent === item.id"
                            @delete="deleteComponent('body', index)"
                            @edit="editComponent(item)"
                          />
                        </div>
                      </template>
                    </draggable>
                  </div>
                </div>

                <!-- Email Footer -->
                <div class="email-footer-section">
                  <div
                    class="drag-zone"
                    @drop="onSectionDrop($event, 'footer')"
                    @dragover.prevent
                    @dragenter.prevent
                  >
                    <draggable
                      v-model="templateComponents.footer"
                      :options="{ animation: 200, handle: '.drag-handle', ghostClass: 'ghost' }"
                      @start="drag = true"
                      @end="drag = false"
                    >
                      <template v-for="(item, index) in templateComponents.footer">
                        <div
                          v-if="item.type === 'row'"
                          :key="item.id"
                          :class="['email-row', { selected: selectedComponent === item.id }]"
                          @click.stop="selectComponent(item.id)"
                        >
                          <div class="row-columns" :style="{ display: 'grid', gridTemplateColumns: item.layout.gridTemplate }">
                            <div
                              v-for="(column, colIndex) in item.columns"
                              :key="colIndex"
                              class="row-column"
                              @drop="onColumnDrop($event, item.id, colIndex)"
                              @dragover.prevent
                              @dragenter.prevent
                            >
                              <div v-if="column.length === 0" class="empty-column">
                                <span>Drop component</span>
                              </div>
                              <div
                                v-for="(component, compIndex) in column"
                                :key="component.id"
                                :class="['column-component-wrapper', { selected: selectedComponent === component.id }]"
                                @click.stop="selectComponent(component.id)"
                              >
                                <div class="column-component-controls">
                                  <button class="column-control-btn" @click.stop="deleteFromColumn(item.id, colIndex, compIndex)" title="Delete">
                                    <i class="tim-icons icon-trash-simple"></i>
                                  </button>
                                </div>
                                <component
                                  :is="getComponentType(component.type)"
                                  :component-data="component"
                                  :selected="selectedComponent === component.id"
                                  @edit="editComponent(component)"
                                />
                              </div>
                            </div>
                          </div>
                          <div class="row-controls">
                            <button class="row-control-btn" @click.stop="deleteRow('footer', index)">
                              <i class="tim-icons icon-trash-simple"></i>
                            </button>
                          </div>
                        </div>
                        <div
                          v-else
                          :key="item.id"
                          :class="['component-wrapper', { selected: selectedComponent === item.id }]"
                          @click="handleComponentClick(item.id, $event)"
                        >
                          <div class="component-controls">
                            <button class="component-control-btn" @click.stop="deleteComponent('footer', index)">
                              <i class="tim-icons icon-trash-simple"></i>
                            </button>
                          </div>
                          <component
                            :is="getComponentType(item.type)"
                            :component-data="item"
                            :selected="selectedComponent === item.id"
                            @delete="deleteComponent('footer', index)"
                            @edit="editComponent(item)"
                          />
                        </div>
                      </template>
                    </draggable>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Sidebar - Properties -->
        <div class="properties-sidebar">
          <!-- Row Layout Settings (only show when row is selected) -->
          <div v-if="selectedComponent && getSelectedItem()?.type === 'row'" class="sidebar-section">
            <div class="section-header">
              <h4 class="section-title">Row Layout</h4>
              <base-button type="danger" simple size="sm" @click="deleteSelectedComponent()">
                <i class="tim-icons icon-trash-simple"></i>
              </base-button>
            </div>
            <div class="layout-grid">
              <div
                v-for="layout in layoutOptions"
                :key="layout.id"
                :class="['layout-option', { active: getSelectedItem()?.layout?.id === layout.id }]"
                @click="updateRowLayout(layout)"
              >
                <div class="layout-preview">
                  <div
                    v-for="(width, index) in layout.widths"
                    :key="index"
                    class="layout-column"
                    :style="{ width: width }"
                  ></div>
                </div>
                <span>{{ layout.name }}</span>
              </div>
            </div>
          </div>

          <!-- Component Properties (only show when component is selected) -->
          <div v-if="selectedComponent && getSelectedItem()?.type !== 'row'" class="sidebar-section">
            <div class="section-header">
              <h4 class="section-title">Component properties
                <i class="tim-icons text-danger icon-trash-simple ml-2" style="cursor:pointer;" @click="deleteSelectedComponent()"></i>
              </h4>
            </div>

            <!-- Dynamic component properties based on selected component -->
            <div>
              <div v-if="getSelectedComponent()?.type === 'text'" class="text-properties">
                <div style="padding: 0.75rem;">
                  <div style="display: flex; gap: 1rem; padding: 1rem; background: rgba(225, 78, 202, 0.1); border: 1px solid rgba(225, 78, 202, 0.3); border-radius: 0.5rem;">
                    <i class="tim-icons icon-bulb-63" style="font-size: 1.5rem; color: #e14eca; flex-shrink: 0;"></i>
                    <div>
                      <p style="margin: 0 0 0.25rem 0; font-weight: 600; color: rgba(255, 255, 255, 0.9); font-size: 0.875rem;">Text editor active</p>
                      <p style="margin: 0; font-size: 0.75rem; color: rgba(255, 255, 255, 0.7); line-height: 1.4;">Use the rich text editor on the canvas to format your text, change fonts, add links, and more.</p>
                    </div>
                  </div>

                  <!-- Smart values popover -->
                  <div class="smart-values-trigger mt-3">
                    <el-popover
                      placement="left-start"
                      width="420"
                      trigger="manual"
                      popper-class="smart-values-popover"
                      v-model="showSmartValues"
                    >
                      <SmartValuesPanel
                        v-if="showSmartValues"
                        @insert-value="onSmartValueInsert"
                        @close="showSmartValues = false"
                      />
                      <base-button
                        slot="reference"
                        :type="showSmartValues ? 'primary' : 'default'"
                        size="sm"
                        class="smart-values-btn"
                        @click="toggleSmartValuesPopover"
                      >
                        <span class="sv-btn-icon">{ }</span>
                        Smart variables
                      </base-button>
                    </el-popover>
                  </div>

                  <!-- Add this new appearance section -->
                  <div class="appearance-section mt-3">
                    <h5 class="subsection-title">Appearance</h5>
                    <div class="row">
                      <div class="col">
                        <label class="text-white opacity-7 small">Background color</label>
                        <base-input>
                          <div class="color-input-group">
                            <el-color-picker
                              :predefine="['#ffffff', '#f8f9fa', '#e5e7eb', '#fef3c7', '#dbeafe', '#e0e7ff', '#ede9fe', '#fce7f3', '#ffe4e6']"
                              v-model="getSelectedComponent().styles.backgroundColor"
                              @change="updateComponentStyles"
                              size="small"
                              :show-alpha="true"
                            ></el-color-picker>
                            <base-button
                              simple
                              type="primary"
                              size="sm"
                              @click="setTextBackgroundInherit"
                              class="inherit-btn"
                            >
                              Inherit
                            </base-button>
                          </div>
                        </base-input>
                      </div>
                    </div>
                  </div>

                  <!-- Minimal spacing controls -->
                  <div class="spacing-section">
                    <h5 class="subsection-title">Spacing</h5>
                    <div class="row">
                      <div class="col">
                        <label class="text-white opacity-7 small">Top margin</label>
                        <base-input>
                          <input
                            type="range"
                            min="0"
                            max="50"
                            step="2"
                            class="slider"
                            :value="parseInt(getSelectedComponent().styles.marginTop) || 16"
                            @input="updateTopMargin($event.target.value)"
                          >
                          <span class="range-value">{{ parseInt(getSelectedComponent().styles.marginTop) || 16 }}px</span>
                        </base-input>
                      </div>
                      <div class="col">
                        <label class="text-white opacity-7 small">Bottom margin</label>
                        <base-input >
                          <input
                            type="range"
                            min="0"
                            max="50"
                            step="2"
                            class="slider"
                            :value="parseInt(getSelectedComponent().styles.marginBottom) || 16"
                            @input="updateBottomMargin($event.target.value)"
                          >
                          <span class="range-value">{{ parseInt(getSelectedComponent().styles.marginBottom) || 16 }}px</span>
                        </base-input>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Button Component Properties -->
              <div v-if="getSelectedComponent()?.type === 'button'" >
                <!-- Button Content -->
                <h5 class="subsection-title">Button content</h5>
                <div class="form-group">
                  <label class="text-white opacity-7 small">Button text</label>
                  <base-input>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="getSelectedComponent().content"
                      placeholder="Button text"
                      @input="updateComponent"
                    >
                  </base-input>
                </div>

                <!-- Button Link -->
<!--                <div class="form-group">-->
<!--                  <label class="text-white opacity-7 small">Link URL</label>-->
<!--                  <base-input>-->
<!--                    <input-->
<!--                      type="text"-->
<!--                      class="form-control form-control-sm"-->
<!--                      v-model="getSelectedComponent().href"-->
<!--                      placeholder="https://example.com"-->
<!--                      @input="updateComponent"-->
<!--                    >-->
<!--                  </base-input>-->
<!--                </div>-->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Link type</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().linkType"
                      class="select-primary"
                      placeholder="Select link type"
                      style="width: 100%"
                    >
                      <el-option class="select-primary" label="Custom URL" value="custom"></el-option>
                      <el-option class="select-primary" label="Payment request" value="payment"></el-option>
                      <el-option class="select-primary" label="Waiver signature" value="waiver"></el-option>
                      <el-option class="select-primary" label="Transaction details page" value="transaction"></el-option>
                    </el-select>
                  </base-input>
                </div>

                <!-- Custom URL Input (only shown when linkType is 'custom') -->
                <div v-if="!getSelectedComponent().linkType || getSelectedComponent().linkType === 'custom'" class="form-group">
                  <label class="text-white opacity-7 small">Link URL</label>
                  <base-input>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="getSelectedComponent().href"
                      placeholder="https://example.com"
                      @input="updateComponent"
                    >
                  </base-input>
                </div>

                <!-- Payment Request Info (shown when linkType is 'payment') -->
                <div v-if="getSelectedComponent().linkType === 'payment'" class="link-type-info mb-3">
                  <div style="display: flex; gap: 1rem; padding: 1rem; background: rgba(225, 78, 202, 0.1); border: 1px solid rgba(225, 78, 202, 0.3); border-radius: 0.5rem;">
                    <i class="tim-icons icon-credit-card" style="font-size: 1.5rem; color: #e14eca; flex-shrink: 0;"></i>
                    <div>
                      <p style="margin: 0 0 0.25rem 0; font-weight: 600; color: rgba(255, 255, 255, 0.9); font-size: 0.875rem;">Payment request link</p>
                      <p style="margin: 0; font-size: 0.75rem; color: rgba(255, 255, 255, 0.7); line-height: 1.4;">This button will link to the payment request for this booking. The actual URL will be generated when the email is sent.</p>
                    </div>
                  </div>
                </div>

<!--                Transaction details page-->
                <div v-if="getSelectedComponent().linkType === 'transaction'" class="link-type-info mb-3">
                  <div style="display: flex; gap: 1rem; padding: 1rem; background: rgba(225, 78, 202, 0.1); border: 1px solid rgba(225, 78, 202, 0.3); border-radius: 0.5rem;">
                    <i class="tim-icons icon-transaction" style="font-size: 1.5rem; color: #e14eca; flex-shrink: 0;"></i>
                    <div>
                      <p style="margin: 0 0 0.25rem 0; font-weight: 600; color: rgba(255, 255, 255, 0.9); font-size: 0.875rem;">Transaction details page link</p>
                      <p style="margin: 0; font-size: 0.75rem; color: rgba(255, 255, 255, 0.7); line-height: 1.4;">This button will link to the internal transaction details page for this booking. The actual URL will be generated when the email is sent.</p>
                    </div>
                  </div>
                </div>

                <!-- Waiver Signature Info (shown when linkType is 'waiver') -->
                <div v-if="getSelectedComponent().linkType === 'waiver'" class="link-type-info mb-3">
                  <div style="display: flex; gap: 1rem; padding: 1rem; background: rgba(225, 78, 202, 0.1); border: 1px solid rgba(225, 78, 202, 0.3); border-radius: 0.5rem;">
                    <i class="tim-icons icon-notes" style="font-size: 1.5rem; color: #e14eca; flex-shrink: 0;"></i>
                    <div>
                      <p style="margin: 0 0 0.25rem 0; font-weight: 600; color: rgba(255, 255, 255, 0.9); font-size: 0.875rem;">Waiver link</p>
                      <p style="margin: 0; font-size: 0.75rem; color: rgba(255, 255, 255, 0.7); line-height: 1.4;">This button will link to the unique waiver page for this booking. The actual URL will be generated when the email is sent.</p>
                    </div>
                  </div>
                </div>
                <h5 class="subsection-title">Button style</h5>
                <!-- Button Size -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Button size</label>
                  <div class="size-buttons">
                    <button
                      class="size-btn"
                      :class="{ active: getSelectedComponent().size === 'small' }"
                      @click="updateButtonSize('small')"
                    >
                      Small
                    </button>
                    <button
                      class="size-btn"
                      :class="{ active: getSelectedComponent().size === 'medium' }"
                      @click="updateButtonSize('medium')"
                    >
                      Medium
                    </button>
                    <button
                      class="size-btn"
                      :class="{ active: getSelectedComponent().size === 'large' }"
                      @click="updateButtonSize('large')"
                    >
                      Large
                    </button>
                  </div>
                </div>

                <!-- Button Alignment -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Button alignment</label>
                  <div class="alignment-buttons">
                    <button
                      class="align-btn"
                      :class="{ active: getSelectedComponent().alignment === 'left' }"
                      @click="updateButtonAlignment('left')"
                      title="Align left"
                    >
                      <i class="fas fa-align-left"></i>
                    </button>
                    <button
                      class="align-btn"
                      :class="{ active: getSelectedComponent().alignment === 'center' }"
                      @click="updateButtonAlignment('center')"
                      title="Align center"
                    >
                      <i class="fas fa-align-center"></i>
                    </button>
                    <button
                      class="align-btn"
                      :class="{ active: getSelectedComponent().alignment === 'right' }"
                      @click="updateButtonAlignment('right')"
                      title="Align right"
                    >
                      <i class="fas fa-align-right"></i>
                    </button>
                  </div>
                </div>

                <!-- Button Colors -->
                <div class="appearance-section">
                  <div class="row">
                    <div class="col-12">
                      <label class="text-white opacity-7 small">Background color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.backgroundColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                    <div class="col-12">
                      <label class="text-white opacity-7 small">Text color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.color"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Border radius</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="25"
                          :value="parseInt(getSelectedComponent().styles.borderRadius) || 4"
                          @input="updateButtonBorderRadius($event.target.value)"
                          class="slider"
                        >
                        <span class="range-value">{{ parseInt(getSelectedComponent().styles.borderRadius) || 4 }}px</span>
                      </base-input>
                    </div>
                  </div>
                </div>

                <h5 class="subsection-title">Spacing</h5>
                <!-- Spacing Section -->
                <div class="row">
                  <div class="col">
                    <label class="text-white opacity-7 small">Top margin</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        step="2"
                        class="slider"
                        :value="parseInt(getSelectedComponent().styles.marginTop) || 16"
                        @input="updateButtonMarginTop($event.target.value)"
                      >
                      <span class="range-value">{{ parseInt(getSelectedComponent().styles.marginTop) || 16 }}px</span>
                    </base-input>
                  </div>
                  <div class="col">
                    <label class="text-white opacity-7 small">Bottom margin</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        step="2"
                        class="slider"
                        :value="parseInt(getSelectedComponent().styles.marginBottom) || 16"
                        @input="updateButtonMarginBottom($event.target.value)"
                      >
                      <span class="range-value">{{ parseInt(getSelectedComponent().styles.marginBottom) || 16 }}px</span>
                    </base-input>
                  </div>
                </div>
              </div>

              <!-- Image Component Properties -->
              <div v-if="getSelectedComponent()?.type === 'image'" class="image-properties">
                <!-- Image Source Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Image Source</h5>

                  <!-- Upload/URL Toggle -->
                  <div class="source-toggle">
                    <button
                      class="toggle-btn"
                      :class="{ active: getSelectedComponent().sourceType === 'upload' }"
                      @click="setImageSourceType('upload')"
                    >
                      Upload
                    </button>
                    <button
                      class="toggle-btn"
                      :class="{ active: getSelectedComponent().sourceType === 'url' || !getSelectedComponent().sourceType }"
                      @click="setImageSourceType('url')"
                    >
                      URL
                    </button>
                  </div>

                  <!-- Upload Option -->
                  <div v-if="getSelectedComponent().sourceType === 'upload'" class="upload-section mb-3">
                    <div v-if="getSelectedComponent().uploadedUrl" class="current-image">
                      <img
                        :src="getSelectedComponent().uploadedUrl"
                        alt="Current image"
                        class="current-image-preview"
                      />
                      <div class="image-actions">
                        <ImageEditor
                          :edit="true"
                          :edited_image="getCurrentImageData()"
                          size="sm"
                          :show_slider="false"
                          :show-background-color="false"
                          :show-description="false"
                          :show-fit="false"
                          @saveImage="handleImageUpdate"
                        />
                        <base-button
                          type="danger"
                          size="sm"
                          icon
                          link
                          @click="clearUploadedImage"
                        >
                          <i class="tim-icons icon-trash-simple"></i>
                        </base-button>
                      </div>
                    </div>

                    <div v-else>
                      <ImageEditor
                        :edit="false"
                        :text="'Upload Image'"
                        size="sm"
                        :show_slider="false"
                        :show-background-color="false"
                        :show-description="false"
                        :show-fit="false"
                        @saveImage="handleImageUpload"
                        @clickUpload="onImageUploadClick"
                      />
                      <p class="upload-help-text">
                        Upload an image from your computer. It will be stored securely and optimized for email.
                      </p>
                    </div>
                  </div>

                  <!-- URL Option -->
                  <div v-if="getSelectedComponent().sourceType !== 'upload'" class="url-section">
                    <div class="form-group">
                      <label class="text-white opacity-7 small">Image URL</label>
                      <base-input>
                        <input
                          type="text"
                          class="form-control form-control-sm"
                          v-model="getSelectedComponent().src"
                          placeholder="https://example.com/image.jpg"
                          @input="updateComponent"
                        >
                      </base-input>
                      <p class="help-text">Enter a direct link to an image</p>
                    </div>
                  </div>
                </div>

                <!-- Image Settings -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Image Settings</h5>

                  <div class="form-group">
                    <label class="text-white opacity-7 small">Alt text</label>
                    <base-input>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().alt"
                        placeholder="Describe this image"
                        @input="updateComponent"
                      >
                    </base-input>
                    <p class="help-text">Important for accessibility and when images don't load</p>
                  </div>

                  <div class="form-group">
                    <label class="text-white opacity-7 small">Link URL (optional)</label>
                    <base-input>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().href"
                        placeholder="https://example.com"
                        @input="updateComponent"
                      >
                    </base-input>
                    <p class="help-text">Make image clickable</p>
                  </div>
                </div>

                <!-- Appearance Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Appearance</h5>

                  <!-- Alignment -->
                  <div class="form-group">
                    <label class="text-white opacity-7 small">Alignment</label>
                    <div class="alignment-buttons">
                      <button
                        class="align-btn"
                        :class="{ active: getSelectedComponent().alignment === 'left' }"
                        @click="updateImageAlignment('left')"
                        title="Align Left"
                      >
                        <i class="fas fa-align-left"></i>
                      </button>
                      <button
                        class="align-btn"
                        :class="{ active: getSelectedComponent().alignment === 'center' }"
                        @click="updateImageAlignment('center')"
                        title="Align Center"
                      >
                        <i class="fas fa-align-center"></i>
                      </button>
                      <button
                        class="align-btn"
                        :class="{ active: getSelectedComponent().alignment === 'right' }"
                        @click="updateImageAlignment('right')"
                        title="Align Right"
                      >
                        <i class="fas fa-align-right"></i>
                      </button>
                    </div>
                  </div>

                  <!-- Width Control -->
                  <div class="form-group">
                    <label class="text-white opacity-7 small">Width</label>
                    <div class="width-control">
                      <input
                        type="range"
                        min="50"
                        max="600"
                        step="10"
                        :value="parseInt(getSelectedComponent().styles.width)"
                        @input="updateImageWidth($event.target.value)"
                        class="slider"
                      >
                      <div class="width-input-group">
                        <input
                          type="number"
                          class="form-control form-control-sm"
                          :value="parseInt(getSelectedComponent().styles.width)"
                          @input="updateImageWidth($event.target.value)"
                          min="50"
                          max="600"
                        >
                        <span class="unit">px</span>
                      </div>
                    </div>

                    <!-- Width Presets -->
                    <div class="width-presets">
                      <button
                        v-for="preset in getImageWidthPresets()"
                        :key="preset.value"
                        class="preset-btn"
                        :class="{ active: parseInt(getSelectedComponent().styles.width) === preset.value }"
                        @click="updateImageWidth(preset.value)"
                      >
                        {{ preset.label }}
                      </button>
                    </div>
                  </div>

                  <!-- Border Radius -->
                  <div class="form-group">
                    <label class="text-white opacity-7 small">Border radius</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        :value="parseInt(getSelectedComponent().styles.borderRadius) || 0"
                        @input="updateImageBorderRadius($event.target.value)"
                        class="slider"
                      >
                    </base-input>
                    <span class="range-value">{{ parseInt(getSelectedComponent().styles.borderRadius) || 0 }}px</span>
                  </div>
                </div>

                <!-- Spacing Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Spacing</h5>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Top Margin</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          step="2"
                          class="slider"
                          :value="parseInt(getSelectedComponent().styles.marginTop) || 16"
                          @input="updateImageMarginTop($event.target.value)"
                        >
                        <span class="range-value">{{ parseInt(getSelectedComponent().styles.marginTop) || 16 }}px</span>
                      </base-input>
                    </div>
                    <div class="col">
                      <label class="text-white opacity-7 small">Bottom Margin</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          step="2"
                          class="slider"
                          :value="parseInt(getSelectedComponent().styles.marginBottom) || 16"
                          @input="updateImageMarginBottom($event.target.value)"
                        >
                        <span class="range-value">{{ parseInt(getSelectedComponent().styles.marginBottom) || 16 }}px</span>
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="getSelectedComponent()?.type === 'header'">
                <div class="sidebar-section">
                  <h5 class="subsection-title">Logo</h5>

                  <!-- Upload/URL Toggle -->
                  <div class="source-toggle">
                    <button
                      class="toggle-btn"
                      :class="{ active: getSelectedComponent().sourceType === 'upload' }"
                      @click="setHeaderSourceType('upload')"
                    >
                      Upload
                    </button>
                    <button
                      class="toggle-btn"
                      :class="{ active: getSelectedComponent().sourceType !== 'upload' }"
                      @click="setHeaderSourceType('url')"
                    >
                      URL
                    </button>
                  </div>

                  <!-- Upload Option -->
                  <div v-if="getSelectedComponent().sourceType === 'upload'" class="upload-section mb-3">
                    <div v-if="getSelectedComponent().uploadedUrl" class="current-image">
                      <img
                        :src="getSelectedComponent().uploadedUrl"
                        alt="Current logo"
                        class="current-image-preview"
                      />
                      <div class="image-actions">
                        <ImageEditor
                          :edit="true"
                          :edited_image="getCurrentHeaderImageData()"
                          size="sm"
                          :show_slider="false"
                          :show-background-color="false"
                          :show-description="false"
                          :show-fit="false"
                          @saveImage="handleHeaderImageUpdate"
                        />
                        <base-button
                          type="danger"
                          size="sm"
                          icon
                          link
                          @click="clearHeaderUploadedImage"
                        >
                          <i class="tim-icons icon-trash-simple"></i>
                        </base-button>
                      </div>
                    </div>

                    <div v-else>
                      <ImageEditor
                        :edit="false"
                        text="Upload Logo"
                        size="sm"
                        :show_slider="false"
                        :show-background-color="false"
                        :show-description="false"
                        :show-fit="false"
                        @saveImage="handleHeaderImageUpload"
                      />
                      <p class="upload-help-text">
                        Upload your company logo. It will be stored securely and optimized for email.
                      </p>
                    </div>
                  </div>

                  <!-- URL Option -->
                  <div v-if="getSelectedComponent().sourceType !== 'upload'" class="url-section">
                    <div class="form-group">
                      <label class="text-white opacity-7 small">Header image URL</label>
                      <base-input>
                        <input
                          type="text"
                          class="form-control form-control-sm"
                          v-model="getSelectedComponent().imageSrc"
                          placeholder="https://example.com/logo.png"
                          @input="updateComponent"
                        >
                      </base-input>
                    </div>
                  </div>
                </div>

                <!-- Image Width -->
                <div class="row">
                  <div class="col">
                    <label class="text-white opacity-7 small">Image width</label>
                    <base-input>
                      <input
                        type="range"
                        min="100"
                        max="400"
                        v-model="getSelectedComponent().styles.imageWidth"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.imageWidth }}px</span>
                    </base-input>
                  </div>
                </div>

                <h5 class="subsection-title">Header style</h5>
                <!-- Background Color -->
                <div class="row">
                  <div class="col">
                    <label class="text-white opacity-7 small">Background color</label>
                    <base-input>
                      <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                        v-model="getSelectedComponent().styles.backgroundColor"
                        @change="updateComponentStyles"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <!-- Alignment -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Logo alignment</label>
                  <div class="alignment-buttons">
                    <button
                      class="align-btn"
                      :class="{ active: getSelectedComponent().styles.textAlign === 'left' }"
                      @click="setHeaderAlignment('left')"
                      title="Align Left"
                    >
                      <i class="fas fa-align-left"></i>
                    </button>
                    <button
                      class="align-btn"
                      :class="{ active: getSelectedComponent().styles.textAlign === 'center' }"
                      @click="setHeaderAlignment('center')"
                      title="Align Center"
                    >
                      <i class="fas fa-align-center"></i>
                    </button>
                    <button
                      class="align-btn"
                      :class="{ active: getSelectedComponent().styles.textAlign === 'right' }"
                      @click="setHeaderAlignment('right')"
                      title="Align Right"
                    >
                      <i class="fas fa-align-right"></i>
                    </button>
                  </div>
                </div>

                <!-- Padding Controls -->
                <div class="spacing-section">
                  <h5 class="subsection-title">Spacing</h5>

                  <div class="row">
                    <div class="col-12">
                      <label class="text-white opacity-7 small">Padding top/bottom</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="80"
                          v-model="getSelectedComponent().styles.paddingTop"
                          @input="updateHeaderPaddingVertical($event.target.value)"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.paddingTop }}px</span>
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Divider Component Properties -->
              <div v-if="getSelectedComponent()?.type === 'divider'" class="divider-properties">
                <h5 class="subsection-title">Line settings</h5>
                <!-- Line Settings -->
                <div class="sidebar-section">
                  <!-- Line Thickness -->
                  <div class="form-group">
                    <label class="text-white opacity-7 small">Line thickness</label>
                    <div class="thickness-buttons">
                      <button
                        class="thickness-btn"
                        :class="{ active: getSelectedComponent().styles.thickness === '1' }"
                        @click="updateDividerThickness('1')"
                      >
                        Thin
                      </button>
                      <button
                        class="thickness-btn"
                        :class="{ active: getSelectedComponent().styles.thickness === '2' }"
                        @click="updateDividerThickness('2')"
                      >
                        Medium
                      </button>
                      <button
                        class="thickness-btn"
                        :class="{ active: getSelectedComponent().styles.thickness === '3' }"
                        @click="updateDividerThickness('3')"
                      >
                        Thick
                      </button>
                    </div>
                  </div>

                  <!-- Line Color -->
                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Line color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.lineColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>
                </div>
                <h5 class="subsection-title">Spacing</h5>
                <!-- Spacing Section -->
                <div class="sidebar-section">
                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Top margin</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="60"
                          step="5"
                          v-model="getSelectedComponent().styles.marginTop"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.marginTop }}px</span>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Bottom margin</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="60"
                          step="5"
                          v-model="getSelectedComponent().styles.marginBottom"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.marginBottom }}px</span>
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Custom HTML Component Properties -->
              <div v-if="getSelectedComponent()?.type === 'html'" class="html-properties">
                <div class="sidebar-section">
                  <info-hint variant="warning" icon="icon-alert-circle-exc" class="mb-3">
                    Custom HTML gives you complete control over your email design, but email clients support HTML and CSS differently than web browsers. Always test custom or AI generated HTML before sending a large campaign.
                  </info-hint>

                  <base-button type="primary" size="sm" block @click="showHtmlDrawer = true">
                    <i class="tim-icons icon-html5 mr-2"></i> Edit HTML
                  </base-button>

                  <div class="html-block-summary text-white opacity-7 small mt-2">
                    {{ getHtmlBlockSummary() }}
                  </div>
                </div>

                <h5 class="subsection-title">Spacing</h5>
                <div class="sidebar-section">
                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Top padding</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="60"
                          step="5"
                          v-model="getSelectedComponent().styles.paddingTop"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.paddingTop }}px</span>
                      </base-input>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Bottom padding</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="60"
                          step="5"
                          v-model="getSelectedComponent().styles.paddingBottom"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.paddingBottom }}px</span>
                      </base-input>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Side padding</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="60"
                          step="5"
                          v-model="getSelectedComponent().styles.paddingLeft"
                          @input="updateHtmlBlockSidePadding($event.target.value)"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.paddingLeft }}px</span>
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Billing Info Component Properties -->
              <div v-if="getSelectedComponent()?.type === 'billing'" class="billing-properties">
                <!-- Language Selection -->
                <h5 class="subsection-title">Content</h5>
                <div class="form-group">
                  <label class="text-white small opacity-7">Language</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().language"
                      @change="updateComponent"
                      class="select-primary"
                      placeholder="Select language"
                      style="width: 100%"
                    >
                      <el-option label="English" value="english"></el-option>
                      <el-option label="Español (Spanish)" value="spanish"></el-option>
                      <el-option label="Français (French)" value="french"></el-option>
                      <el-option label="Svenska (Swedish)" value="swedish"></el-option>
                      <el-option label="Ελληνικά (Greek)" value="greek"></el-option>
                      <el-option label="Dansk (Danish)" value="danish"></el-option>
                    </el-select>
                  </base-input>
                </div>

                <!-- Typography Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Typography</h5>

                  <div class="form-group">
                    <label class="text-white opacity-7 small">Font family</label>
                    <base-input>
                      <el-select
                        v-model="getSelectedComponent().styles.fontFamily"
                        @change="updateComponentStyles"
                        class="select-primary"
                        placeholder="Select font"
                        filterable
                        style="width: 100%"
                        value-key="family"
                      >
                        <el-option
                          v-for="font in availableFonts"
                          :key="font.label"
                          :label="font.label"
                          :value="font.value"
                          class="select-primary"
                        >
                        </el-option>
                      </el-select>
                    </base-input>
                  </div>

                  <div class="form-group">
                    <label class="text-white small opacity-7">Font size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.fontSize"
                        @input="updateComponentStyles"
                        min="10"
                        max="20"
                      >
                    </base-input>
                  </div>
                </div>

                <!-- Appearance Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Appearance</h5>

                  <div class="row">
                    <div class="col">
                      <label class="text-white small opacity-7">Background color</label>
                      <base-input>
                        <div class="color-input-group">
                          <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                            v-model="getSelectedComponent().styles.backgroundColor"
                            @change="updateComponentStyles"
                            size="small"
                            :show-alpha="true"
                          ></el-color-picker>
                          <base-button simple
                            type="primary"
                            size="sm"
                            @click="setBillingBackgroundInherit"
                            class="inherit-btn"
                          >
                            Inherit
                          </base-button>
                        </div>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white small opacity-7">Text color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.textColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white small opacity-7">Customer name color</label>
                      <base-input>
                        <el-color-picker :predefine="['#333333', '#000000', '#1e1e2e', '#4a5568', '#e14eca', '#1d8cf8']"
                                         v-model="getSelectedComponent().styles.nameColor"
                                         @change="updateComponentStyles"
                                         size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <!-- Status Colors -->
                  <div class="row">
                    <div class="col">
                      <label class="text-white small opacity-7">Paid status color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.statusColorPaid"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white small opacity-7">Unpaid status color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.statusColorUnpaid"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <!-- Border Settings -->
                  <div class="form-group">
                    <div class="switch-control">
                      <el-switch
                        active-color="#e14eca"
                        v-model="getSelectedComponent().styles.showBorder"
                        @change="updateComponentStyles"
                      />
                      <label class="switch-label text-white opacity-7 small">&nbsp; Show border</label>
                    </div>
                  </div>

                  <div v-if="getSelectedComponent().styles.showBorder" class="form-group">
                    <label class="text-white small opacity-7">Border color</label>
                    <base-input>
                      <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                        v-model="getSelectedComponent().styles.borderColor"
                        @change="updateComponentStyles"
                      ></el-color-picker>
                    </base-input>
                  </div>

                  <!-- Border Radius -->
                  <div class="row">
                    <div class="col">
                      <label class="text-white small opacity-7">Border radius</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="20"
                          v-model="getSelectedComponent().styles.borderRadius"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.borderRadius }}px</span>
                      </base-input>
                    </div>
                  </div>
                </div>

                <!-- Spacing Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Spacing</h5>

                  <div class="row">
                    <div class="col">
                      <label class="text-white small opacity-7">Top margin</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          v-model="getSelectedComponent().styles.marginTop"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.marginTop }}px</span>
                      </base-input>
                    </div>
                    <div class="col">
                      <label class="text-white small opacity-7">Bottom margin</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          v-model="getSelectedComponent().styles.marginBottom"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.marginBottom }}px</span>
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Line Items Component Properties -->
              <div v-if="getSelectedComponent()?.type === 'lineItems'" class="line-items-properties">
                <h5 class="subsection-title">Content</h5>
                <!-- Language Selection -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Language</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().language"
                      @change="updateComponent"
                      class="select-primary"
                      placeholder="Select language"
                      style="width: 100%"
                    >
                      <el-option label="English" value="english"></el-option>
                      <el-option label="Español (Spanish)" value="spanish"></el-option>
                      <el-option label="Français (French)" value="french"></el-option>
                      <el-option label="Svenska (Swedish)" value="swedish"></el-option>
                      <el-option label="Ελληνικά (Greek)" value="greek"></el-option>
                      <el-option label="Dansk (Danish)" value="danish"></el-option>
                    </el-select>
                  </base-input>
                </div>

                <!-- Show Price Toggle -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showPrice"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white small opacity-7">&nbsp; Show price column</label>
                  </div>

                  <!-- Show Ticket Categories Toggle (same group as the price toggle so the two sit close together) -->
                  <div class="switch-control mt-1">
                    <el-switch
                      active-color="#e14eca"
                      :value="getSelectedComponent().showTicketCategories === true"
                      @change="setShowTicketCategories"
                    />
                    <label class="switch-label text-white small opacity-7">&nbsp; Show ticket categories</label>
                  </div>

                  <!-- Show End Time Toggle: the Date/time row adds each booking's end time -->
                  <div class="switch-control mt-1">
                    <el-switch
                      active-color="#e14eca"
                      :value="getSelectedComponent().showEndTime === true"
                      @change="setShowEndTime"
                    />
                    <label class="switch-label text-white small opacity-7">&nbsp; Show end time</label>
                  </div>
                </div>

                <!-- Typography Section -->
                <div class="typography-section">
                  <h5 class="subsection-title">Appearance</h5>

                  <!-- Font Family -->
                  <div class="form-group">
                    <label class="text-white opacity-7 small">Font family</label>
                    <base-input>
                      <el-select
                        v-model="getSelectedComponent().styles.fontFamily"
                        @change="updateComponentStyles"
                        class="select-primary"
                        placeholder="Select font"
                        filterable
                        style="width: 100%"
                        value-key="family"
                      >
                        <el-option
                          v-for="font in availableFonts"
                          :key="font.label"
                          :label="font.label"
                          :value="font.value"
                          class="select-primary"
                        >
                        </el-option>
                      </el-select>
                    </base-input>
                  </div>
                </div>

                <!-- Appearance Section -->
                <div class="appearance-section">
                  <!-- Background Color -->
                  <div class="row">
                    <div class="col">

                      <label class="text-white opacity-7 small">Background color</label>
                      <base-input>
                        <div class="color-input-group">
                          <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                            v-model="getSelectedComponent().styles.backgroundColor"
                            @change="updateComponentStyles"
                            size="small"
                            :show-alpha="true"
                          ></el-color-picker>
                          <base-button simple
                            type="primary"
                            size="sm"
                            @click="setLineItemsBackgroundInherit"
                            class="inherit-btn"
                          >
                            Inherit
                          </base-button>
                        </div>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Text color</label>
                      <base-input >
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.textColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Header color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.headerColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Divider color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.dividerColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <!-- Show Border Toggle -->
                  <div class="form-group">
                    <div class="switch-control">
                      <el-switch
                        active-color="#e14eca"
                        v-model="getSelectedComponent().styles.showBorder"
                        @change="updateComponentStyles"
                      />
                      <label class="switch-label text-white opacity-7 small">&nbsp; Show border</label>
                    </div>
                  </div>

                  <div v-if="getSelectedComponent().styles.showBorder" class="form-group">
                    <label class="text-white opacity-7 small">Border color</label>
                    <base-input>
                      <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                        v-model="getSelectedComponent().styles.borderColor"
                        @change="updateComponentStyles"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <!-- Spacing Section -->
                <div class="spacing-section">
                  <h5 class="subsection-title">Spacing</h5>

                  <div class="row">
                    <div class="col-12">
                      <label class="text-white opacity-7 small">Top margin</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          v-model="getSelectedComponent().styles.marginTop"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.marginTop }}px</span>
                      </base-input>
                    </div>
                    <div class="col-12">
                      <label class="text-white opacity-7 small">Bottom margin</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          v-model="getSelectedComponent().styles.marginBottom"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.marginBottom }}px</span>
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Customer Responses Component Properties -->
              <div v-if="getSelectedComponent()?.type === 'customFields'" class="customer-responses-properties">
                <!-- Language Selection -->
                <h5 class="subsection-title">Content</h5>
                <div class="form-group">
                  <label class="text-white opacity-7 small">Language</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().language"
                      @change="updateComponent"
                      class="select-primary"
                      placeholder="Select language"
                      style="width: 100%"
                    >
                      <el-option label="English" value="english"></el-option>
                      <el-option label="Español (Spanish)" value="spanish"></el-option>
                      <el-option label="Français (French)" value="french"></el-option>
                      <el-option label="Svenska (Swedish)" value="swedish"></el-option>
                      <el-option label="Ελληνικά (Greek)" value="greek"></el-option>
                      <el-option label="Dansk (Danish)" value="danish"></el-option>
                    </el-select>
                  </base-input>
                </div>

                <!-- Typography Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Typography</h5>

                  <div class="form-group">
                    <label class="text-white opacity-7 small">Font family</label>
                    <base-input>
                      <el-select
                        v-model="getSelectedComponent().styles.fontFamily"
                        @change="updateComponentStyles"
                        class="select-primary"
                        placeholder="Select font"
                        filterable
                        style="width: 100%"
                        value-key="family"
                      >
                        <el-option
                          v-for="font in availableFonts"
                          :key="font.label"
                          :label="font.label"
                          :value="font.value"
                          class="select-primary"
                        >
                        </el-option>
                      </el-select>
                    </base-input>
                  </div>

                  <!-- Question Typography -->
                  <div class="row">
                    <div class="col">
                      <div class="form-group">
                        <label class="text-white opacity-7 small">Question size</label>
                        <base-input>
                          <input
                            type="number"
                            class="form-control form-control-sm"
                            v-model="getSelectedComponent().styles.questionFontSize"
                            @input="updateComponentStyles"
                            min="10"
                            max="20"
                          >
                        </base-input>
                      </div>
                    </div>
                    <div class="col">
                      <div class="form-group">
                        <label class="text-white opacity-7 small">Answer size</label>
                        <base-input>
                          <input
                            type="number"
                            class="form-control form-control-sm"
                            v-model="getSelectedComponent().styles.answerFontSize"
                            @input="updateComponentStyles"
                            min="10"
                            max="20"
                          >
                        </base-input>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Appearance Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Appearance</h5>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Background color</label>
                      <base-input >
                        <div class="color-input-group">
                          <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                            v-model="getSelectedComponent().styles.backgroundColor"
                            @change="updateComponentStyles"
                            size="small"
                            :show-alpha="true"
                          ></el-color-picker>
                          <base-button simple
                            type="primary"
                            size="sm"
                            @click="setCustomerResponsesBackgroundInherit"
                            class="inherit-btn"
                          >
                            Inherit
                          </base-button>
                        </div>
                      </base-input>
                    </div>
                  </div>

                  <!-- Color Settings -->
                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Question color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.questionColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Answer color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.answerColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <!-- Border Settings -->
                  <div class="form-group mt-3">
                    <div class="switch-control">
                      <el-switch
                        active-color="#e14eca"
                        v-model="getSelectedComponent().styles.showBorder"
                        @change="updateComponentStyles"
                      />
                      <label class="switch-label text-white opacity-7 small">&nbsp; Show border</label>
                    </div>
                  </div>

                  <div v-if="getSelectedComponent().styles.showBorder" class="form-group">
                    <label class="text-white opacity-7 small">Border color</label>
                    <base-input>
                      <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                        v-model="getSelectedComponent().styles.borderColor"
                        @change="updateComponentStyles"
                      ></el-color-picker>
                    </base-input>
                  </div>

                  <!-- Border Radius -->
                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Border radius</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="20"
                          v-model="getSelectedComponent().styles.borderRadius"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.borderRadius }}px</span>
                      </base-input>
                    </div>
                  </div>
                </div>

                <!-- Spacing Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Spacing</h5>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Top margin</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          v-model="getSelectedComponent().styles.marginTop"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.marginTop }}px</span>
                      </base-input>
                    </div>
                    <div class="col">
                      <label class="text-white opacity-7 small">Bottom margin</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          v-model="getSelectedComponent().styles.marginBottom"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.marginBottom }}px</span>
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Payment Summary Component Properties -->
              <div v-if="getSelectedComponent()?.type === 'paymentSummary'" class="payment-summary-properties">
                <h5 class="subsection-title">Content</h5>
                <!-- Language Selection -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Language</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().language"
                      @change="updateComponent"
                      class="select-primary"
                      placeholder="Select language"
                      style="width: 100%"
                    >
                      <el-option label="English" value="english"></el-option>
                      <el-option label="Español (Spanish)" value="spanish"></el-option>
                      <el-option label="Français (French)" value="french"></el-option>
                      <el-option label="Svenska (Swedish)" value="swedish"></el-option>
                      <el-option label="Ελληνικά (Greek)" value="greek"></el-option>
                      <el-option label="Dansk (Danish)" value="danish"></el-option>
                    </el-select>
                  </base-input>
                </div>

                <!-- Payment Status Toggle -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showPaymentStatus"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show amount paid/due</label>
                  </div>
                </div>

                <!-- Typography Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Typography</h5>

                  <div class="form-group">
                    <label class="text-white opacity-7 small">Font family</label>
                    <base-input>
                      <el-select
                        v-model="getSelectedComponent().styles.fontFamily"
                        @change="updateComponentStyles"
                        class="select-primary"
                        placeholder="Select font"
                        filterable
                        style="width: 100%"
                        value-key="family"
                      >
                        <el-option
                          v-for="font in availableFonts"
                          :key="font.label"
                          :label="font.label"
                          :value="font.value"
                          class="select-primary"
                        >
                        </el-option>
                      </el-select>
                    </base-input>
                  </div>

                  <div class="row">
                    <div class="col">
                      <div class="form-group">
                        <label class="text-white opacity-7 small">Font size</label>
                        <base-input>
                          <input
                            type="number"
                            class="form-control form-control-sm"
                            v-model="getSelectedComponent().styles.fontSize"
                            @input="updateComponentStyles"
                            min="10"
                            max="20"
                          >
                        </base-input>
                      </div>
                    </div>
                    <div class="col">
                      <div class="form-group">
                        <label class="text-white opacity-7 small">Total size</label>
                        <base-input>
                          <input
                            type="number"
                            class="form-control form-control-sm"
                            v-model="getSelectedComponent().styles.totalFontSize"
                            @input="updateComponentStyles"
                            min="14"
                            max="24"
                          >
                        </base-input>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Appearance Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Appearance</h5>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Background color</label>

                      <base-input>
                        <div class="color-input-group">
                          <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                            v-model="getSelectedComponent().styles.backgroundColor"
                            @change="updateComponentStyles"
                            size="small"
                            :show-alpha="true"
                          ></el-color-picker>
                          <base-button simple
                            type="primary"
                            size="sm"
                            @click="setPaymentSummaryBackgroundInherit"
                            class="inherit-btn"
                          >
                            Inherit
                          </base-button>
                        </div>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Text color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.textColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Total color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.totalColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Amount paid color</label>
                      <base-input>
                        <el-color-picker :predefine="['#10b981', '#22c55e', '#16a34a', '#15803d', '#14532d', '#059669']"
                                         v-model="getSelectedComponent().styles.amountPaidColor"
                                         @change="updateComponentStyles"
                                         size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Amount due color</label>
                      <base-input>
                        <el-color-picker :predefine="['#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d', '#f87171']"
                                         v-model="getSelectedComponent().styles.amountDueColor"
                                         @change="updateComponentStyles"
                                         size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Divider color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.dividerColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="form-group">
                    <div class="switch-control">
                      <el-switch
                        active-color="#e14eca"
                        v-model="getSelectedComponent().styles.showBorder"
                        @change="updateComponentStyles"
                      />
                      <label class="switch-label text-white opacity-7 small">&nbsp; Show border</label>
                    </div>
                  </div>

                  <div v-if="getSelectedComponent().styles.showBorder" class="form-group">
                    <base-input label="Border Color">
                      <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                        v-model="getSelectedComponent().styles.borderColor"
                        @change="updateComponentStyles"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <!-- Spacing Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Spacing</h5>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Top margin</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          v-model="getSelectedComponent().styles.marginTop"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.marginTop }}px</span>
                      </base-input>
                    </div>
                    <div class="col">
                      <label class="text-white opacity-7 small">Bottom margin</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          v-model="getSelectedComponent().styles.marginBottom"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.marginBottom }}px</span>
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Applied Payments Component Properties -->
              <div v-if="getSelectedComponent()?.type === 'appliedPayments'" class="applied-payments-properties">
                <!-- Language Selection -->
                <h5 class="subsection-title">Content</h5>
                <div class="form-group">
                  <label class="text-white opacity-7 small">Language</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().language"
                      @change="updateComponent"
                      class="select-primary"
                      placeholder="Select language"
                      style="width: 100%"
                    >
                      <el-option label="English" value="english"></el-option>
                      <el-option label="Español (Spanish)" value="spanish"></el-option>
                      <el-option label="Français (French)" value="french"></el-option>
                      <el-option label="Svenska (Swedish)" value="swedish"></el-option>
                      <el-option label="Ελληνικά (Greek)" value="greek"></el-option>
                      <el-option label="Dansk (Danish)" value="danish"></el-option>
                    </el-select>
                  </base-input>
                </div>

                <!-- Display Options -->
                <div class="sidebar-section">
                  <div class="form-group">
                    <div class="switch-control">
                      <el-switch
                        active-color="#e14eca"
                        v-model="getSelectedComponent().showTotal"
                        @change="updateComponent"
                      />
                      <label class="switch-label text-white opacity-7 small">&nbsp; Show total applied</label>
                    </div>
                  </div>
                </div>

                <!-- Typography Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Typography</h5>

                  <div class="form-group">
                    <label class="text-white opacity-7 small">Font family</label>
                    <base-input>
                      <el-select
                        v-model="getSelectedComponent().styles.fontFamily"
                        @change="updateComponentStyles"
                        class="select-primary"
                        placeholder="Select font"
                        filterable
                        style="width: 100%"
                        value-key="family"
                      >
                        <el-option
                          v-for="font in availableFonts"
                          :key="font.label"
                          :label="font.label"
                          :value="font.value"
                          class="select-primary"
                        >
                        </el-option>
                      </el-select>
                    </base-input>
                  </div>

                  <div class="row">
                    <div class="col">
                      <div class="form-group">
                        <label class="text-white opacity-7 small">Font size</label>
                        <base-input>
                          <input
                            type="number"
                            class="form-control form-control-sm"
                            v-model="getSelectedComponent().styles.fontSize"
                            @input="updateComponentStyles"
                            min="10"
                            max="20"
                          >
                        </base-input>
                      </div>
                    </div>
                    <div class="col">
                      <div class="form-group">
                        <label class="text-white opacity-7 small">Title size</label>
                        <base-input>
                          <input
                            type="number"
                            class="form-control form-control-sm"
                            v-model="getSelectedComponent().styles.titleFontSize"
                            @input="updateComponentStyles"
                            min="14"
                            max="24"
                          >
                        </base-input>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Appearance Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Appearance</h5>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Background color</label>
                      <base-input>
                        <div class="color-input-group">
                          <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                            v-model="getSelectedComponent().styles.backgroundColor"
                            @change="updateComponentStyles"
                            size="small"
                            :show-alpha="true"
                          ></el-color-picker>
                          <base-button simple
                            type="primary"
                            size="sm"
                            @click="setAppliedPaymentsBackgroundInherit"
                            class="inherit-btn"
                          >
                            Inherit
                          </base-button>
                        </div>
                      </base-input>
                    </div>
                  </div>

                  <!-- Color Settings -->
                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Title color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.titleColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Text color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.textColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Amount color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.amountColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Total applied color</label>
                      <base-input>
                        <el-color-picker :predefine="['#111827', '#000000', '#1e1e2e', '#4a5568', '#e14eca', '#1d8cf8']"
                                         v-model="getSelectedComponent().styles.totalColor"
                                         @change="updateComponentStyles"
                                         size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Description color</label>
                      <base-input>
                        <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                          v-model="getSelectedComponent().styles.metaColor"
                          @change="updateComponentStyles"
                          size="small"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <!-- Border Settings -->
                  <div class="form-group mt-3">
                    <div class="switch-control">
                      <el-switch
                        active-color="#e14eca"
                        v-model="getSelectedComponent().styles.showBorder"
                        @change="updateComponentStyles"
                      />
                      <label class="switch-label text-white opacity-7 small">&nbsp; Show border</label>
                    </div>
                  </div>

                  <div v-if="getSelectedComponent().styles.showBorder" class="form-group">
                    <label class="text-white opacity-7 small">Border color</label>
                    <base-input>
                      <el-color-picker :predefine="['#e14eca', '#1d8cf8', '#00bf9a', '#ff8d72', '#fd5d93', '#825ee4']"
                        v-model="getSelectedComponent().styles.borderColor"
                        @change="updateComponentStyles"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <!-- Spacing Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Spacing</h5>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Top margin</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          v-model="getSelectedComponent().styles.marginTop"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.marginTop }}px</span>
                      </base-input>
                    </div>
                    <div class="col">
                      <label class="text-white opacity-7 small">Bottom margin</label>
                      <base-input>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          v-model="getSelectedComponent().styles.marginBottom"
                          @input="updateComponentStyles"
                          class="slider"
                        >
                        <span class="range-value">{{ getSelectedComponent().styles.marginBottom }}px</span>
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Game Component Properties -->
              <div v-if="getSelectedComponent()?.type === 'game'" class="game-properties">
                <h5 class="subsection-title">Layout</h5>

                <div class="layout-options">
                  <!-- Image Top Layout -->
                  <div
                    class="layout-option"
                    :class="{ active: getSelectedComponent().layout === 'image-top' }"
                    @click="updateGameLayout('image-top')"
                  >
                    <div class="layout-preview">
                      <div class="preview-content">
                        <div class="preview-box" style="width: 30px; height: 30px; background: #e14eca; border-radius: 3px; margin: 0 auto;"></div>
                        <div style="display: flex; gap: 3px; margin-top: 5px; justify-content: center;">
                          <div class="preview-box" style="width: 12px; height: 4px; background: #d1d5db; border-radius: 1px;"></div>
                          <div class="preview-box" style="width: 8px; height: 4px; background: #d1d5db; border-radius: 1px;"></div>
                          <div class="preview-box" style="width: 8px; height: 4px; background: #d1d5db; border-radius: 1px;"></div>
                        </div>
                      </div>
                    </div>
                    <span>Image<br/>Top</span>
                  </div>

                  <!-- Image Left Layout -->
                  <div
                    class="layout-option"
                    :class="{ active: getSelectedComponent().layout === 'image-left' }"
                    @click="updateGameLayout('image-left')"
                  >
                    <div class="layout-preview">
                      <div class="preview-content" style="display: flex; gap: 5px; align-items: flex-start;">
                        <div class="preview-box" style="width: 25px; height: 30px; background: #e14eca; border-radius: 3px; flex-shrink: 0;"></div>
                        <div style="flex: 1; padding-top: 2px;">
                          <div class="preview-box" style="width: 100%; height: 4px; background: #d1d5db; border-radius: 1px; margin-bottom: 3px;"></div>
                          <div style="display: flex; gap: 2px;">
                            <div class="preview-box" style="width: 12px; height: 4px; background: #d1d5db; border-radius: 1px;"></div>
                            <div class="preview-box" style="width: 8px; height: 4px; background: #d1d5db; border-radius: 1px;"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <span>Image<br/>Left</span>
                  </div>

                  <!-- Image Right Layout -->
                  <div
                    class="layout-option"
                    :class="{ active: getSelectedComponent().layout === 'image-right' }"
                    @click="updateGameLayout('image-right')"
                  >
                    <div class="layout-preview">
                      <div class="preview-content" style="display: flex; gap: 5px; align-items: flex-start;">
                        <div style="flex: 1; padding-top: 2px;">
                          <div class="preview-box" style="width: 100%; height: 4px; background: #d1d5db; border-radius: 1px; margin-bottom: 3px;"></div>
                          <div style="display: flex; gap: 2px;">
                            <div class="preview-box" style="width: 12px; height: 4px; background: #d1d5db; border-radius: 1px;"></div>
                            <div class="preview-box" style="width: 8px; height: 4px; background: #d1d5db; border-radius: 1px;"></div>
                          </div>
                        </div>
                        <div class="preview-box" style="width: 25px; height: 30px; background: #e14eca; border-radius: 3px; flex-shrink: 0;"></div>
                      </div>
                    </div>
                    <span>Image<br/>Right</span>
                  </div>
                </div>


                <h5 class="subsection-title">Display Options</h5>

                <!-- Language Selection -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Language</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().language"
                      @change="updateComponent"
                      class="select-primary"
                      placeholder="Select language"
                      style="width: 100%"
                    >
                      <el-option label="English" value="english"></el-option>
                      <el-option label="Español (Spanish)" value="spanish"></el-option>
                      <el-option label="Français (French)" value="french"></el-option>
                      <el-option label="Svenska (Swedish)" value="swedish"></el-option>
                      <el-option label="Ελληνικά (Greek)" value="greek"></el-option>
                      <el-option label="Dansk (Danish)" value="danish"></el-option>
                    </el-select>
                  </base-input>
                </div>

                <!-- Show/Hide Toggles -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showImage"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show event image</label>
                  </div>
                </div>

                <!-- Image Settings (only if showImage is true) -->
                <div v-if="getSelectedComponent().showImage">
                  <div class="form-group">
                    <label class="text-white opacity-7 small">
                      {{ getSelectedComponent().layout === 'image-top' ? 'Image width' : 'Image column width' }}
                    </label>
                    <base-input>
                      <input
                        type="range"
                        :min="getSelectedComponent().layout === 'image-top' ? '50' : '30'"
                        :max="getSelectedComponent().layout === 'image-top' ? '100' : '60'"
                        step="5"
                        v-model="getSelectedComponent().styles.imageWidth"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.imageWidth }}%</span>
                    </base-input>
                  </div>

                  <div class="form-group">
                    <label class="text-white opacity-7 small">Image border radius</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        v-model="getSelectedComponent().styles.imageBorderRadius"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.imageBorderRadius }}px</span>
                    </base-input>
                  </div>
                </div>

                <!-- Description Toggle -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showDescription"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show description</label>
                  </div>
                </div>

                <!-- Rating toggle (stored as showDifficulty) -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showDifficulty"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show ratings
                      <el-popover trigger="hover" placement="left" width="240">
                        <div>
                          <div class="popover-body" style="word-break: normal; text-align: left;">Shows every rating of the event that customers can see, each under its own name. Events with none skip this.</div>
                        </div>
                        <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                      </el-popover>
                    </label>
                  </div>
                </div>

                <!-- Duration Toggle -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showDuration"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show duration</label>
                  </div>
                </div>

                <!-- Group Size Toggle -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showGroupSize"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show group size</label>
                  </div>
                </div>

                <!-- Details Layout (only show if at least one detail is shown) -->
                <div v-if="getSelectedComponent().showDifficulty || getSelectedComponent().showDuration || getSelectedComponent().showGroupSize" class="form-group">
                  <label class="text-white opacity-7 small">Details layout</label>
                  <div class="size-buttons">
                    <button
                      class="size-btn"
                      :class="{ active: getSelectedComponent().styles.detailsLayout === 'grid' }"
                      @click="updateDetailsLayout('grid')"
                    >
                      Grid
                    </button>
                    <button
                      class="size-btn"
                      :class="{ active: getSelectedComponent().styles.detailsLayout === 'vertical' }"
                      @click="updateDetailsLayout('vertical')"
                    >
                      Vertical
                    </button>
                  </div>
                </div>

                <h5 class="subsection-title">Typography</h5>

                <!-- Font Family -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Font family</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().styles.fontFamily"
                      @change="updateComponentStyles"
                      class="select-primary"
                      placeholder="Select font"
                      filterable
                      style="width: 100%"
                    >
                      <el-option
                        v-for="font in availableFonts"
                        :key="font.label"
                        :label="font.label"
                        :value="font.value"
                        class="select-primary"
                      >
                      </el-option>
                    </el-select>
                  </base-input>
                </div>

                <!-- Font Sizes -->
                <div class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Name size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.nameFontSize"
                        @input="updateComponentStyles"
                        min="14"
                        max="48"
                      >
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Description size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.descriptionFontSize"
                        @input="updateComponentStyles"
                        min="10"
                        max="20"
                      >
                    </base-input>
                  </div>
                </div>

                <div v-if="getSelectedComponent().showDifficulty || getSelectedComponent().showDuration || getSelectedComponent().showGroupSize" class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Label size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.labelFontSize"
                        @input="updateComponentStyles"
                        min="8"
                        max="16"
                      >
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Value size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.valueFontSize"
                        @input="updateComponentStyles"
                        min="10"
                        max="20"
                      >
                    </base-input>
                  </div>
                </div>

                <h5 class="subsection-title">Colors</h5>

                <!-- Background Color -->
                <div class="row">
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Background color</label>
                    <base-input>
                      <div class="color-input-group">
                        <el-color-picker
                          v-model="getSelectedComponent().styles.backgroundColor"
                          @change="updateComponentStyles"
                          size="small"
                          :show-alpha="true"
                        ></el-color-picker>
                        <base-button simple
                                     type="primary"
                                     size="sm"
                                     @click="setGameBackgroundInherit"
                                     class="inherit-btn"
                        >
                          Inherit
                        </base-button>
                      </div>
                    </base-input>
                  </div>
                </div>

                <!-- Text Colors -->
                <div class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Name color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.nameColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div v-if="getSelectedComponent().showDescription" class="col-6">
                    <label class="text-white opacity-7 small">Description color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.descriptionColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <div v-if="getSelectedComponent().showDifficulty || getSelectedComponent().showDuration || getSelectedComponent().showGroupSize" class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Label color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.labelColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Value color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.valueColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <h5 class="subsection-title">Border & Spacing</h5>

                <!-- Border Toggle -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().styles.showBorder"
                      @change="updateComponentStyles"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show border</label>
                  </div>
                </div>

                <!-- Border Settings (only if showBorder is true) -->
                <div v-if="getSelectedComponent().styles.showBorder" class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Border color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.borderColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Border radius</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        v-model="getSelectedComponent().styles.borderRadius"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.borderRadius }}px</span>
                    </base-input>
                  </div>
                </div>

                <!-- Spacing -->

                <div class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Top margin</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        v-model="getSelectedComponent().styles.marginTop"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.marginTop }}px</span>
                    </base-input>
                  </div>
                </div>
                <div class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Bottom margin</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        v-model="getSelectedComponent().styles.marginBottom"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.marginBottom }}px</span>
                    </base-input>
                  </div>
                </div>
              </div>

              <!-- Game Master Component Properties -->
              <div v-if="getSelectedComponent()?.type === 'gameMaster'" class="game-master-properties">
                <h5 class="subsection-title">Display Options</h5>

                <!-- Display Field Selection -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Display</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().displayField"
                      @change="updateComponent"
                      class="select-primary"
                      placeholder="Select display field"
                      style="width: 100%"
                    >
                      <el-option class="select-primary" label="Full name" value="name"></el-option>
                      <el-option class="select-primary" label="First name" value="first_name"></el-option>
                      <el-option class="select-primary" label="Nickname" value="nickname"></el-option>
                    </el-select>
                  </base-input>
                </div>

                <!-- Show Role Toggle -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showRole"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show "Game Master" title</label>
                  </div>
                </div>

                <!-- Language Selection -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Language</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().language"
                      @change="updateComponent"
                      class="select-primary"
                      placeholder="Select language"
                      style="width: 100%"
                    >
                      <el-option label="English" value="english"></el-option>
                      <el-option label="Español (Spanish)" value="spanish"></el-option>
                      <el-option label="Français (French)" value="french"></el-option>
                      <el-option label="Svenska (Swedish)" value="swedish"></el-option>
                      <el-option label="Ελληνικά (Greek)" value="greek"></el-option>
                      <el-option label="Dansk (Danish)" value="danish"></el-option>
                    </el-select>
                  </base-input>
                </div>

                <h5 class="subsection-title">Layout</h5>

                <!-- Layout Options -->
                <div class="game-master-layout-options">
                  <div
                    class="gm-layout-option"
                    :class="{ active: getSelectedComponent().layout === 'image-left' }"
                    @click="updateGameMasterLayout('image-left')"
                  >
                    <div class="gm-layout-preview">
                      <div class="gm-avatar"></div>
                      <div class="gm-text-lines">
                        <div class="gm-line gm-line-long"></div>
                        <div class="gm-line gm-line-short"></div>
                      </div>
                    </div>
                    <span>Image Left</span>
                  </div>

                  <div
                    class="gm-layout-option"
                    :class="{ active: getSelectedComponent().layout === 'image-right' }"
                    @click="updateGameMasterLayout('image-right')"
                  >
                    <div class="gm-layout-preview">
                      <div class="gm-text-lines">
                        <div class="gm-line gm-line-long"></div>
                        <div class="gm-line gm-line-short"></div>
                      </div>
                      <div class="gm-avatar"></div>
                    </div>
                    <span>Image Right</span>
                  </div>

                  <div
                    class="gm-layout-option"
                    :class="{ active: getSelectedComponent().layout === 'image-top' }"
                    @click="updateGameMasterLayout('image-top')"
                  >
                    <div class="gm-layout-preview gm-layout-vertical">
                      <div class="gm-avatar"></div>
                      <div class="gm-text-lines-center">
                        <div class="gm-line gm-line-long"></div>
                        <div class="gm-line gm-line-short"></div>
                      </div>
                    </div>
                    <span>Image Top</span>
                  </div>
                </div>

                <h5 class="subsection-title">Image Settings</h5>

                <!-- Image Size -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Image size</label>
                  <base-input>
                    <input
                      type="range"
                      min="40"
                      max="150"
                      step="5"
                      v-model="getSelectedComponent().styles.imageSize"
                      @input="updateComponentStyles"
                      class="slider"
                    >
                    <span class="range-value">{{ getSelectedComponent().styles.imageSize }}px</span>
                  </base-input>
                </div>

                <!-- Image Shape -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Image shape</label>
                  <div class="size-buttons">
                    <button
                      class="size-btn"
                      :class="{ active: getSelectedComponent().styles.imageShape === 'circle' }"
                      @click="updateImageShape('circle')"
                    >
                      Circle
                    </button>
                    <button
                      class="size-btn"
                      :class="{ active: getSelectedComponent().styles.imageShape === 'square' }"
                      @click="updateImageShape('square')"
                    >
                      Square
                    </button>
                  </div>
                </div>

                <!-- Border Radius (for square only) -->
                <div v-if="getSelectedComponent().styles.imageShape === 'square'" class="form-group">
                  <label class="text-white opacity-7 small">Image border radius</label>
                  <base-input>
                    <input
                      type="range"
                      min="0"
                      max="40"
                      v-model="getSelectedComponent().styles.imageBorderRadius"
                      @input="updateComponentStyles"
                      class="slider"
                    >
                    <span class="range-value">{{ getSelectedComponent().styles.imageBorderRadius }}px</span>
                  </base-input>
                </div>

                <!-- Image Border Toggle -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().styles.showImageBorder"
                      @change="updateComponentStyles"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show image border</label>
                  </div>
                </div>

                <!-- Image Border Color -->
                <div v-if="getSelectedComponent().styles.showImageBorder" class="form-group">
                  <label class="text-white opacity-7 small">Border color</label>
                  <base-input>
                    <el-color-picker
                      v-model="getSelectedComponent().styles.imageBorderColor"
                      @change="updateComponentStyles"
                      size="small"
                    ></el-color-picker>
                  </base-input>
                </div>

                <h5 class="subsection-title">Typography</h5>

                <!-- Font Family -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Font family</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().styles.fontFamily"
                      @change="updateComponentStyles"
                      class="select-primary"
                      placeholder="Select font"
                      filterable
                      style="width: 100%"
                    >
                      <el-option
                        v-for="font in availableFonts"
                        :key="font.label"
                        :label="font.label"
                        :value="font.value"
                        class="select-primary"
                      >
                      </el-option>
                    </el-select>
                  </base-input>
                </div>

                <!-- Name Typography -->
                <div class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Name size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.nameFontSize"
                        @input="updateComponentStyles"
                        min="12"
                        max="32"
                      >
                    </base-input>
                  </div>
                </div>

                <!-- Role Typography (if shown) -->
                <div v-if="getSelectedComponent().showRole" class="row mt-2">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Title size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.roleFontSize"
                        @input="updateComponentStyles"
                        min="10"
                        max="20"
                      >
                    </base-input>
                  </div>
                </div>

                <h5 class="subsection-title">Colors</h5>

                <!-- Background Color -->
                <div class="row">
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Background color</label>
                    <base-input>
                      <div class="color-input-group">
                        <el-color-picker
                          v-model="getSelectedComponent().styles.backgroundColor"
                          @change="updateComponentStyles"
                          size="small"
                          :show-alpha="true"
                        ></el-color-picker>
                        <base-button simple
                                     type="primary"
                                     size="sm"
                                     @click="setGameMasterBackgroundInherit"
                                     class="inherit-btn"
                        >
                          Inherit
                        </base-button>
                      </div>
                    </base-input>
                  </div>
                </div>

                <!-- Text Colors -->
                <div class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Name color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.nameColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div v-if="getSelectedComponent().showRole" class="col-6">
                    <label class="text-white opacity-7 small">Title color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.roleColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <h5 class="subsection-title">Border & Spacing</h5>

                <!-- Border Toggle -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().styles.showBorder"
                      @change="updateComponentStyles"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show border</label>
                  </div>
                </div>

                <!-- Border Settings -->
                <div v-if="getSelectedComponent().styles.showBorder" class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Border color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.borderColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Border radius</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        v-model="getSelectedComponent().styles.borderRadius"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.borderRadius }}px</span>
                    </base-input>
                  </div>
                </div>

                <!-- Spacing -->
                <div class="row">
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Top margin</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        v-model="getSelectedComponent().styles.marginTop"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.marginTop }}px</span>
                    </base-input>
                  </div>
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Bottom margin</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        v-model="getSelectedComponent().styles.marginBottom"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.marginBottom }}px</span>
                    </base-input>
                  </div>
                </div>
              </div>

              <!-- Gift Card Component Properties -->
              <div v-if="getSelectedComponent()?.type === 'giftCard'" class="gift-card-properties">
                <h5 class="subsection-title">Layout</h5>

                <!-- Layout Options -->
                <div class="layout-options">
                  <div
                    class="layout-option"
                    :class="{ active: getSelectedComponent().layout === 'image-top' }"
                    @click="updateGiftCardLayout('image-top')"
                  >
                    <div class="layout-preview">
                      <div class="preview-content">
                        <div class="preview-box" style="width: 30px; height: 30px; background: #e14eca; border-radius: 3px; margin: 0 auto;"></div>
                        <div style="display: flex; gap: 3px; margin-top: 5px; justify-content: center;">
                          <div class="preview-box" style="width: 20px; height: 4px; background: #d1d5db; border-radius: 1px;"></div>
                          <div class="preview-box" style="width: 10px; height: 4px; background: #d1d5db; border-radius: 1px;"></div>
                        </div>
                      </div>
                    </div>
                    <span>Image<br/>Top</span>
                  </div>

                  <div
                    class="layout-option"
                    :class="{ active: getSelectedComponent().layout === 'image-left' }"
                    @click="updateGiftCardLayout('image-left')"
                  >
                    <div class="layout-preview">
                      <div class="preview-content" style="display: flex; gap: 5px; align-items: flex-start;">
                        <div class="preview-box" style="width: 25px; height: 30px; background: #e14eca; border-radius: 3px; flex-shrink: 0;"></div>
                        <div style="flex: 1; padding-top: 2px;">
                          <div class="preview-box" style="width: 100%; height: 4px; background: #d1d5db; border-radius: 1px; margin-bottom: 3px;"></div>
                          <div style="display: flex; gap: 2px;">
                            <div class="preview-box" style="width: 12px; height: 4px; background: #d1d5db; border-radius: 1px;"></div>
                            <div class="preview-box" style="width: 8px; height: 4px; background: #d1d5db; border-radius: 1px;"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <span>Image<br/>Left</span>
                  </div>

                  <div
                    class="layout-option"
                    :class="{ active: getSelectedComponent().layout === 'image-right' }"
                    @click="updateGiftCardLayout('image-right')"
                  >
                    <div class="layout-preview">
                      <div class="preview-content" style="display: flex; gap: 5px; align-items: flex-start;">
                        <div style="flex: 1; padding-top: 2px;">
                          <div class="preview-box" style="width: 100%; height: 4px; background: #d1d5db; border-radius: 1px; margin-bottom: 3px;"></div>
                          <div style="display: flex; gap: 2px;">
                            <div class="preview-box" style="width: 12px; height: 4px; background: #d1d5db; border-radius: 1px;"></div>
                            <div class="preview-box" style="width: 8px; height: 4px; background: #d1d5db; border-radius: 1px;"></div>
                          </div>
                        </div>
                        <div class="preview-box" style="width: 25px; height: 30px; background: #e14eca; border-radius: 3px; flex-shrink: 0;"></div>
                      </div>
                    </div>
                    <span>Image<br/>Right</span>
                  </div>
                </div>

                <h5 class="subsection-title">Display Options</h5>

                <!-- Language Selection -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Language</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().language"
                      @change="updateComponent"
                      class="select-primary"
                      placeholder="Select language"
                      style="width: 100%"
                    >
                      <el-option label="English" value="english"></el-option>
                      <el-option label="Español (Spanish)" value="spanish"></el-option>
                      <el-option label="Français (French)" value="french"></el-option>
                      <el-option label="Svenska (Swedish)" value="swedish"></el-option>
                      <el-option label="Ελληνικά (Greek)" value="greek"></el-option>
                      <el-option label="Dansk (Danish)" value="danish"></el-option>
                    </el-select>
                  </base-input>
                </div>

                <!-- Show/Hide Toggles -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showImage"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show gift card image</label>
                  </div>
                </div>

                <!-- Image Settings (only if showImage is true) -->
                <div v-if="getSelectedComponent().showImage">
                  <div class="form-group">
                    <label class="text-white opacity-7 small">
                      {{ getSelectedComponent().layout === 'image-top' ? 'Image width' : 'Image column width' }}
                    </label>
                    <base-input>
                      <input
                        type="range"
                        :min="getSelectedComponent().layout === 'image-top' ? '50' : '30'"
                        :max="getSelectedComponent().layout === 'image-top' ? '100' : '60'"
                        step="5"
                        v-model="getSelectedComponent().styles.imageWidth"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.imageWidth }}%</span>
                    </base-input>
                  </div>

                  <div class="form-group">
                    <label class="text-white opacity-7 small">Image border radius</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        v-model="getSelectedComponent().styles.imageBorderRadius"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.imageBorderRadius }}px</span>
                    </base-input>
                  </div>
                </div>

                <!-- Other toggles -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showPersonalMessage"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show personal message</label>
                  </div>
                </div>

                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showTotal"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show total value</label>
                  </div>
                </div>

                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showRemaining"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show remaining value</label>
                  </div>
                </div>

                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showActivationDate"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show activation date</label>
                  </div>
                </div>

                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showExpirationDate"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show expiration date</label>
                  </div>
                </div>

                <!-- Details Layout -->
                <div v-if="getSelectedComponent().showTotal || getSelectedComponent().showRemaining || getSelectedComponent().showActivationDate || getSelectedComponent().showExpirationDate" class="form-group">
                  <label class="text-white opacity-7 small">Details layout</label>
                  <div class="size-buttons">
                    <button
                      class="size-btn"
                      :class="{ active: getSelectedComponent().styles.detailsLayout === 'grid' }"
                      @click="updateGiftCardDetailsLayout('grid')"
                    >
                      Grid
                    </button>
                    <button
                      class="size-btn"
                      :class="{ active: getSelectedComponent().styles.detailsLayout === 'vertical' }"
                      @click="updateGiftCardDetailsLayout('vertical')"
                    >
                      Vertical
                    </button>
                  </div>
                </div>

                <h5 class="subsection-title">Typography</h5>

                <!-- Font Family -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Font family</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().styles.fontFamily"
                      @change="updateComponentStyles"
                      class="select-primary"
                      placeholder="Select font"
                      filterable
                      style="width: 100%"
                    >
                      <el-option
                        v-for="font in availableFonts"
                        :key="font.label"
                        :label="font.label"
                        :value="font.value"
                        class="select-primary"
                      >
                      </el-option>
                    </el-select>
                  </base-input>
                </div>

                <!-- Code Font Sizes -->
                <div class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Code size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.codeFontSize"
                        @input="updateComponentStyles"
                        min="16"
                        max="36"
                      >
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Code label size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.codeLabelFontSize"
                        @input="updateComponentStyles"
                        min="10"
                        max="16"
                      >
                    </base-input>
                  </div>
                </div>

                <!-- Message Font Sizes -->
                <div v-if="getSelectedComponent().showPersonalMessage" class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Message size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.messageFontSize"
                        @input="updateComponentStyles"
                        min="12"
                        max="18"
                      >
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Label size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.labelFontSize"
                        @input="updateComponentStyles"
                        min="9"
                        max="14"
                      >
                    </base-input>
                  </div>
                </div>

                <!-- Detail Font Sizes -->
                <div class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Value size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.valueFontSize"
                        @input="updateComponentStyles"
                        min="12"
                        max="20"
                      >
                    </base-input>
                  </div>
                </div>

                <h5 class="subsection-title">Colors</h5>

                <!-- Background Color -->
                <div class="row">
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Background color</label>
                    <base-input>
                      <div class="color-input-group">
                        <el-color-picker
                          v-model="getSelectedComponent().styles.backgroundColor"
                          @change="updateComponentStyles"
                          size="small"
                          :show-alpha="true"
                        ></el-color-picker>
                        <base-button simple
                                     type="primary"
                                     size="sm"
                                     @click="setGiftCardBackgroundInherit"
                                     class="inherit-btn"
                        >
                          Inherit
                        </base-button>
                      </div>
                    </base-input>
                  </div>
                </div>

                <!-- Code Colors -->
                <div class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Code color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.codeColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Code background</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.codeBackgroundColor"
                        @change="updateComponentStyles"
                        size="small"
                        :show-alpha="true"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <!-- Message Colors -->
                <div v-if="getSelectedComponent().showPersonalMessage" class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Message color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.messageColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Message background</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.messageBackgroundColor"
                        @change="updateComponentStyles"
                        size="small"
                        :show-alpha="true"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <!-- Detail Colors -->
                <div class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Label color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.labelColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Value color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.valueColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <h5 class="subsection-title">Border & Spacing</h5>

                <!-- Border Toggle -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().styles.showBorder"
                      @change="updateComponentStyles"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show border</label>
                  </div>
                </div>

                <!-- Border Settings -->
                <div v-if="getSelectedComponent().styles.showBorder" class="row">
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Border color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.borderColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Border radius</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        v-model="getSelectedComponent().styles.borderRadius"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.borderRadius }}px</span>
                    </base-input>
                  </div>
                </div>

                <!-- Code Border Toggle -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().styles.showCodeBorder"
                      @change="updateComponentStyles"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show code border</label>
                  </div>
                </div>

                <div v-if="getSelectedComponent().styles.showCodeBorder" class="row">
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Code border color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.codeBorderColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Code border radius</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        v-model="getSelectedComponent().styles.codeBorderRadius"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.codeBorderRadius }}px</span>
                    </base-input>
                  </div>
                </div>

                <!-- Spacing -->
                <div class="row">
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Top margin</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        v-model="getSelectedComponent().styles.marginTop"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.marginTop }}px</span>
                    </base-input>
                  </div>
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Bottom margin</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        v-model="getSelectedComponent().styles.marginBottom"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.marginBottom }}px</span>
                    </base-input>
                  </div>
                </div>
              </div>

              <!-- Project Ticket Component Properties -->
              <div v-if="getSelectedComponent()?.type === 'projectTicket'" class="project-ticket-properties">
                <h5 class="subsection-title">Display Options</h5>

                <!-- Display Toggles -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showTitle"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show ticket title</label>
                  </div>
                </div>

<!--                <div class="form-group">-->
<!--                  <div class="switch-control">-->
<!--                    <el-switch-->
<!--                      active-color="#e14eca"-->
<!--                      v-model="getSelectedComponent().showDescription"-->
<!--                      @change="updateComponent"-->
<!--                    />-->
<!--                    <label class="switch-label text-white opacity-7 small">&nbsp; Show description</label>-->
<!--                  </div>-->
<!--                </div>-->

                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showAssignee"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show assignee</label>
                  </div>
                </div>

                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showProjectName"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show project name</label>
                  </div>
                </div>

                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showProjectKey"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show project key</label>
                  </div>
                </div>

                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showTimeEstimate"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show time estimate</label>
                  </div>
                </div>

<!--                <div class="form-group">-->
<!--                  <div class="switch-control">-->
<!--                    <el-switch-->
<!--                      active-color="#e14eca"-->
<!--                      v-model="getSelectedComponent().showLatestComment"-->
<!--                      @change="updateComponent"-->
<!--                    />-->
<!--                    <label class="switch-label text-white opacity-7 small">&nbsp; Show latest comment</label>-->
<!--                  </div>-->
<!--                </div>-->

                <h5 class="subsection-title">Button Settings</h5>

                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().showButton"
                      @change="updateComponent"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show button</label>
                  </div>
                </div>

                <div v-if="getSelectedComponent().showButton">
                  <div class="form-group">
                    <label class="text-white opacity-7 small">Button text</label>
                    <base-input>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().buttonText"
                        placeholder="View Ticket"
                        @input="updateComponent"
                      >
                    </base-input>
                  </div>

                  <div class="form-group">
                    <label class="text-white opacity-7 small">Button size</label>
                    <div class="size-buttons">
                      <button
                        class="size-btn"
                        :class="{ active: getSelectedComponent().buttonSize === 'small' }"
                        @click="updateProjectTicketButtonSize('small')"
                      >
                        Small
                      </button>
                      <button
                        class="size-btn"
                        :class="{ active: getSelectedComponent().buttonSize === 'medium' }"
                        @click="updateProjectTicketButtonSize('medium')"
                      >
                        Medium
                      </button>
                      <button
                        class="size-btn"
                        :class="{ active: getSelectedComponent().buttonSize === 'large' }"
                        @click="updateProjectTicketButtonSize('large')"
                      >
                        Large
                      </button>
                    </div>
                  </div>

                  <div class="form-group">
                    <label class="text-white opacity-7 small">Button alignment</label>
                    <div class="alignment-buttons">
                      <button
                        class="align-btn"
                        :class="{ active: getSelectedComponent().buttonAlignment === 'left' }"
                        @click="updateProjectTicketButtonAlignment('left')"
                      >
                        <i class="fas fa-align-left"></i>
                      </button>
                      <button
                        class="align-btn"
                        :class="{ active: getSelectedComponent().buttonAlignment === 'center' }"
                        @click="updateProjectTicketButtonAlignment('center')"
                      >
                        <i class="fas fa-align-center"></i>
                      </button>
                      <button
                        class="align-btn"
                        :class="{ active: getSelectedComponent().buttonAlignment === 'right' }"
                        @click="updateProjectTicketButtonAlignment('right')"
                      >
                        <i class="fas fa-align-right"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <h5 class="subsection-title">Typography</h5>

                <div class="form-group">
                  <label class="text-white opacity-7 small">Font family</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().styles.fontFamily"
                      @change="updateComponentStyles"
                      class="select-primary"
                      placeholder="Select font"
                      filterable
                      style="width: 100%"
                    >
                      <el-option
                        v-for="font in availableFonts"
                        :key="font.label"
                        :label="font.label"
                        :value="font.value"
                        class="select-primary"
                      >
                      </el-option>
                    </el-select>
                  </base-input>
                </div>

                <!-- Font Sizes -->
                <div class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Title size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.titleFontSize"
                        @input="updateComponentStyles"
                        min="14"
                        max="32"
                      >
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Description size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.descriptionFontSize"
                        @input="updateComponentStyles"
                        min="10"
                        max="20"
                      >
                    </base-input>
                  </div>
                </div>

                <h5 class="subsection-title">Colors</h5>

                <div class="row">
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Background color</label>
                    <base-input>
                      <div class="color-input-group">
                        <el-color-picker
                          v-model="getSelectedComponent().styles.backgroundColor"
                          @change="updateComponentStyles"
                          size="small"
                          :show-alpha="true"
                        ></el-color-picker>
                        <base-button simple
                                     type="primary"
                                     size="sm"
                                     @click="setProjectTicketBackgroundInherit"
                                     class="inherit-btn"
                        >
                          Inherit
                        </base-button>
                      </div>
                    </base-input>
                  </div>
                </div>

                <div class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Title color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.titleColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Description color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.descriptionColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <div class="row">
                  <div class="col-7">
                    <label class="text-white opacity-7 small">Button background</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.buttonBackgroundColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div class="col-5">
                    <label class="text-white opacity-7 small">Button text</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.buttonTextColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <h5 class="subsection-title">Border & Spacing</h5>

                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().styles.showBorder"
                      @change="updateComponentStyles"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show border</label>
                  </div>
                </div>

                <div v-if="getSelectedComponent().styles.showBorder" class="row">
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Border color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.borderColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Border radius</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        v-model="getSelectedComponent().styles.borderRadius"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.borderRadius }}px</span>
                    </base-input>
                  </div>
                </div>

                <div class="row">
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Top margin</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        v-model="getSelectedComponent().styles.marginTop"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.marginTop }}px</span>
                    </base-input>
                  </div>
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Bottom margin</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        v-model="getSelectedComponent().styles.marginBottom"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.marginBottom }}px</span>
                    </base-input>
                  </div>
                </div>
              </div>

              <!-- Change Log Component Properties -->
              <div v-if="getSelectedComponent()?.type === 'changeLog'" class="change-log-properties">
<!--                <h5 class="subsection-title">Display Options</h5>-->

                <!-- Display Toggles -->
<!--                <div class="form-group">-->
<!--                  <div class="switch-control">-->
<!--                    <el-switch-->
<!--                      active-color="#e14eca"-->
<!--                      v-model="getSelectedComponent().showAdditionalInfo"-->
<!--                      @change="updateComponent"-->
<!--                    />-->
<!--                    <label class="switch-label text-white opacity-7 small">&nbsp; Show change details</label>-->
<!--                  </div>-->
<!--                </div>-->

<!--                <div class="form-group">-->
<!--                  <div class="switch-control">-->
<!--                    <el-switch-->
<!--                      active-color="#e14eca"-->
<!--                      v-model="getSelectedComponent().showBookingReference"-->
<!--                      @change="updateComponent"-->
<!--                    />-->
<!--                    <label class="switch-label text-white opacity-7 small">&nbsp; Show booking reference</label>-->
<!--                  </div>-->
<!--                </div>-->

                <h5 class="subsection-title">Typography</h5>

                <!-- Font Family -->
                <div class="form-group">
                  <label class="text-white opacity-7 small">Font family</label>
                  <base-input>
                    <el-select
                      v-model="getSelectedComponent().styles.fontFamily"
                      @change="updateComponentStyles"
                      class="select-primary"
                      placeholder="Select font"
                      filterable
                      style="width: 100%"
                    >
                      <el-option
                        v-for="font in availableFonts"
                        :key="font.label"
                        :label="font.label"
                        :value="font.value"
                        class="select-primary"
                      >
                      </el-option>
                    </el-select>
                  </base-input>
                </div>

                <!-- Font Sizes -->
                <div class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Timestamp size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.timestampFontSize"
                        @input="updateComponentStyles"
                        min="10"
                        max="16"
                      >
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Description size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.descriptionFontSize"
                        @input="updateComponentStyles"
                        min="12"
                        max="20"
                      >
                    </base-input>
                  </div>
                </div>

                <div class="row mt-2">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">User name size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.userNameFontSize"
                        @input="updateComponentStyles"
                        min="12"
                        max="18"
                      >
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Details size</label>
                    <base-input>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="getSelectedComponent().styles.changesContentFontSize"
                        @input="updateComponentStyles"
                        min="11"
                        max="16"
                      >
                    </base-input>
                  </div>
                </div>

                <h5 class="subsection-title">Colors</h5>

                <!-- Background Color -->
                <div class="row">
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Background color</label>
                    <base-input>
                      <div class="color-input-group">
                        <el-color-picker
                          v-model="getSelectedComponent().styles.backgroundColor"
                          @change="updateComponentStyles"
                          size="small"
                          :show-alpha="true"
                        ></el-color-picker>
                        <base-button simple
                                     type="primary"
                                     size="sm"
                                     @click="setChangeLogBackgroundInherit"
                                     class="inherit-btn"
                        >
                          Inherit
                        </base-button>
                      </div>
                    </base-input>
                  </div>
                </div>

                <!-- Icon Colors -->
                <div class="row">
<!--                  <div class="col-6">-->
<!--                    <label class="text-white opacity-7 small">Icon color</label>-->
<!--                    <base-input>-->
<!--                      <el-color-picker-->
<!--                        v-model="getSelectedComponent().styles.iconColor"-->
<!--                        @change="updateComponentStyles"-->
<!--                        size="small"-->
<!--                      ></el-color-picker>-->
<!--                    </base-input>-->
<!--                  </div>-->
                  <div class="col-6">
                    <label class="text-white opacity-7 small">User name color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.userNameColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Icon background</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.iconBackgroundColor"
                        @change="updateComponentStyles"
                        size="small"
                        :show-alpha="true"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <!-- Text Colors -->
                <div class="row">
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Timestamp color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.timestampColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div class="col-6">
                    <label class="text-white opacity-7 small">Description color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.descriptionColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <!-- Accent Color -->
<!--                <div class="row">-->
<!--                  <div class="col-12">-->
<!--                    <label class="text-white opacity-7 small">Changes accent color</label>-->
<!--                    <base-input>-->
<!--                      <el-color-picker-->
<!--                        v-model="getSelectedComponent().styles.changesAccentColor"-->
<!--                        @change="updateComponentStyles"-->
<!--                        size="small"-->
<!--                      ></el-color-picker>-->
<!--                    </base-input>-->
<!--                  </div>-->
<!--                </div>-->

                <h5 class="subsection-title">Border & Spacing</h5>

                <!-- Border Toggle -->
                <div class="form-group">
                  <div class="switch-control">
                    <el-switch
                      active-color="#e14eca"
                      v-model="getSelectedComponent().styles.showBorder"
                      @change="updateComponentStyles"
                    />
                    <label class="switch-label text-white opacity-7 small">&nbsp; Show border</label>
                  </div>
                </div>

                <!-- Border Settings -->
                <div v-if="getSelectedComponent().styles.showBorder" class="row">
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Border color</label>
                    <base-input>
                      <el-color-picker
                        v-model="getSelectedComponent().styles.borderColor"
                        @change="updateComponentStyles"
                        size="small"
                      ></el-color-picker>
                    </base-input>
                  </div>
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Border radius</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        v-model="getSelectedComponent().styles.borderRadius"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.borderRadius }}px</span>
                    </base-input>
                  </div>
                </div>

                <!-- Spacing -->
                <div class="row">
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Top margin</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        v-model="getSelectedComponent().styles.marginTop"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.marginTop }}px</span>
                    </base-input>
                  </div>
                  <div class="col-12">
                    <label class="text-white opacity-7 small">Bottom margin</label>
                    <base-input>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        v-model="getSelectedComponent().styles.marginBottom"
                        @input="updateComponentStyles"
                        class="slider"
                      >
                      <span class="range-value">{{ getSelectedComponent().styles.marginBottom }}px</span>
                    </base-input>
                  </div>
                </div>
              </div>

              <!-- Footer Component Properties Panel - Add this to the properties sidebar -->
              <div v-if="getSelectedComponent()?.type === 'footer'" class="footer-properties">
                <!-- Logo Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Logo Settings</h5>

                  <div class="form-group">
                    <div class="switch-control">
                      <el-switch
                        active-color="#e14eca"
                        v-model="getSelectedComponent().showLogo"
                        @change="updateComponent"
                      />
                      <label class="switch-label text-white opacity-7 small">&nbsp; Show logo</label>
                    </div>
                  </div>

                  <div v-if="getSelectedComponent().showLogo">
                    <!-- Upload/URL Toggle -->
                    <div class="source-toggle">
                      <button
                        class="toggle-btn"
                        :class="{ active: getSelectedComponent().sourceType === 'upload' }"
                        @click="setFooterSourceType('upload')"
                      >
                        Upload
                      </button>
                      <button
                        class="toggle-btn"
                        :class="{ active: getSelectedComponent().sourceType !== 'upload' }"
                        @click="setFooterSourceType('url')"
                      >
                        URL
                      </button>
                    </div>

                    <!-- Upload Option -->
                    <div v-if="getSelectedComponent().sourceType === 'upload'" class="upload-section mb-3">
                      <div v-if="getSelectedComponent().uploadedUrl" class="current-image">
                        <img
                          :src="getSelectedComponent().uploadedUrl"
                          alt="Current logo"
                          class="current-image-preview"
                        />
                        <div class="image-actions">
                          <ImageEditor
                            :edit="true"
                            :edited_image="getCurrentFooterImageData()"
                            size="sm"
                            :show_slider="false"
                            :show-background-color="false"
                            :show-description="false"
                            :show-fit="false"
                            @saveImage="handleFooterImageUpdate"
                          />
                          <base-button
                            type="danger"
                            size="sm"
                            icon
                            link
                            @click="clearFooterUploadedImage"
                          >
                            <i class="tim-icons icon-trash-simple"></i>
                          </base-button>
                        </div>
                      </div>

                      <div v-else>
                        <ImageEditor
                          :edit="false"
                          text="Upload Logo"
                          size="sm"
                          :show_slider="false"
                          :show-background-color="false"
                          :show-description="false"
                          :show-fit="false"
                          @saveImage="handleFooterImageUpload"
                        />
                        <p class="upload-help-text">
                          Upload your company logo. It will be stored securely and optimized for email.
                        </p>
                      </div>
                    </div>

                    <!-- URL Option -->
                    <div v-if="getSelectedComponent().sourceType !== 'upload'" class="url-section">
                      <div class="form-group">
                        <label class="text-white opacity-7 small">Logo URL</label>
                        <base-input>
                          <input
                            type="text"
                            class="form-control form-control-sm"
                            v-model="getSelectedComponent().logoSrc"
                            placeholder="https://example.com/logo.png"
                            @input="updateComponent"
                          >
                        </base-input>
                      </div>
                    </div>

                    <div class="form-group mt-3">
                      <label class="text-white opacity-7 small">Logo width</label>
                      <div class="range-input">
                        <input
                          type="range"
                          min="60"
                          max="200"
                          step="10"
                          :value="parseInt(getSelectedComponent().styles.logoWidth) || 120"
                          @input="updateFooterLogoWidth($event.target.value)"
                          class="slider"
                        >
                        <span class="range-value">{{ parseInt(getSelectedComponent().styles.logoWidth) || 120 }}px</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Address Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Address display</h5>

                  <div class="form-group">
                    <label class="text-white opacity-7 small">Show address</label>
                    <base-input>
                      <el-select
                        v-model="getSelectedComponent().addressDisplay"
                        @change="updateComponent"
                        class="select-primary"
                        placeholder="Select address option"
                        style="width: 100%"
                      >
                        <el-option class="select-primary" label="No address" value="none"></el-option>
                        <el-option class="select-primary" label="Company address" value="company"></el-option>
                        <el-option class="select-primary" label="Company group address" value="group"></el-option>
                      </el-select>
                    </base-input>
                  </div>
                </div>

                <!-- Typography Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Typography</h5>

                  <div class="form-group">
                    <label class="text-white opacity-7 small">Font family</label>
                    <base-input>
                      <el-select
                        v-model="getSelectedComponent().styles.fontFamily"
                        @change="updateComponentStyles"
                        class="select-primary"
                        placeholder="Select font"
                        filterable
                        style="width: 100%"
                        value-key="family"
                      >
                        <el-option
                          v-for="font in availableFonts"
                          :key="font.label"
                          :label="font.label"
                          :value="font.value"
                          class="select-primary"
                        >
                        </el-option>
                      </el-select>
                    </base-input>
                  </div>

                  <div class="row">
                    <div class="col">
                      <div class="form-group">
                        <label class="text-white opacity-7 small">Font size</label>
                        <base-input>
                          <input
                            type="number"
                            class="form-control form-control-sm"
                            v-model="getSelectedComponent().styles.fontSize"
                            @input="updateComponentStyles"
                            min="10"
                            max="18"
                            placeholder="13"
                          >
                        </base-input>
                      </div>
                    </div>
                    <div class="col">
                      <div class="form-group">
                        <label class="text-white opacity-7 small">Text color</label>
                        <base-input>
                          <el-color-picker
                            :predefine="['#666666', '#333333', '#000000', '#4a5568', '#718096', '#a0aec0']"
                            v-model="getSelectedComponent().styles.textColor"
                            @change="updateComponentStyles"
                            size="small"
                          ></el-color-picker>
                        </base-input>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Social Media Section -->
<!--                <div class="sidebar-section">-->
<!--                  <h5 class="subsection-title">Social media</h5>-->

<!--                  <div class="form-group">-->
<!--                    <div class="switch-control">-->
<!--                      <el-switch-->
<!--                        active-color="#e14eca"-->
<!--                        v-model="getSelectedComponent().showSocialLinks"-->
<!--                        @change="updateComponent"-->
<!--                      />-->
<!--                      <label class="switch-label text-white opacity-7 small">&nbsp; Show social links</label>-->
<!--                    </div>-->
<!--                  </div>-->

<!--                  <div v-if="getSelectedComponent().showSocialLinks" class="social-links-editor">-->
<!--                    <div-->
<!--                      v-for="(social, index) in getSelectedComponent().socialLinks"-->
<!--                      :key="social.platform + index"-->
<!--                      class="social-link-item"-->
<!--                    >-->
<!--                      <div class="social-platform">-->
<!--                        <i :class="getSocialIcon(social.platform)"></i>-->
<!--                        <span class="text-white opacity-7 small">{{ formatPlatformName(social.platform) }}</span>-->
<!--                      </div>-->
<!--                      <base-input>-->
<!--                        <input-->
<!--                          type="url"-->
<!--                          class="form-control form-control-sm"-->
<!--                          v-model="social.url"-->
<!--                          :placeholder="getSocialPlaceholder(social.platform)"-->
<!--                          @input="updateComponent"-->
<!--                        >-->
<!--                      </base-input>-->
<!--                    </div>-->
<!--                  </div>-->

<!--                  <div v-if="getSelectedComponent().showSocialLinks" class="row mt-3">-->
<!--                    <div class="col-12">-->
<!--                      <label class="text-white opacity-7 small">Social background</label>-->
<!--                      <base-input>-->
<!--                        <el-color-picker-->
<!--                          :predefine="['#333333', '#000000', '#1e3a8a', '#e14eca', '#1d8cf8', '#00bf9a']"-->
<!--                          v-model="getSelectedComponent().styles.socialBackgroundColor"-->
<!--                          @change="updateComponentStyles"-->
<!--                          size="small"-->
<!--                        ></el-color-picker>-->
<!--                      </base-input>-->
<!--                    </div>-->
<!--                    <div class="col-12">-->
<!--                      <label class="text-white opacity-7 small">Social icon color</label>-->
<!--                      <base-input>-->
<!--                        <el-color-picker-->
<!--                          :predefine="['#ffffff', '#e5e7eb', '#9ca3af', '#e14eca', '#1d8cf8']"-->
<!--                          v-model="getSelectedComponent().styles.socialIconColor"-->
<!--                          @change="updateComponentStyles"-->
<!--                          size="small"-->
<!--                        ></el-color-picker>-->
<!--                      </base-input>-->
<!--                    </div>-->
<!--                  </div>-->
<!--                </div>-->

                <!-- Appearance Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Appearance</h5>

                  <div class="form-group">
                    <label class="text-white opacity-7 small">Footer height</label>
                    <div class="range-input">
                      <input
                        type="range"
                        min="60"
                        max="150"
                        step="10"
                        :value="parseInt(getSelectedComponent().styles.height) || 80"
                        @input="updateFooterHeight($event.target.value)"
                        class="slider"
                      >
                      <span class="range-value">{{ parseInt(getSelectedComponent().styles.height) || 80 }}px</span>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Background color</label>
                      <base-input>
                        <el-color-picker
                          :predefine="['#f8f9fa', '#ffffff', '#e5e7eb', '#1e1e2e', '#27293d', '#e14eca']"
                          v-model="getSelectedComponent().styles.backgroundColor"
                          @change="updateComponentStyles"
                        ></el-color-picker>
                      </base-input>
                    </div>
                  </div>

                  <div class="form-group mt-3">
                    <div class="switch-control">
                      <el-switch
                        active-color="#e14eca"
                        v-model="getSelectedComponent().styles.showTopBorder"
                        @change="updateComponentStyles"
                      />
                      <label class="switch-label text-white opacity-7 small">&nbsp; Show top border</label>
                    </div>
                  </div>

                  <div v-if="getSelectedComponent().styles.showTopBorder" class="form-group">
                    <label class="text-white opacity-7 small">Border Color</label>
                    <base-input>
                      <el-color-picker
                        :predefine="['#e5e7eb', '#d1d5db', '#9ca3af', '#e14eca', '#393c54']"
                        v-model="getSelectedComponent().styles.borderColor"
                        @change="updateComponentStyles"
                      ></el-color-picker>
                    </base-input>
                  </div>
                </div>

                <!-- Spacing Section -->
                <div class="sidebar-section">
                  <h5 class="subsection-title">Spacing</h5>

                  <div class="row">
                    <div class="col">
                      <label class="text-white opacity-7 small">Padding top/bottom</label>
                      <base-input>
                        <input
                          type="range"
                          min="10"
                          max="60"
                          step="5"
                          :value="parseInt(getSelectedComponent().styles.paddingTop) || 20"
                          @input="updateFooterPaddingVertical($event.target.value)"
                          class="slider"
                        >
                        <span class="range-value">{{ parseInt(getSelectedComponent().styles.paddingTop) || 20 }}px</span>
                      </base-input>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr  v-if="selectedComponent" style="border-top: 1px solid #f4f5f7;">

          <!-- Layout Settings -->
          <div class="sidebar-section">
            <h4 class="section-title">Layout Settings</h4>
            <div class="row">
              <div class="col">
                <el-switch active-color="#e14eca"
                           v-model="isFullBleedLayout" />
                <label class="text-white"> &nbsp; Full width layout
                  <el-popover trigger="hover"
                              placement="right">

                    <div>
                      <div class="popover-body">Removes the fixed width container so your content spans the full width of the email. Useful when a custom HTML block supplies its own layout.</div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
            </div>
            <div class="row" v-if="templateData.layout.mode !== 'fullBleed'">
              <div class="col">
                <label class="text-white small opacity-7">Content width</label>
                <base-input>
                  <input
                    type="range"
                    min="400"
                    max="800"
                    class="slider"
                    v-model="templateData.layout.contentWidth"
                    @input="updateLayoutWidth"
                  >
                  <span class="range-value">{{ templateData.layout.contentWidth }}px</span>
                </base-input>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <label class="text-white small opacity-7">Canvas background</label>
                <base-input>
                  <div class="background-controls">
                    <el-color-picker
                      v-model="templateData.layout.backgroundColor"
                      @change="updateLayoutBackground"
                      :predefine="['#f5f5f5', '#ffffff', '#f8f9fa', '#e5e7eb', '#27293d', '#1e1e2e']"
                    ></el-color-picker>
                  </div>
                </base-input>
              </div>
            </div>
            <div class="row" v-if="templateData.layout.mode !== 'fullBleed'">
              <div class="col">
                <label class="text-white small opacity-7">Container background</label>
                <base-input>
                  <el-color-picker
                    :predefine="['#f5f5f5', '#ffffff', '#f8f9fa', '#e5e7eb', '#27293d', '#1e1e2e']"
                    v-model="templateData.layout.containerBackground"
                    @change="updateLayoutBackground"
                  ></el-color-picker>
                </base-input>
              </div>
            </div>
          </div>

          <hr style="border-top: 1px solid #f4f5f7;">
          <!-- Template Stats -->
          <div class="sidebar-section">
            <h4 class="section-title">Template Stats</h4>
            <div class="stats-list">
              <div class="stat-item">
                <span class="text-white opacity-7">Components</span>
                <span class="stat-value">{{ getTotalComponents() }}</span>
              </div>
              <div class="stat-item">
                <span class="text-white opacity-7">Estimated size</span>
                <span class="stat-value">{{ getEstimatedSize() }}</span>
              </div>
              <div class="stat-item">
                <span class="text-white opacity-7">Mobile ready</span>
                <span class="stat-value">{{ getMobileReadiness() }}%</span>
              </div>
              <div class="stat-item">
                <span class="text-white opacity-7">Attachments</span>
                <span class="stat-value">{{ attachmentCount }}</span>
              </div>
              <div class="stat-item spam-score-item">
      <span class="text-white opacity-7">
        Spam score
        <el-popover
          trigger="hover"
          placement="left"
          width="250"
        >
          <div class="spam-score-tooltip">
            <p class="tooltip-title">Spam risk: {{ spamScore.riskLevel }}</p>
            <p class="tooltip-description">Score: {{ spamScore.score }}/100 (higher is better)</p>
            <div v-if="spamScore.triggers.length > 0" class="spam-triggers">
              <p class="triggers-title">Top issues:</p>
              <ul class="triggers-list">
                <li v-for="trigger in spamScore.triggers" :key="trigger">
                  {{ trigger }}
                </li>
              </ul>
            </div>
            <p v-else class="no-issues">No content issues detected</p>
            <p class="tooltip-description" style="margin-top: 6px; font-size: 0.7rem; opacity: 0.7;">Covers wording and structure only. Sending domain authentication and unsubscribe handling are managed by the platform.</p>
          </div>
          <i slot="reference" class="tim-icons icon-alert-circle-exc" style="font-size: 0.8rem; margin-left: 0.25rem; cursor: help;"></i>
        </el-popover>
      </span>
                <span class="stat-value" :style="{ color: spamScore.riskColor }">
        {{ spamScore.score }}%
      </span>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Template Availability Modal -->
      <modal :show.sync="modals.templateAvailability"
             body-classes="p-0"
             modal-classes="modal-dialog-centered">
        <card type="secondary"
              header-classes="bg-white pb-5"
              body-classes="px-lg-5 py-lg-5"
              class="border-0 mb-0">
          <h4 class="card-title">Template availability</h4>
          <div class="d-flex align-items-center mt-2 p-2 border border-primary rounded mb-3">
            <i class="text-white fas fa-info-circle mr-2"></i>
            <small class="text-white">
              Configure where this template should be available in the system
            </small>
          </div>
          <div class="availability-options">
            <div class="availability-option">
              <div class="switch-control">
                <el-switch
                  active-color="#e14eca"
                  v-model="templateAvailability.showOnTransactionDetails"
                  :disabled="templateAvailability.loading"
                />
                <label class="switch-label text-white opacity-7">&nbsp; Show on transaction details page
                  <el-popover trigger="hover"
                              placement="right">

                    <div>
                      <div class="popover-body">When enabled, this template will be available for users <br/>to send from the transaction details page.</div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-center mt-4">
            <base-button
              type="secondary"
              @click="modals.templateAvailability = false"
              :disabled="templateAvailability.loading"
              class="mr-2">
              Cancel
            </base-button>
            <base-button
              type="primary"
              @click="saveTemplateAvailability"
              :disabled="templateAvailability.loading">
              <i v-if="templateAvailability.loading" class="tim-icons icon-refresh-02 spin mr-2"></i>
              {{ templateAvailability.loading ? 'Saving...' : 'Save settings' }}
            </base-button>
          </div>
        </card>
      </modal>

      <!-- Combined Template Settings Modal -->
      <modal :show.sync="modals.templateSettings"
             body-classes="p-0"
             modal-classes="modal-dialog-centered modal-xl modal-offset-left"
             :show-close="templateData.id !== null"
             :closable="templateData.id !== null"
             :centered="true">
        <card type="secondary"
              header-classes="bg-white pb-5"
              body-classes="px-lg-5 py-lg-5"
              class="border-0 mb-0">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h4>{{ templateData.id ? 'Edit template settings' : 'Create new template' }}</h4>
          </div>

          <!-- Template Basic Info -->
          <div class="mb-4">
            <p class="text-white text-uppercase mb-3">Basic information</p>
            <div class="row">
              <div class="col-md-6">
                <base-input
                  label="Template name"
                  :error="validationErrors.name">
                  <input
                    type="text"
                    class="form-control"
                    v-model="templateData.name"
                    placeholder="Enter template name"
                    @blur="validateField('name')"
                    @input="clearFieldError('name')"
                    required
                  >
                </base-input>
              </div>
              <div class="col-md-6">
                <base-input
                  label="Template type"
                  :error="validationErrors.template_type">
                  <el-select
                    v-model="templateData.template_type"
                    class="select-primary"
                    name="template_type"
                    placeholder="Select template type"
                    value-key="id"
                    @blur="validateField('template_type')"
                    @change="clearFieldError('template_type')"
                    style="width: 100%"
                  >
                    <el-option
                      v-for="type in templateTypes"
                      class="select-primary template-type-option"
                      :label="type.name"
                      :value="{id: type.id, name: type.name, color: type.color, icon: type.icon}"
                      :key="'T' + type.id"
                    >
                      <div class="template-type-dropdown-item">
                        <div class="type-icon-badge" :style="{ backgroundColor: type.color + '20', color: type.color }">
                          <i :class="getTypeIconClass(type.icon)"></i>
                        </div>
                        <div >
                          <div>{{ type.name }}</div>
                        </div>
                      </div>
                    </el-option>
                  </el-select>
                </base-input>
              </div>

            </div>
            <div class="row">
              <div class="col">
                <base-input label="Description">
                  <el-input
                    v-model="templateData.description"
                    :rows="3"
                    type="textarea"
                    placeholder="Brief description of this template..."
                    maxlength="500"
                    show-word-limit
                  />
                </base-input>
              </div>
            </div>
          </div>

          <!-- Email Settings -->
          <div>
            <p class="text-white text-uppercase mb-3">Email settings</p>
            <div class="row">
              <div class="col-md-6">
                <base-input
                  label="Subject line"
                  :error="validationErrors.subject_line">
                  <input
                    type="text"
                    class="form-control"
                    v-model="templateData.subject_line"
                    placeholder="Enter email subject"
                    @blur="validateField('subject_line')"
                    @input="clearFieldError('subject_line')"
                    maxlength="255"
                    required
                  >
                </base-input>
              </div>
              <div class="col-md-6">
                <base-input
                  label="Preheader text"
                  :error="validationErrors.preview_text">
                  <input
                    type="text"
                    class="form-control"
                    v-model="templateData.preview_text"
                    placeholder="Preview text shown in inbox"
                    @blur="validateField('preview_text')"
                    @input="clearFieldError('preview_text')"
                    maxlength="255"
                    required
                  >
                </base-input>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <base-input
                  label="From name"
                  :error="validationErrors.from_name">
                  <input
                    type="text"
                    class="form-control"
                    v-model="templateData.from_name"
                    placeholder="Your Company Name"
                    @blur="validateField('from_name')"
                    @input="clearFieldError('from_name')"
                    maxlength="255"
                    required
                  >
                </base-input>
              </div>
              <div class="col-md-6">
                <label>From email
                  <el-popover trigger="hover"
                              placement="right">

                    <div>
                      <div class="popover-body">Default sending domain provided. To use your own domain <br/>(e.g., noreply@yourcompany.com), verify it in email settings first.
                      </div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
                <base-input
                  :error="validationErrors.from_email"
                  :class="{ 'has-from-email-warning': fromEmailWarning && !activationBlockReason && !validationErrors.from_email }">
                  <input
                    type="email"
                    class="form-control"
                    v-model="templateData.from_email"
                    placeholder="no-reply@yourcompany.com"
                    @blur="validateField('from_email')"
                    @input="clearFieldError('from_email')"
                    maxlength="255"
                    required
                  >
                  <template slot="helperText">
                    <info-hint
                      v-if="activationBlockReason && (!validationErrors.from_email || isReservedFromDomain)"
                      variant="danger"
                      icon="icon-alert-circle-exc"
                      class="mt-2 mb-0">
                      {{ activationBlockReason }}
                      <div v-if="isReservedFromDomain" class="mt-2">
                        <base-button
                          variant="outlined"
                          tone="primary"
                          size="sm"
                          @click="useSuggestedPlatformEmail">
                          <i class="tim-icons icon-check-2"></i>
                          Use {{ suggestedPlatformEmail }}
                        </base-button>
                      </div>
                    </info-hint>
                    <info-hint
                      v-else-if="fromEmailWarning && !validationErrors.from_email"
                      variant="warning"
                      icon="icon-alert-circle-exc"
                      class="mt-2 mb-0">
                      {{ fromEmailWarning }}
                    </info-hint>
                  </template>
                </base-input>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <base-input
                  label="Reply to email"
                  :error="validationErrors.reply_to_email">
                  <input
                    type="email"
                    class="form-control"
                    v-model="templateData.reply_to_email"
                    placeholder="support@yourcompany.com"
                    @blur="validateField('reply_to_email')"
                    @input="clearFieldError('reply_to_email')"
                    maxlength="255"
                    required
                  >
                </base-input>
              </div>
            </div>
          </div>

          <div slot="footer" class="d-flex justify-content-center">
            <base-button
              type="secondary"
              @click="goBackToTemplates"
              class="mr-3">
              Back
            </base-button>
            <base-button
              type="primary"
              @click="saveTemplateSettings()">
              <i v-if="loading" class="tim-icons icon-refresh-02 spin mr-2"></i>
              {{ loading ? 'Creating...' : (templateData.id ? 'Update settings' : 'Create template') }}
            </base-button>
          </div>
        </card>
      </modal>

      <!-- Test Email Modal -->
      <modal :show.sync="modals.testEmail"
             body-classes="p-0"
             modal-classes="modal-dialog-centered">
        <card type="secondary"
              header-classes="bg-white pb-5"
              body-classes="px-lg-5 py-lg-5"
              class="border-0 mb-0">
          <h4>Send test email</h4>
          <p class="text-white mb-4">Send a test email to verify how your template looks and works.</p>
          <info-hint v-if="hasUnsavedChanges" variant="warning" icon="icon-alert-circle-exc" class="mb-3">
            This test uses the content in the editor, including unsaved changes. Save the template to keep them.
          </info-hint>

          <div class="form-group">
            <base-input
              label="Email address"
              :error="testEmailErrors.email">
              <input
                type="email"
                class="form-control"
                v-model="testEmailData.email"
                placeholder="Enter email address"
                @blur="validateTestEmail"
                @input="clearTestEmailError"
                :disabled="testEmailData.loading"
                required
              >
            </base-input>
          </div>

          <div class="row">
            <div class="col text-center">
              <base-button
                type="secondary"
                @click="modals.testEmail = false"
                :disabled="testEmailData.loading"
                class="mr-2">
                Cancel
              </base-button>
              <base-button
                type="primary"
                @click="sendTestEmailConfirm"
                :disabled="testEmailData.loading">
                <i v-if="testEmailData.loading" class="tim-icons icon-refresh-02 spin mr-2"></i>
                {{ testEmailData.loading ? 'Sending...' : 'Send Test Email' }}
              </base-button>
            </div>

          </div>
        </card>
      </modal>

      <base-alert-dialog
        :show="alertDialog.show"
        :title="alertDialog.title"
        :message="alertDialog.message"
        :variant="alertDialog.variant"
        :confirm-text="alertDialog.confirmText"
        @confirm="closeAlert"
        @cancel="closeAlert" />

      <base-alert-dialog
        :show="reservedEmailPrompt.show"
        title="From email not allowed"
        variant="warning"
        :message="reservedEmailPrompt.message"
        show-cancel
        :confirm-text="`Use ${suggestedPlatformEmail}`"
        cancel-text="Edit manually"
        @confirm="confirmReservedFromEmailFix"
        @cancel="cancelReservedFromEmailFix" />

      <html-block-drawer
        :show="showHtmlDrawer"
        :block="getSelectedComponent()"
        @close="showHtmlDrawer = false"
        @update="onHtmlBlockUpdate" />

    </div>
  </div>
</template>

<script>
import { API_LOCATION } from "@/api-config";
import axios from "axios";
import { Auth } from "aws-amplify";
import { isValidEmail as validateEmail } from "@/util/emailValidator";
import { Dropdown, DropdownItem, DropdownMenu, ColorPicker, Input, Select, Option, OptionGroup, Switch, Popover } from "element-ui";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import SmartValuesPanel from "@/components/SmartValuesPanel";
import { debounce } from 'lodash';
import moment from 'moment';
import { pickerTimeFormat } from '@/plugins/dates';
import LineItemsComponent from '@/pages/Mail/CustomEmails/Components/LineItemsComponent'
import PaymentSummaryComponent from '@/pages/Mail/CustomEmails/Components/PaymentSummaryComponent'
import AppliedPaymentsComponent from '@/pages/Mail/CustomEmails/Components/AppliedPaymentsComponent'
import CustomerResponsesComponent from '@/pages/Mail/CustomEmails/Components/CustomerResponsesComponent'
import DividerComponent from '@/pages/Mail/CustomEmails/Components/DividerComponent'
import ImageEditor from '@/components/ImageEditor'
import GameComponent from '@/pages/Mail/CustomEmails/Components/GameComponent'
import GameMasterComponent from '@/pages/Mail/CustomEmails/Components/GameMasterComponent'
import GiftCardComponent from '@/pages/Mail/CustomEmails/Components/GiftCardComponent'
import ProjectTicketComponent from '@/pages/Mail/CustomEmails/Components/ProjectTicketComponent'
import ChangeLogComponent from '@/pages/Mail/CustomEmails/Components/ChangeLogComponent'
import HtmlComponent from '@/pages/Mail/CustomEmails/Components/HtmlComponent'
import HtmlBlockDrawer from '@/pages/Mail/CustomEmails/HtmlBlockDrawer'
import AiTemplatePrompt from '@/pages/Mail/CustomEmails/AiTemplatePrompt'
import { scanEmailHtmlCompatibility, findUnsafeEmailHtml } from '@/util/emailHtml'
import { scoreEmailContent } from '@/util/spamScore'
import { gameToEventLabel } from '@/util/eventLabels'
import draggable from 'vuedraggable'

import {
  TextComponent,
  ButtonComponent,
  ImageComponent,
  HeaderComponent,
  FooterComponent,
  BillingInfoComponent,
} from '@/pages/Mail/CustomEmails/Components'

export default {
  name: "EmailTemplateBuilder",
  components: {
    TextComponent,
    ButtonComponent,
    ImageComponent,
    BillingInfoComponent,
    HeaderComponent,
    FooterComponent,
    LineItemsComponent,
    PaymentSummaryComponent,
    AppliedPaymentsComponent,
    CustomerResponsesComponent,
    DividerComponent,
    draggable,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [ColorPicker.name]: ColorPicker,
    [Input.name]: Input,
    [Select.name]: Select,
    [Option.name]: Option,
    [OptionGroup.name]: OptionGroup,
    [Switch.name]: Switch,
    [Popover.name]: Popover,
    BreadcrumbItem,
    Breadcrumb,
    SmartValuesPanel,
    ImageEditor,
    GameComponent,
    GameMasterComponent,
    GiftCardComponent,
    ProjectTicketComponent,
    ChangeLogComponent,
    HtmlComponent,
    HtmlBlockDrawer,
    AiTemplatePrompt
  },
  data() {
    return {
      loading: false,
      activeTab: 'design',
      deviceView: 'desktop',
      selectedComponent: null,
      selectedLayout: 'single',
      previewMode: 'desktop',
      showSmartValues: false,
      showHtmlDrawer: false,
      aiPromptDismissed: false,
      aiPanelOpen: false,
      aiLoading: false,
      aiError: '',
      savedEditorRange: null,
      selectedAttachments: [],
      drag: false,

      // Client-side validation errors
      validationErrors: {
        name: null,
        template_type: null,
        subject_line: null,
        preview_text: null,
        from_name: null,
        from_email: null,
        reply_to_email: null
      },

      // Group email settings (from automated_email_settings) used to warn
      // when the template's from email doesn't match the configured sender
      groupEmailSettings: null,

      templateData: {
        id: null,
        name: '',
        description: '',
        template_type: this.$route.query.type || '',
        subject_line: '',
        from_name: '',
        from_email: 'orders@offthecouchmail.com',
        reply_to_email: 'orders@offthecouchmail.com',
        preview_text: '',
        layout: {
          contentWidth: 600,
          backgroundColor: '#f5f5f5',
          containerBackground: '#ffffff',
          // 'contained' (default) or 'fullBleed'. Absent or unknown means
          // contained, which is what keeps every existing template rendering
          // exactly as before.
          mode: 'contained'
        }
      },

      templateTypes: [], // Will be populated from API

      // Add fonts array to store available fonts
      availableFonts: [
        // Sans-serif fonts
        {
          label: 'Arial',
          value: 'Arial, sans-serif',
          category: 'sans-serif',
          fallback: 'sans-serif'
        },
        {
          label: 'Helvetica',
          value: 'Helvetica, Arial, sans-serif',
          category: 'sans-serif',
          fallback: 'sans-serif'
        },
        {
          label: 'Verdana',
          value: 'Verdana, Geneva, sans-serif',
          category: 'sans-serif',
          fallback: 'sans-serif'
        },
        {
          label: 'Tahoma',
          value: 'Tahoma, Geneva, sans-serif',
          category: 'sans-serif',
          fallback: 'sans-serif'
        },
        {
          label: 'Trebuchet MS',
          value: '"Trebuchet MS", Helvetica, sans-serif',
          category: 'sans-serif',
          fallback: 'sans-serif'
        },
        {
          label: 'Segoe UI',
          value: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
          category: 'sans-serif',
          fallback: 'sans-serif'
        },

        // Serif fonts
        {
          label: 'Georgia',
          value: 'Georgia, serif',
          category: 'serif',
          fallback: 'serif'
        },
        {
          label: 'Times New Roman',
          value: '"Times New Roman", Times, serif',
          category: 'serif',
          fallback: 'serif'
        },
        {
          label: 'Garamond',
          value: 'Garamond, Baskerville, serif',
          category: 'serif',
          fallback: 'serif'
        },
        {
          label: 'Palatino',
          value: '"Palatino Linotype", "Book Antiqua", Palatino, serif',
          category: 'serif',
          fallback: 'serif'
        },

        // Monospace fonts
        {
          label: 'Courier New',
          value: '"Courier New", Courier, monospace',
          category: 'monospace',
          fallback: 'monospace'
        },
        {
          label: 'Lucida Console',
          value: '"Lucida Console", Monaco, monospace',
          category: 'monospace',
          fallback: 'monospace'
        },
        {
          label: 'Monaco',
          value: 'Monaco, "Courier New", monospace',
          category: 'monospace',
          fallback: 'monospace'
        }
      ],

      templateComponents: {
        header: [],
        body: [],
        footer: []
      },

      layoutOptions: [
        {
          id: 'single',
          name: '1 Column',
          columns: 1,
          widths: ['100%'],
          gridTemplate: '1fr'
        },
        {
          id: 'half',
          name: '2 Equal',
          columns: 2,
          widths: ['50%', '50%'],
          gridTemplate: '1fr 1fr'
        },
        {
          id: 'third',
          name: '3 Equal',
          columns: 3,
          widths: ['33.33%', '33.33%', '33.33%'],
          gridTemplate: '1fr 1fr 1fr'
        },
        {
          id: 'sidebar-left',
          name: '1/3 + 2/3',
          columns: 2,
          widths: ['33.33%', '66.67%'],
          gridTemplate: '1fr 2fr'
        },
        {
          id: 'sidebar-right',
          name: '2/3 + 1/3',
          columns: 2,
          widths: ['66.67%', '33.33%'],
          gridTemplate: '2fr 1fr'
        }
      ],

      // Enhanced available components with better text defaults
      availableComponents: [
        {
          type: 'header',
          name: 'Header',
          icon: 'tim-icons icon-header',
          defaultData: {
            imageSrc: '',
            uploadedUrl: null,
            imageAlt: 'Header Logo',
            imageLink: '',
            sourceType: 'url',
            imageMetadata: null,
            imageEditorData: null,
            styles: {
              backgroundColor: '#1e3a8a',
              paddingTop: '20',
              paddingBottom: '20',
              paddingLeft: '20',
              paddingRight: '20',
              textAlign: 'center',
              imageWidth: '200',
              marginTop: '0',
              marginBottom: '0'
            }
          }
        },
        {
          type: 'footer',
          name: 'Footer',
          icon: 'tim-icons icon-footer',
          defaultData: {
            // Address settings
            addressDisplay: 'company', // 'none', 'company', or 'group'

            // Logo settings (mirroring header)
            showLogo: true,
            logoSrc: '',
            uploadedUrl: null,
            logoAlt: 'Company Logo',
            sourceType: 'url',
            imageMetadata: null,
            imageEditorData: null,

            // Social media settings with new platforms
            showSocialLinks: true,
            socialLinks: [
              { platform: 'facebook', url: '' },
              { platform: 'x', url: '' },
              { platform: 'instagram', url: '' },
              { platform: 'linkedin', url: '' },
              { platform: 'youtube', url: '' },
              { platform: 'tripadvisor', url: '' },
              { platform: 'yelp', url: '' },
              { platform: 'morty', url: '' },
              { platform: 'website', url: '' }
            ],

            // Styles with typography settings
            styles: {
              backgroundColor: '#f8f9fa',
              paddingTop: '20',
              paddingBottom: '20',
              paddingLeft: '20',
              paddingRight: '20',
              height: '80',
              logoWidth: '120',
              showTopBorder: true,
              borderColor: '#e5e7eb',

              // Typography settings
              fontFamily: 'Arial, sans-serif',
              fontSize: '13',
              textColor: '#666666',

              // Social icon styling
              socialBackgroundColor: '#333333',
              socialIconColor: '#ffffff'
            }
          }
        },
        {
          type: 'text',
          name: 'Text',
          icon: 'tim-icons icon-align-left-2',
          defaultData: {
            content: 'Your text content here...',
            styles: {
              fontSize: '16',
              fontFamily: 'Arial, sans-serif',
              fontWeight: '400',
              color: '#333333',
              textAlign: 'left',
              lineHeight: '1.5',
              marginTop: '16',
              marginBottom: '16',
              backgroundColor: 'inherit',
            }
          }
        },
        {
          type: 'button',
          name: 'Button',
          icon: 'tim-icons icon-button-power',
          defaultData: {
            content: 'Click Here',
            linkType: 'custom',
            href: '',
            alignment: 'center', // Add alignment property
            size: 'medium', // Add size property
            styles: {
              backgroundColor: '#007bff',
              color: '#ffffff',
              borderRadius: '4px',
              marginTop: '16px',
              marginBottom: '16px'
            }
          }
        },
        {
          type: 'image',
          name: 'Image',
          icon: 'tim-icons icon-image-02',
          defaultData: {
            src: '',
            uploadedUrl: null, // Store S3 URL here
            alt: 'Image',
            href: '', // Optional link
            alignment: 'center',
            sourceType: 'url', // 'upload' or 'url'
            imageMetadata: null, // Store ImageEditor metadata
            imageEditorData: null, // Store full ImageEditor data for re-editing
            styles: {
              width: '300px',
              height: 'auto',
              marginTop: '16px',
              marginBottom: '16px',
              borderRadius: '0px'
            }
          },
          config: {
            widthPresets: [
              { label: 'Small', value: 150 },
              { label: 'Medium', value: 300 },
              { label: 'Large', value: 450 },
              { label: 'Full', value: 600 }
            ]
          }
        },
        {
          type: 'divider',
          name: 'Divider',
          icon: 'tim-icons icon-minimal-down',
          defaultData: {
            styles: {
              thickness: '1',
              lineColor: '#e5e7eb',
              marginTop: '20',
              marginBottom: '20'
            }
          }
        },
        {
          // No language key: custom HTML is single language by design,
          // like other operator-authored text.
          type: 'html',
          name: 'Custom HTML',
          icon: 'tim-icons icon-html5',
          defaultData: {
            html: '',
            hoistedStyles: '',
            styles: {
              paddingTop: '0',
              paddingBottom: '0',
              paddingLeft: '0',
              paddingRight: '0'
            }
          }
        }
      ],

      customComponents: [
        {
          type: 'lineItems',
          name: 'Purchased items',
          icon: 'tim-icons icon-bullet-list-67',
          defaultData: {
            language: 'english',
            showPrice: true, // Toggle for price column
            showTicketCategories: false, // Quantity cell lists ticket categories for bookings
            showEndTime: false, // Date/time row adds the booking's end time
            items: [], // Will use default placeholder items
            styles: {
              backgroundColor: 'inherit', // inherit by default
              fontFamily: 'Arial, sans-serif', // Font family option
              paddingTop: '20',
              paddingBottom: '20',
              paddingLeft: '20',
              paddingRight: '20',
              marginTop: '16',
              marginBottom: '16',
              borderRadius: '0',
              showBorder: false,
              borderColor: '#e5e7eb',
              fontSize: '14',
              textColor: '#333333',
              headerFontSize: '12',
              headerFontWeight: '600',
              headerColor: '#666666',
              dividerColor: '#e5e7eb'
            }
          }
        },
        {
          type: 'paymentSummary',
          name: 'Payment summary',
          icon: 'tim-icons icon-money-coins',
          defaultData: {
            language: 'english',
            showPaymentStatus: true,
            styles: {
              backgroundColor: 'inherit', // Default to inherit
              fontFamily: 'Arial, sans-serif',
              fontSize: '14',
              textColor: '#666666',
              totalFontSize: '18',
              totalColor: '#111827',
              dividerColor: '#d1d5db',
              paddingTop: '20',
              paddingBottom: '20',
              paddingLeft: '20',
              paddingRight: '20',
              marginTop: '16',
              marginBottom: '16',
              borderRadius: '8',
              showBorder: false,
              borderColor: '#e5e7eb'
            }
          }
        },
        {
          type: 'appliedPayments',
          name: 'Applied payments',
          icon: 'tim-icons icon-credit-card',
          defaultData: {
            language: 'english',
            showTotal: true,
            showSampleData: true,
            currencySymbol: '$',
            styles: {
              backgroundColor: 'inherit',
              fontFamily: 'Arial, sans-serif',
              fontSize: '14',
              titleFontSize: '16',
              titleColor: '#111827',
              textColor: '#333333',
              metaFontSize: '12',
              metaColor: '#666666',
              amountFontSize: '15',
              amountColor: '#111827',
              totalFontSize: '16',
              totalColor: '#111827',
              dividerColor: '#e5e7eb',
              itemDividerColor: '#f3f4f6',
              paddingTop: '20',
              paddingBottom: '20',
              paddingLeft: '20',
              paddingRight: '20',
              marginTop: '16',
              marginBottom: '16',
              borderRadius: '8',
              showBorder: false,
              borderColor: '#e5e7eb'
            }
          }
        },
        {
          type: 'customFields',
          name: 'Customer responses',
          icon: 'tim-icons icon-check-2',
          defaultData: {
            language: 'english',
            showTitle: true,
            showSampleData: true,
            showDividers: true,
            excludeEmpty: false,
            maxResponses: 0,
            responses: [],
            styles: {
              backgroundColor: 'inherit',
              fontFamily: 'Arial, sans-serif',
              fontSize: '14',
              questionFontSize: '13',
              questionFontWeight: '600',
              questionColor: '#4b5563',
              answerFontSize: '14',
              answerFontWeight: '400',
              answerColor: '#111827',
              metaFontSize: '11',
              metaColor: '#6b7280',
              linkColor: '#2563eb',
              paddingTop: '20',
              paddingBottom: '20',
              paddingLeft: '20',
              paddingRight: '20',
              marginTop: '16',
              marginBottom: '16',
              borderRadius: '8',
              showBorder: false,
              borderColor: '#e5e7eb',
              showDividers: true,
              dividerColor: '#e5e7eb',
              itemDividerColor: '#f3f4f6'
            }
          }
        },
        {
          type: 'billing',
          name: 'Billing info',
          icon: 'tim-icons icon-badge',
          defaultData: {
            language: 'english',
            styles: {
              fontSize: '14',
              nameFontSize: '16',
              fontFamily: 'Arial, sans-serif',
              textColor: '#666666',
              nameColor: '#333333',
              linkColor: '#007bff',
              backgroundColor: '#f8f9fa',
              statusColorPaid: '#28a745',
              statusColorUnpaid: '#dc3545',
              marginTop: '16',
              marginBottom: '16',
              paddingTop: '16',
              paddingBottom: '16',
              paddingLeft: '16',
              paddingRight: '16',
              borderRadius: '8',
              showBorder: true,
              borderColor: '#e9ecef'
            }
          }
        },
        {
          type: 'game',
          name: 'Game',
          icon: 'tim-icons icon-controller',
          defaultData: {
            // Layout option - NEW!
            layout: 'image-top', // 'image-top', 'image-left', 'image-right'

            // Display toggles
            showImage: true,
            showDescription: true,
            showDifficulty: true,
            showDuration: true,
            showGroupSize: true,

            // Image alignment (for image-top layout)
            imageAlignment: 'center',

            // Language
            language: 'english',

            styles: {
              backgroundColor: 'inherit',
              fontFamily: 'Arial, sans-serif',

              // Spacing
              paddingTop: '20',
              paddingBottom: '20',
              paddingLeft: '20',
              paddingRight: '20',
              marginTop: '16',
              marginBottom: '16',
              borderRadius: '8',
              showBorder: false,
              borderColor: '#e5e7eb',

              // Image styles
              imageWidth: '100', // For image-top layout, this is %. For side layouts, this is % of container
              imageBorderRadius: '8',

              // Name styles
              nameFontSize: '24',
              nameFontWeight: '700',
              nameColor: '#111827',

              // Description styles
              descriptionFontSize: '14',
              descriptionColor: '#6b7280',

              // Details layout
              detailsLayout: 'grid', // 'grid' or 'vertical'
              detailPadding: '8',
              detailBackgroundColor: 'transparent',
              detailBorderRadius: '4',

              // Icon styles
              iconSize: '18',
              iconColor: '#e14eca',

              // Label styles
              labelFontSize: '11',
              labelFontWeight: '600',
              labelColor: '#9ca3af',

              // Value styles
              valueFontSize: '14',
              valueFontWeight: '500',
              valueColor: '#111827'
            }
          }
        },
        {
          type: 'gameMaster',
          name: 'Game master',
          icon: 'tim-icons icon-single-02',
          defaultData: {
            // Display options
            displayField: 'name',
            showRole: true,
            language: 'english',
            layout: 'image-left',
            alignment: 'center',

            styles: {
              // Container
              backgroundColor: 'inherit',
              paddingTop: '20',
              paddingBottom: '20',
              paddingLeft: '20',
              paddingRight: '20',
              marginTop: '16',
              marginBottom: '16',
              borderRadius: '8',
              showBorder: false,
              borderColor: '#e5e7eb',

              // Typography
              fontFamily: 'Arial, sans-serif',
              nameFontSize: '18',
              nameFontWeight: '600',
              nameColor: '#111827',
              roleFontSize: '13',
              roleFontWeight: '400',
              roleColor: '#6b7280',

              // Image
              imageSize: '80',
              imageShape: 'circle', // 'circle' or 'square'
              imageBorderRadius: '8', // only for square
              showImageBorder: false,
              imageBorderColor: '#e5e7eb'
            }
          }
        },
        {
          type: 'projectTicket',
          name: 'Project ticket',
          icon: 'tim-icons icon-notes',
          defaultData: {
            // Display toggles
            showTitle: true,
            showDescription: false,
            showAssignee: true,
            showProjectName: true,
            showProjectKey: true,
            showTimeEstimate: true,
            showLatestComment: false,
            showButton: true,

            // Button settings
            buttonText: 'View Ticket',
            buttonSize: 'medium',
            buttonAlignment: 'left',
            linkType: 'custom', // or 'ticket'

            // Language
            language: 'english',

            styles: {
              backgroundColor: 'inherit',
              fontFamily: 'Arial, sans-serif',

              // Spacing
              paddingTop: '20',
              paddingBottom: '20',
              paddingLeft: '20',
              paddingRight: '20',
              marginTop: '16',
              marginBottom: '16',
              borderRadius: '8',
              showBorder: true,
              borderColor: '#e5e7eb',
              dividerColor: '#e5e7eb',

              // Ticket number
              ticketNumberFontSize: '14',
              ticketNumberFontWeight: '600',
              ticketNumberColor: '#6b7280',

              // Assignee
              assigneeFontSize: '13',
              assigneeColor: '#6b7280',

              // Project info
              projectKeyFontSize: '12',
              projectKeyFontWeight: '600',
              projectKeyColor: '#e14eca',
              projectKeyBackgroundColor: 'rgba(225, 78, 202, 0.1)',
              projectNameFontSize: '13',
              projectNameFontWeight: '500',
              projectNameColor: '#4b5563',

              // Title
              titleFontSize: '20',
              titleFontWeight: '700',
              titleColor: '#111827',

              // Description
              descriptionFontSize: '14',
              descriptionColor: '#4b5563',

              // Meta (time estimate)
              metaFontSize: '13',
              metaColor: '#6b7280',

              // Comment
              commentBackgroundColor: '#f9fafb',
              commentBorderColor: '#e5e7eb',
              commentHeaderFontSize: '12',
              commentHeaderColor: '#6b7280',
              commentTextFontSize: '13',
              commentTextColor: '#4b5563',

              // Button
              buttonBackgroundColor: '#e14eca',
              buttonTextColor: '#ffffff',
              buttonBorderRadius: '6',

              // Icons
              iconColor: '#9ca3af'
            }
          }
        },
        {
          type: 'giftCard',
          name: 'Gift card',
          icon: 'tim-icons icon-gift-2',
          defaultData: {
            // Layout option
            layout: 'image-top', // 'image-top', 'image-left', 'image-right'

            // Display toggles
            showImage: true,
            showPersonalMessage: true,
            showTotal: true,
            showRemaining: true,
            showActivationDate: true,
            showExpirationDate: true,

            // Image alignment (for image-top layout)
            imageAlignment: 'center',

            // Language
            language: 'english',

            styles: {
              backgroundColor: 'inherit',
              fontFamily: 'Arial, sans-serif',

              // Spacing
              paddingTop: '20',
              paddingBottom: '20',
              paddingLeft: '20',
              paddingRight: '20',
              marginTop: '16',
              marginBottom: '16',
              borderRadius: '8',
              showBorder: false,
              borderColor: '#e5e7eb',

              // Image styles
              imageWidth: '100',
              imageBorderRadius: '8',

              // Code section styles
              codePadding: '16',
              codeBackgroundColor: 'rgba(225, 78, 202, 0.1)',
              codeBorderRadius: '8',
              showCodeBorder: true,
              codeBorderColor: '#e14eca',
              codeLabelFontSize: '12',
              codeLabelFontWeight: '600',
              codeLabelColor: '#6b7280',
              codeFontSize: '24',
              codeFontWeight: '700',
              codeColor: '#e14eca',
              codeFont: 'Monaco, Courier, monospace',

              // Personal message styles
              messagePadding: '12',
              messageBackgroundColor: 'rgba(249, 250, 251, 0.5)',
              messageBorderRadius: '6',
              messageBorderColor: '#e14eca',
              messageLabelFontSize: '11',
              messageLabelFontWeight: '600',
              messageLabelColor: '#6b7280',
              messageFontSize: '14',
              messageFontWeight: '400',
              messageColor: '#374151',

              // Details layout
              detailsLayout: 'grid', // 'grid' or 'vertical'
              detailPadding: '12',
              detailBackgroundColor: 'transparent',
              detailBorderRadius: '6',
              showDetailBorder: false,
              detailBorderColor: '#e5e7eb',

              // Label styles
              labelFontSize: '11',
              labelFontWeight: '600',
              labelColor: '#9ca3af',

              // Value styles
              valueFontSize: '16',
              valueFontWeight: '600',
              valueColor: '#111827'
            }
          }
        },
        {
          type: 'changeLog',
          name: 'Changes',
          icon: 'tim-icons icon-refresh-02',
          defaultData: {
            // Display toggles
            showAdditionalInfo: false,
            showBookingReference: false,

            styles: {
              // Container
              backgroundColor: 'inherit',
              paddingTop: '20',
              paddingBottom: '20',
              paddingLeft: '20',
              paddingRight: '20',
              marginTop: '16',
              marginBottom: '16',
              borderRadius: '8',
              showBorder: false,
              borderColor: '#e5e7eb',

              // Typography
              fontFamily: 'Arial, sans-serif',

              // Icon
              iconColor: '#e14eca',
              iconBackgroundColor: 'rgba(225, 78, 202, 0.1)',

              // Timestamp
              timestampFontSize: '12',
              timestampFontWeight: '500',
              timestampColor: '#6b7280',

              // Description
              descriptionFontSize: '16',
              descriptionFontWeight: '600',
              descriptionColor: '#111827',

              // User section
              userSectionBackgroundColor: 'rgba(249, 250, 251, 0.5)',
              userNameFontSize: '14',
              userNameFontWeight: '600',
              userNameColor: '#111827',
              userRoleFontSize: '12',
              userRoleColor: '#6b7280',

              // Changes section
              changesSectionBackgroundColor: 'rgba(225, 78, 202, 0.05)',
              changesAccentColor: '#e14eca',
              changesLabelFontSize: '11',
              changesLabelColor: '#6b7280',
              changesContentFontSize: '13',
              changesContentColor: '#374151',

              // Booking reference
              bookingRefFontSize: '12',
              bookingRefColor: '#6b7280',
              bookingRefIconColor: '#9ca3af'
            }
          }
        },
      ],
      attachmentComponents: [
        {
          type: 'attachment_invoice',
          name: 'Invoice',
          icon: 'tim-icons icon-paper',
          category: 'attachment'
        },
        {
          type: 'attachment_photo',
          name: 'Photo',
          icon: 'tim-icons icon-image-02',
          category: 'attachment'
        }
      ],

      // Font size presets for quick selection
      fontSizePresets: [
        { label: 'Small', value: '12' },
        { label: 'Regular', value: '16' },
        { label: 'Medium', value: '18' },
        { label: 'Large', value: '24' },
        { label: 'XLarge', value: '32' },
        { label: 'XXLarge', value: '48' }
      ],

      // Font weight options
      fontWeightOptions: [
        { label: 'Light', value: '300' },
        { label: 'Regular', value: '400' },
        { label: 'Medium', value: '500' },
        { label: 'Semi Bold', value: '600' },
        { label: 'Bold', value: '700' },
        { label: 'Extra Bold', value: '800' }
      ],

      availableVariables: [
        { key: 'customer.firstName', syntax: '{{customer.firstName}}', description: 'Customer first name' },
        { key: 'customer.lastName', syntax: '{{customer.lastName}}', description: 'Customer last name' },
        { key: 'customer.email', syntax: '{{customer.email}}', description: 'Customer email' },
        { key: 'booking.id', syntax: '{{booking.id}}', description: 'Booking ID' },
        { key: 'booking.date', syntax: '{{booking.date}}', description: 'Booking date' },
        { key: 'booking.time', syntax: '{{booking.time}}', description: 'Booking time' },
        { key: 'company.name', syntax: '{{company.name}}', description: 'Company name' },
        { key: 'company.phone', syntax: '{{company.phone}}', description: 'Company phone' }
      ],
      testEmailData: {
        email: '',
        loading: false
      },
      testEmailErrors: {
        email: null
      },
      // Serialized template as of the last load or successful save; drives
      // the unsaved changes indicator.
      savedSnapshot: null,
      templateAvailability: {
        showOnTransactionDetails: false,
        loading: false
      },
      modals: {
        templateSettings: false,
        testEmail: false,
        templateAvailability: false,
      },
      // Single-button notices (success, validation, load and save failures).
      alertDialog: {
        show: false,
        title: '',
        message: '',
        variant: 'warning',
        confirmText: 'OK',
        onClose: null,
      },
      // Prompt shown when a save carries the reserved platform sending domain.
      // `retry` re-runs whichever save path raised it once the address is fixed.
      reservedEmailPrompt: {
        show: false,
        message: '',
        retry: null,
      },

      previewUrl: null,

      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id
    }
  },
  computed: {
    hasUnsavedChanges() {
      if (this.savedSnapshot === null) {
        return this.templateComponents.header.length + this.templateComponents.body.length + this.templateComponents.footer.length > 0
      }
      return JSON.stringify(this.generateTemplateJSON()) !== this.savedSnapshot
    },
    // Full width layout toggle. $set on write so templates saved before the
    // mode key existed stay reactive when it is first added.
    isFullBleedLayout: {
      get() {
        return this.templateData.layout.mode === 'fullBleed'
      },
      set(value) {
        this.$set(this.templateData.layout, 'mode', value ? 'fullBleed' : 'contained')
        this.updateLayoutBackground()
      }
    },

    // Auto-shown while creating a brand new template with an empty canvas;
    // openable any time from the canvas toolbar bulb for AI assist.
    showAiPrompt() {
      if (this.aiPanelOpen) return true
      if (this.aiPromptDismissed) return false
      if (this.templateData.id) return false
      return this.canvasIsEmpty
    },

    canvasIsEmpty() {
      return this.templateComponents.header.length === 0
        && this.templateComponents.body.length === 0
        && this.templateComponents.footer.length === 0
    },


    // Cached content score. A computed so the full HTML generation runs once
    // per content change instead of once per template reference per render.
    spamScore() {
      return scoreEmailContent({
        subject: this.templateData.subject_line || '',
        bodyText: this.getPlainTextFromComponents(),
        html: this.generateEmailHTML(),
        imageCount: this.countImageComponents(),
      })
    },

    attachmentCount() {
      return this.selectedAttachments.length;
    },

    isAttachmentSelected() {
      return (type) => this.selectedAttachments.includes(type);
    },

    // Non-blocking warning when the from email's domain won't deliver.
    // Platform-owned sending domains never warn; the group's own domain warns
    // only while verification is pending; any other domain gets a warning.
    fromEmailWarning() {
      const entered = (this.templateData.from_email || '').trim().toLowerCase()
      if (!entered || !this.isValidEmail(entered) || !this.groupEmailSettings) {
        return null
      }

      const enteredDomain = entered.split('@')[1]
      const platformDomains = ['offthecouch.io', 'offthecouchmail.com']
      if (platformDomains.includes(enteredDomain)) {
        return null
      }

      // SES verifies the domain, not the mailbox: any address on the verified
      // domain delivers, so only a domain difference warns.
      const configuredDomain = (this.groupEmailSettings.domain || '').trim().toLowerCase()
      if (configuredDomain && enteredDomain === configuredDomain) {
        if (!this.groupEmailSettings.custom_domain_verified) {
          return 'This domain is pending verification. Emails may not send until verification completes.'
        }
        return null
      }

      return `${enteredDomain} is not your verified sending domain${configuredDomain ? ` (${configuredDomain})` : ''}. Emails sent from an unverified domain will fail to deliver.`
    },

    // True when the from email sits on the reserved platform system domain,
    // which is the one block a single click can fix.
    isReservedFromDomain() {
      return this.isReservedDomainEmail(this.templateData.from_email)
    },

    // A ready-to-use address on the platform sending domain, keeping whatever
    // mailbox name the operator already chose.
    suggestedPlatformEmail() {
      const entered = (this.templateData.from_email || '').trim().toLowerCase()
      const at = entered.lastIndexOf('@')
      const local = (at === -1 ? entered : entered.slice(0, at)).replace(/[^a-z0-9._-]/g, '')
      return `${local || 'no-reply'}@offthecouchmail.com`
    },

    // Mirrors the backend activation gate. Non-null means "Save & activate" is
    // refused; the same string is what the backend would return.
    activationBlockReason() {
      const entered = (this.templateData.from_email || '').trim().toLowerCase()
      const at = entered.lastIndexOf('@')
      const domain = at === -1 ? '' : entered.slice(at + 1)

      if (!entered || !this.isValidEmail(entered) || !domain) {
        return 'This template needs a valid from email before it can be activated.'
      }
      if (domain === 'offthecouchmail.com') {
        return null
      }
      if (domain === 'offthecouch.io') {
        return `offthecouch.io is reserved for platform system emails. Change the from email above to ${this.suggestedPlatformEmail}, or to an address on a domain you have verified under email and messaging settings.`
      }

      const settings = this.groupEmailSettings
      const configured = (settings && settings.domain ? String(settings.domain) : '').trim().toLowerCase()
      if (!settings
          || !configured
          || configured !== domain
          || !settings.custom_domain_confirmed
          || !settings.custom_domain_verified) {
        return `${domain} is not a verified sending domain for this group. Verify it under email and messaging settings before activating this template.`
      }

      return null
    },
  },
  methods: {
    markTemplateSaved() {
      this.savedSnapshot = JSON.stringify(this.generateTemplateJSON())
    },
    // Display only: tiles read "Event" and "Event host"; component types and saved data keep their names.
    gameToEventLabel,
    updateProjectTicketButtonSize(size) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'projectTicket') {
        component.buttonSize = size
        this.updateComponent()
      }
    },

    updateProjectTicketButtonAlignment(alignment) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'projectTicket') {
        component.buttonAlignment = alignment
        this.updateComponent()
      }
    },

    setProjectTicketBackgroundInherit() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'projectTicket') {
        component.styles.backgroundColor = 'inherit'
        this.updateComponentStyles()
      }
    },
    setGiftCardBackgroundInherit() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'giftCard') {
        component.styles.backgroundColor = 'inherit'
        this.updateComponentStyles()
      }
    },

    updateGiftCardLayout(layout) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'giftCard') {
        this.$set(component, 'layout', layout)

        // Adjust default image width based on layout
        if (layout === 'image-top') {
          component.styles.imageWidth = '100'
        } else {
          component.styles.imageWidth = '40'
        }

        this.updateComponent()
      }
    },

    updateGiftCardDetailsLayout(layout) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'giftCard') {
        component.styles.detailsLayout = layout
        this.updateComponentStyles()
      }
    },
    updateGameMasterLayout(layout) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'gameMaster') {
        this.$set(component, 'layout', layout)
        this.updateComponent()
      }
    },

    updateGameMasterAlignment(alignment) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'gameMaster') {
        this.$set(component, 'alignment', alignment)
        this.updateComponent()
      }
    },

    updateImageShape(shape) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'gameMaster') {
        component.styles.imageShape = shape
        this.updateComponentStyles()
      }
    },

    setGameMasterBackgroundInherit() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'gameMaster') {
        component.styles.backgroundColor = 'inherit'
        this.updateComponentStyles()
      }
    },
    generateGameMasterHTML(component) {
      const styles = component.styles || {}
      const layout = component.layout || 'image-left'
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      const containerStyles = `
      background-color: ${backgroundColor};
      padding: ${styles.paddingTop || '20'}px ${styles.paddingRight || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingLeft || '20'}px;
      margin: ${styles.marginTop || '16'}px 0 ${styles.marginBottom || '16'}px 0;
      border-radius: ${styles.borderRadius || '8'}px;
      font-family: ${styles.fontFamily || 'Arial, sans-serif'};
      ${styles.showBorder ? `border: 1px solid ${styles.borderColor || '#e5e7eb'};` : ''}
    `

      let html = `<div style="${containerStyles}">`

      // Wrapper with layout
      let wrapperStyles = ''
      if (layout === 'image-top') {
        wrapperStyles = `display: flex; flex-direction: column; align-items: ${component.alignment || 'center'}; gap: 12px;`
      } else {
        wrapperStyles = `display: flex; flex-direction: ${layout === 'image-left' ? 'row' : 'row-reverse'}; gap: 16px; align-items: center;`
      }

      html += `<div style="${wrapperStyles}">`

      // Image
      const imageSize = styles.imageSize || '80'
      const imageStyles = `
      width: ${imageSize}px;
      height: ${imageSize}px;
      border-radius: ${styles.imageShape === 'square' ? `${styles.imageBorderRadius || '8'}px` : '50%'};
      ${styles.showImageBorder ? `border: 2px solid ${styles.imageBorderColor || '#e5e7eb'};` : ''}
      object-fit: cover;
      flex-shrink: 0;
    `

      // Use smart value for image - backend will replace this
      const displayField = component.displayField || 'name'
      html += `<img src="{{game_master.img}}" alt="{{game_master.${displayField}}}" style="${imageStyles}" />`

      // Content
      const contentAlign = layout === 'image-top' ? (component.alignment || 'center') : 'left'
      html += `<div style="flex: 1; min-width: 0; text-align: ${contentAlign};">`

      // Name/Nickname
      const nameStyles = `
      font-size: ${styles.nameFontSize || '18'}px;
      font-weight: ${styles.nameFontWeight || '600'};
      color: ${styles.nameColor || '#111827'};
      margin: 0;
      font-family: ${styles.fontFamily || 'Arial, sans-serif'};
      line-height: 1.3;
    `

      html += `<div style="${nameStyles}">{{game_master.${displayField}}}</div>`

      // Role (if shown)
      if (component.showRole) {
        const roleStyles = `
        font-size: ${styles.roleFontSize || '13'}px;
        font-weight: ${styles.roleFontWeight || '400'};
        color: ${styles.roleColor || '#6b7280'};
        margin: 4px 0 0 0;
        font-family: ${styles.fontFamily || 'Arial, sans-serif'};
      `

        // Get localized label
        const labels = {
          english: 'Game Master',
          spanish: 'Maestro de Juego',
          french: 'Maître du Jeu',
          swedish: 'Spelledare',
          danish: 'Spilleder',
          greek: 'Αρχηγός Παιχνιδιού'
        }
        const roleLabel = labels[component.language || 'english'] || labels.english

        html += `<div style="${roleStyles}">${roleLabel}</div>`
      }

      html += '</div>' // Close content
      html += '</div>' // Close wrapper
      html += '</div>' // Close container

      return html
    },
  // Game component helper methods
    updateGameLayout(layout) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'game') {
        this.$set(component, 'layout', layout)

        // Adjust default image width based on layout
        if (layout === 'image-top') {
          component.styles.imageWidth = '100'
        } else {
          component.styles.imageWidth = '40'
        }

        this.updateComponent()
      }
    },

    updateDetailsLayout(layout) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'game') {
        component.styles.detailsLayout = layout
        this.updateComponentStyles()
      }
    },

    setGameBackgroundInherit() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'game') {
        component.styles.backgroundColor = 'inherit'
        this.updateComponentStyles()
      }
    },

    updateGamePadding(value) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'game') {
        // Update all padding values together
        component.styles.paddingTop = value
        component.styles.paddingBottom = value
        component.styles.paddingLeft = value
        component.styles.paddingRight = value
        this.updateComponentStyles()
      }
    },
    getPlainTextFromComponents() {
      let text = ''

      const extractText = (component) => {
        if (component.type === 'text') {
          text += (component.content || '') + ' '
        } else if (component.type === 'button') {
          text += (component.content || '') + ' '
        } else if (component.type === 'html') {
          text += (component.html || '').replace(/<[^>]*>/g, ' ') + ' '
        } else if (component.type === 'row' && component.columns) {
          component.columns.forEach(column => {
            column.forEach(extractText)
          })
        }
      }

      this.templateComponents.header.forEach(extractText)
      this.templateComponents.body.forEach(extractText)
      this.templateComponents.footer.forEach(extractText)

      return text
    },
    countImageComponents() {
      let count = 0

      const countImages = (component) => {
        if (component.type === 'image' || component.type === 'header') {
          count += 1
        } else if (component.type === 'html') {
          count += ((component.html || '').match(/<img\b/gi) || []).length
        } else if (component.type === 'row' && component.columns) {
          component.columns.forEach(column => {
            column.forEach(countImages)
          })
        }
      }

      this.templateComponents.header.forEach(countImages)
      this.templateComponents.body.forEach(countImages)
      this.templateComponents.footer.forEach(countImages)

      return count
    },
    setTextBackgroundInherit() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'text') {
        component.styles.backgroundColor = 'inherit'
        this.updateComponentStyles()
      }
    },
    setHeaderSourceType(type) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'header') {
        this.$set(component, 'sourceType', type)
        this.updateComponent()
      }
    },

    getCurrentHeaderImageData() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'header') {
        return component.imageEditorData || {
          url: component.uploadedUrl || component.imageSrc,
          description: component.imageAlt || '',
          scale: 100,
          background_color: '#e8e7ec',
          object_fit: 'cover',
          x: 0,
          y: 0,
          image_id: component.imageMetadata?.imageId || ''
        }
      }
      return null
    },

    handleHeaderImageUpload(imageData) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'header') {
        component.uploadedUrl = imageData.url
        component.sourceType = 'upload'

        component.imageEditorData = {
          url: imageData.url,
          description: imageData.description || '',
          scale: imageData.scale || 100,
          background_color: imageData.background_color || '#e8e7ec',
          object_fit: imageData.object_fit || 'cover',
          x: imageData.x || 0,
          y: imageData.y || 0,
          image_id: imageData.image_id || ''
        }

        if (imageData.description) {
          component.imageAlt = imageData.description
        }

        component.imageMetadata = {
          uploadedAt: new Date().toISOString(),
          originalFile: imageData.file || null,
          imageId: imageData.image_id || null
        }

        this.updateComponent()

        this.$notify({
          title: 'Logo Uploaded',
          message: 'Your header logo has been uploaded successfully',
          type: 'success',
          duration: 3000
        })
      }
    },

    handleHeaderImageUpdate(imageData) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'header') {
        if (imageData.url) {
          component.uploadedUrl = imageData.url
        }

        component.imageEditorData = {
          url: imageData.url,
          description: imageData.description || '',
          scale: imageData.scale || 100,
          background_color: imageData.background_color || '#e8e7ec',
          object_fit: imageData.object_fit || 'cover',
          x: imageData.x || 0,
          y: imageData.y || 0,
          image_id: imageData.image_id || component.imageEditorData?.image_id || ''
        }

        if (imageData.description) {
          component.imageAlt = imageData.description
        }

        if (!component.imageMetadata) {
          component.imageMetadata = {}
        }
        component.imageMetadata.lastUpdated = new Date().toISOString()
        if (imageData.image_id) {
          component.imageMetadata.imageId = imageData.image_id
        }

        this.updateComponent()

        this.$notify({
          title: 'Logo Updated',
          message: 'Your header logo has been updated successfully',
          type: 'success',
          duration: 3000
        })
      }
    },

    clearHeaderUploadedImage() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'header') {
        component.uploadedUrl = null
        component.imageSrc = ''
        component.imageMetadata = null
        component.imageEditorData = null
        component.sourceType = 'url'
        this.updateComponent()

        this.$notify({
          title: 'Logo Removed',
          message: 'The header logo has been removed',
          type: 'info',
          duration: 2000
        })
      }
    },
    getCurrentImageData() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'image') {
        // Return stored ImageEditor data or create new one
        return component.imageEditorData || {
          url: component.uploadedUrl,
          description: component.alt || '',
          scale: 100,
          background_color: '#e8e7ec',
          object_fit: 'cover',
          x: 0,
          y: 0,
          image_id: component.imageMetadata?.imageId || ''
        }
      }
      return null
    },

    // Handle new image upload
    handleImageUpload(imageData) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'image') {
        // Store the uploaded URL
        component.uploadedUrl = imageData.url
        component.sourceType = 'upload'

        // Store ImageEditor specific data for re-editing
        component.imageEditorData = {
          url: imageData.url,
          description: imageData.description || '',
          scale: imageData.scale || 100,
          background_color: imageData.background_color || '#e8e7ec',
          object_fit: imageData.object_fit || 'cover',
          x: imageData.x || 0,
          y: imageData.y || 0,
          image_id: imageData.image_id || ''
        }

        // Use description as alt text if provided
        if (imageData.description) {
          component.alt = imageData.description
        }

        // Store metadata
        component.imageMetadata = {
          uploadedAt: new Date().toISOString(),
          originalFile: imageData.file || null,
          imageId: imageData.image_id || null
        }

        this.updateComponent()

        // Show success notification
        this.$notify({
          title: 'Image Uploaded',
          message: 'Your image has been uploaded successfully',
          type: 'success',
          duration: 3000
        })
      }
    },

    // Handle image update (when editing existing uploaded image)
    handleImageUpdate(imageData) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'image') {
        // Update the uploaded URL if changed
        if (imageData.url) {
          component.uploadedUrl = imageData.url
        }

        // Update ImageEditor data
        component.imageEditorData = {
          url: imageData.url,
          description: imageData.description || '',
          scale: imageData.scale || 100,
          background_color: imageData.background_color || '#e8e7ec',
          object_fit: imageData.object_fit || 'cover',
          x: imageData.x || 0,
          y: imageData.y || 0,
          image_id: imageData.image_id || component.imageEditorData?.image_id || ''
        }

        // Update alt text if description changed
        if (imageData.description) {
          component.alt = imageData.description
        }

        // Update metadata
        if (!component.imageMetadata) {
          component.imageMetadata = {}
        }
        component.imageMetadata.lastUpdated = new Date().toISOString()
        if (imageData.image_id) {
          component.imageMetadata.imageId = imageData.image_id
        }

        this.updateComponent()

        // Show success notification
        this.$notify({
          title: 'Image Updated',
          message: 'Your image has been updated successfully',
          type: 'success',
          duration: 3000
        })
      }
    },

    // Clear uploaded image
    clearUploadedImage() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'image') {
        component.uploadedUrl = null
        component.src = ''
        component.imageMetadata = null
        component.imageEditorData = null
        component.sourceType = 'url'
        this.updateComponent()

        this.$notify({
          title: 'Image Removed',
          message: 'The uploaded image has been removed',
          type: 'info',
          duration: 2000
        })
      }
    },

    // Set image source type
    setImageSourceType(type) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'image') {
        component.sourceType = type
        this.updateComponent()
      }
    },

    // Get image width presets
    getImageWidthPresets() {
      const imageComponent = this.availableComponents.find(c => c.type === 'image')
      return imageComponent?.config?.widthPresets || [
        { label: 'Small', value: 150 },
        { label: 'Medium', value: 300 },
        { label: 'Large', value: 450 },
        { label: 'Full', value: 600 }
      ]
    },

    // Image alignment
    updateImageAlignment(alignment) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'image') {
        // Update alignment property using Vue.set for reactivity
        this.$set(component, 'alignment', alignment)

        // Also update in styles for consistency
        if (!component.styles) {
          this.$set(component, 'styles', {})
        }
        this.$set(component.styles, 'textAlign', alignment)

        // Force Vue to update
        this.updateComponent()
        this.$nextTick(() => {
          this.$forceUpdate()
        })
      }
    },

    // Image width
    updateImageWidth(width) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'image') {
        const widthValue = parseInt(width)
        component.styles.width = widthValue + 'px'
        this.updateComponentStyles()
      }
    },

    // Image border radius
    updateImageBorderRadius(radius) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'image') {
        component.styles.borderRadius = radius + 'px'
        this.updateComponentStyles()
      }
    },

    // Image margins
    updateImageMarginTop(value) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'image') {
        component.styles.marginTop = value + 'px'
        this.updateComponentStyles()
      }
    },

    updateImageMarginBottom(value) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'image') {
        component.styles.marginBottom = value + 'px'
        this.updateComponentStyles()
      }
    },

    // Upload click handler
    onImageUploadClick() {
      console.log('Image upload initiated')
    },

    // Update selectComponent to initialize image components
    updateContainerBackground() {
      if (!this.templateData.layout.containerBackground) {
        this.templateData.layout.containerBackground = '#ffffff'
      }
      this.$forceUpdate()
    },
    updateDividerThickness(thickness) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'divider') {
        component.styles.thickness = thickness
        this.updateComponentStyles()
      }
    },
    toggleAttachment(type) {
      const index = this.selectedAttachments.indexOf(type);
      if (index > -1) {
        this.selectedAttachments.splice(index, 1);
      } else {
        this.selectedAttachments.push(type);
      }
    },
    setBillingBackgroundInherit() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'billing') {
        component.styles.backgroundColor = 'inherit'
        this.updateComponentStyles()
      }
    },
    setAppliedPaymentsBackgroundInherit() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'appliedPayments') {
        component.styles.backgroundColor = 'inherit'
        this.updateComponentStyles()
      }
    },
    // Add this method to your methods section in EmailTemplateBuilder.vue
    getTypeIconClass(iconName) {
      return `tim-icons icon-${iconName}`;
    },
    setShowTicketCategories(value) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'lineItems') {
        // $set because line items blocks saved before this option existed lack the key
        this.$set(component, 'showTicketCategories', value)
        this.updateComponent()
      }
    },
    setShowEndTime(value) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'lineItems') {
        // $set because line items blocks saved before this option existed lack the key
        this.$set(component, 'showEndTime', value)
        this.updateComponent()
      }
    },
    setLineItemsBackgroundInherit() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'lineItems') {
        component.styles.backgroundColor = 'inherit'
        this.updateComponentStyles()
      }
    },
    updateButtonBorderRadius(value) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'button') {
        component.styles.borderRadius = value + 'px'
        this.updateComponentStyles()
      }
    },
    updateButtonSize(size) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'button') {
        component.size = size
        this.updateComponent()
      }
    },

    updateButtonAlignment(alignment) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'button') {
        component.alignment = alignment
        this.updateComponent()
      }
    },

    updateButtonMarginTop(value) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'button') {
        component.styles.marginTop = value + 'px'
        this.updateComponentStyles()
      }
    },

    updateButtonMarginBottom(value) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'button') {
        component.styles.marginBottom = value + 'px'
        this.updateComponentStyles()
      }
    },
    // Footer component methods
    setFooterSourceType(type) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'footer') {
        this.$set(component, 'sourceType', type)
        this.updateComponent()
      }
    },

    getCurrentFooterImageData() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'footer') {
        return component.imageEditorData || {
          url: component.uploadedUrl || component.logoSrc,
          description: component.logoAlt || '',
          scale: 100,
          background_color: '#e8e7ec',
          object_fit: 'cover',
          x: 0,
          y: 0,
          image_id: component.imageMetadata?.imageId || ''
        }
      }
      return null
    },

    handleFooterImageUpload(imageData) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'footer') {
        component.uploadedUrl = imageData.url
        component.sourceType = 'upload'

        component.imageEditorData = {
          url: imageData.url,
          description: imageData.description || '',
          scale: imageData.scale || 100,
          background_color: imageData.background_color || '#e8e7ec',
          object_fit: imageData.object_fit || 'cover',
          x: imageData.x || 0,
          y: imageData.y || 0,
          image_id: imageData.image_id || ''
        }

        if (imageData.description) {
          component.logoAlt = imageData.description
        }

        component.imageMetadata = {
          uploadedAt: new Date().toISOString(),
          originalFile: imageData.file || null,
          imageId: imageData.image_id || null
        }

        this.updateComponent()

        this.$notify({
          title: 'Logo Uploaded',
          message: 'Your footer logo has been uploaded successfully',
          type: 'success',
          duration: 3000
        })
      }
    },

    handleFooterImageUpdate(imageData) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'footer') {
        if (imageData.url) {
          component.uploadedUrl = imageData.url
        }

        component.imageEditorData = {
          url: imageData.url,
          description: imageData.description || '',
          scale: imageData.scale || 100,
          background_color: imageData.background_color || '#e8e7ec',
          object_fit: imageData.object_fit || 'cover',
          x: imageData.x || 0,
          y: imageData.y || 0,
          image_id: imageData.image_id || component.imageEditorData?.image_id || ''
        }

        if (imageData.description) {
          component.logoAlt = imageData.description
        }

        if (!component.imageMetadata) {
          component.imageMetadata = {}
        }
        component.imageMetadata.lastUpdated = new Date().toISOString()
        if (imageData.image_id) {
          component.imageMetadata.imageId = imageData.image_id
        }

        this.updateComponent()

        this.$notify({
          title: 'Logo Updated',
          message: 'Your footer logo has been updated successfully',
          type: 'success',
          duration: 3000
        })
      }
    },

    clearFooterUploadedImage() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'footer') {
        component.uploadedUrl = null
        component.logoSrc = ''
        component.imageMetadata = null
        component.imageEditorData = null
        component.sourceType = 'url'
        this.updateComponent()

        this.$notify({
          title: 'Logo Removed',
          message: 'The footer logo has been removed',
          type: 'info',
          duration: 2000
        })
      }
    },

// Enhanced getSocialIcon method with new platforms
    getSocialIcon(platform) {
      const icons = {
        facebook: 'fab fa-facebook-f',
        x: 'x-twitter-custom',
        instagram: 'fab fa-instagram',
        linkedin: 'fab fa-linkedin-in',
        youtube: 'fab fa-youtube',
        tripadvisor: 'fab fa-tripadvisor',
        yelp: 'fab fa-yelp',
        morty: 'tim-icons icon-app', // Using generic app icon for Morty
        website: 'tim-icons icon-world'
      }
      return icons[platform] || 'tim-icons icon-link-72'
    },

// Enhanced formatPlatformName with new platforms
    formatPlatformName(platform) {
      const names = {
        facebook: 'Facebook',
        x: 'X',
        instagram: 'Instagram',
        linkedin: 'LinkedIn',
        youtube: 'YouTube',
        tripadvisor: 'TripAdvisor',
        yelp: 'Yelp',
        morty: 'Morty',
        website: 'Website'
      }
      return names[platform] || platform.charAt(0).toUpperCase() + platform.slice(1)
    },

// Get social platform placeholder
    getSocialPlaceholder(platform) {
      const placeholders = {
        facebook: 'https://facebook.com/yourpage',
        x: 'https://x.com/yourhandle',
        instagram: 'https://instagram.com/yourprofile',
        linkedin: 'https://linkedin.com/company/yourcompany',
        youtube: 'https://youtube.com/yourchannel',
        tripadvisor: 'https://tripadvisor.com/yourbusiness',
        yelp: 'https://yelp.com/biz/yourbusiness',
        morty: 'https://morty.app/yourbusiness',
        website: 'https://yourwebsite.com'
      }
      return placeholders[platform] || `https://${platform}.com/yourpage`
    },

// Enhanced getSocialIconHTML for email rendering with new platforms
    getSocialIconHTML(platform) {
      const icons = {
        facebook: '📘',
        x: '𝕏',
        instagram: '📷',
        linkedin: '💼',
        youtube: '📺',
        tripadvisor: '🦉', // Owl emoji for TripAdvisor
        yelp: '⭐', // Star for Yelp
        morty: '🏠', // House for Morty (property management)
        website: '🌐'
      }
      return icons[platform] || '🔗'
    },
    updateFooterHeight(value) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'footer') {
        component.styles.height = value
        this.updateComponentStyles()
      }
    },

    updateFooterLogoWidth(value) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'footer') {
        component.styles.logoWidth = value
        this.updateComponentStyles()
      }
    },

    updateFooterPaddingVertical(value) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'footer') {
        component.styles.paddingTop = value
        component.styles.paddingBottom = value
        this.updateComponentStyles()
      }
    },

    updateFooterPaddingHorizontal(value) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'footer') {
        component.styles.paddingLeft = value
        component.styles.paddingRight = value
        this.updateComponentStyles()
      }
    },

    // Helper method for social media icons in HTML
    setHeaderAlignment(alignment) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'header') {
        component.styles.textAlign = alignment
        this.updateComponentStyles()
      }
    },

    updateHeaderPaddingVertical(value) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'header') {
        component.styles.paddingTop = value
        component.styles.paddingBottom = value
        this.updateComponentStyles()
      }
    },

    updateHeaderPaddingHorizontal(value) {
      const component = this.getSelectedComponent()
      if (component && component.type === 'header') {
        component.styles.paddingLeft = value
        component.styles.paddingRight = value
        this.updateComponentStyles()
      }
    },
    handleComponentClick(componentId, event) {
      // Stop propagation if we have a real DOM event
      if (event && event.stopPropagation && typeof event.stopPropagation === 'function') {
        event.stopPropagation();
      }
      this.selectComponent(componentId);
    },

    // Insert text into the active contenteditable editor on the canvas
    insertTextAtCursor(component, text) {
      const editorEl = document.querySelector('.text-component.selected .editor-content[contenteditable="true"]')

      if (editorEl) {
        editorEl.focus()

        const selection = window.getSelection()

        // Restore saved cursor position if available
        if (this.savedEditorRange && editorEl.contains(this.savedEditorRange.startContainer)) {
          selection.removeAllRanges()
          selection.addRange(this.savedEditorRange)
        }

        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0)
          if (editorEl.contains(range.commonAncestorContainer)) {
            range.deleteContents()
            const textNode = document.createTextNode(text)
            range.insertNode(textNode)
            // Move cursor after inserted text
            range.setStartAfter(textNode)
            range.setEndAfter(textNode)
            selection.removeAllRanges()
            selection.addRange(range)
            // Save new position for subsequent inserts
            this.savedEditorRange = range.cloneRange()
          } else {
            editorEl.appendChild(document.createTextNode(text))
          }
        } else {
          editorEl.appendChild(document.createTextNode(text))
        }

        // Sync DOM back to component data
        component.content = editorEl.innerHTML
        this.$forceUpdate()
      } else {
        // Fallback: append to data string
        if (!component.content) component.content = ''
        const separator = component.content.length > 0 && !component.content.endsWith(' ') ? ' ' : ''
        component.content = component.content + separator + text
        this.$forceUpdate()
      }
    },
    toggleSmartValuesPopover() {
      // Save cursor position before popover steals focus
      if (!this.showSmartValues) {
        const sel = window.getSelection()
        if (sel && sel.rangeCount > 0) {
          const editorEl = document.querySelector('.text-component.selected .editor-content[contenteditable="true"]')
          if (editorEl && editorEl.contains(sel.anchorNode)) {
            this.savedEditorRange = sel.getRangeAt(0).cloneRange()
          }
        }
      }
      this.showSmartValues = !this.showSmartValues
    },

    onSmartValueInsert(smartValue) {
      const selectedComponent = this.getSelectedComponent();

      if (selectedComponent && selectedComponent.type === 'text') {
        this.insertTextAtCursor(selectedComponent, smartValue.syntax);
      } else if (selectedComponent && selectedComponent.type === 'html') {
        // The HTML drawer has its own smart values panel that inserts at the
        // cursor; from the sidebar, point the user there instead of warning.
        this.$notify({
          title: 'Use the HTML editor',
          message: 'Open Edit HTML to insert smart values into this block',
          type: 'info',
          duration: 3000
        });
      } else {
        this.$notify({
          title: 'Select a text component',
          message: 'Please select a text component to insert the smart value',
          type: 'warning',
          duration: 3000
        });
      }
    },

    closeAiPrompt() {
      this.aiPanelOpen = false
      this.aiPromptDismissed = true
    },

    // The AI modifies the template the user created, never a new row. The
    // canvas is saved first (the AI works on the saved structure), the worker
    // appends generated sections server side, and the canvas reloads when the
    // row carries this request's generation marker.
    async onAiSubmit(prompt) {
      this.aiError = ''

      this.aiLoading = true
      const saved = await this.saveTemplate('draft', { quiet: true })
      if (!saved || !this.templateData.id) {
        this.aiLoading = false
        this.aiError = 'Could not save the template before generating. Fix any highlighted fields and try again.'
        return
      }
      if (this.$route.params.id !== String(this.templateData.id)) {
        this.$router.replace(`/mail/custom/template/${this.templateData.id}`)
      }

      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const generationRequestId = 'gen_' + Date.now() + '_' + Math.floor(Math.random() * 1e6)
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates/${this.templateData.id}/generate`
        await axios.post(url, { prompt, request_id: generationRequestId }, config)
        this.pollGeneration(generationRequestId, 0)
      } catch (err) {
        this.aiLoading = false
        let message = 'Something went wrong. Please try again.'
        const data = err?.response?.data
        if (data) {
          try { message = (typeof data === 'string' ? JSON.parse(data) : data).error || message } catch (e) { /* keep default */ }
        }
        if (err?.response?.status === 429) message = 'Daily AI limit reached. Please try again tomorrow.'
        this.aiError = message
      }
    },

    async pollGeneration(generationRequestId, attempt) {
      if (attempt >= 48) {
        this.aiLoading = false
        this.aiError = 'Generation is taking longer than expected. Reload the template in a minute.'
        return
      }
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates/${this.templateData.id}`
        const response = await axios.get(url, config)
        const marker = response.data?.template?.content_json?.generation

        if (marker && marker.request_id === generationRequestId) {
          if (marker.status === 'failed') {
            this.aiLoading = false
            this.aiError = marker.error || 'Generation failed. Please try again.'
            return
          }
          await this.loadExistingTemplate(this.templateData.id, { quiet: true })
          this.aiLoading = false
          // Keep the prompt open so the user can iterate on the result.
          this.aiPanelOpen = true
          const parts = []
          if (marker.replaced) parts.push('content replaced')
          if (marker.added > 0) parts.push(`${marker.added} ${marker.added === 1 ? 'section' : 'sections'} added`)
          if (marker.edited > 0) parts.push(`${marker.edited} ${marker.edited === 1 ? 'section' : 'sections'} updated`)
          if (marker.removed > 0) parts.push(`${marker.removed} ${marker.removed === 1 ? 'section' : 'sections'} removed`)
          if (marker.layout_updated) parts.push('layout updated')
          this.$notify({
            title: 'Content generated',
            message: parts.length ? parts.join(', ') : 'Template updated',
            type: 'success',
            duration: 4000
          })
          return
        }
      } catch (e) {
        // Transient poll failures are fine; keep going until the attempt cap.
      }
      this.aiPollTimer = setTimeout(() => this.pollGeneration(generationRequestId, attempt + 1), 2500)
    },

    onHtmlBlockUpdate(payload) {
      const component = this.getSelectedComponent()
      if (!component || component.type !== 'html') return
      // $set so Vue 2 reactivity picks the keys up on components loaded from
      // templates saved before this block type existed.
      this.$set(component, 'html', payload.html)
      this.$set(component, 'hoistedStyles', payload.hoistedStyles)
      this.updateComponentStyles()
    },

    getHtmlBlockSummary() {
      const component = this.getSelectedComponent()
      if (!component || component.type !== 'html') return ''
      const html = component.html || ''
      if (!html.trim()) return 'No HTML yet'
      const bytes = new Blob([html]).size
      const size = bytes < 1024 ? `${bytes} B` : `${Math.round(bytes / 1024)} KB`
      const issues = findUnsafeEmailHtml(html, component.hoistedStyles).length
      const warnings = scanEmailHtmlCompatibility(html, component.hoistedStyles).length
      if (issues > 0) return `${size}, ${issues} ${issues === 1 ? 'problem blocks saving' : 'problems block saving'}`
      if (warnings > 0) return `${size}, ${warnings} compatibility ${warnings === 1 ? 'warning' : 'warnings'}`
      return `${size}, no warnings`
    },

    updateHtmlBlockSidePadding(value) {
      const component = this.getSelectedComponent()
      if (!component || component.type !== 'html') return
      component.styles.paddingLeft = value
      component.styles.paddingRight = value
      this.updateComponentStyles()
    },


    getComputedFontStyles(component) {
      if (!component || component.type !== 'text') return {}

      const styles = component.styles || {}
      return {
        fontFamily: styles.fontFamily || 'Arial, sans-serif',
        fontSize: (styles.fontSize || '16') + 'px',
        fontWeight: styles.fontWeight || '400',
        lineHeight: styles.lineHeight || '1.5',
        color: styles.color || '#333333',
        textAlign: styles.textAlign || 'left',
        marginTop: (styles.marginTop || '16') + 'px',
        marginBottom: (styles.marginBottom || '16') + 'px'
      }
    },
    updateTopMargin(value) {
      const component = this.getSelectedComponent()
      if (component) {
        component.styles.marginTop = value
        this.updateComponentStyles()
      }
    },
    updateBottomMargin(value) {
      const component = this.getSelectedComponent()
      if (component) {
        component.styles.marginBottom = value
        this.updateComponentStyles()
      }
    },

    validateTestEmail() {
      this.testEmailErrors.email = null

      if (!this.testEmailData.email || !this.testEmailData.email.trim()) {
        this.testEmailErrors.email = 'Email address is required'
        return false
      }

      if (!this.isValidEmail(this.testEmailData.email)) {
        this.testEmailErrors.email = 'Please enter a valid email address'
        return false
      }

      return true
    },

    clearTestEmailError() {
      if (this.testEmailErrors.email) {
        this.testEmailErrors.email = null
      }
    },

    async sendTestEmailConfirm() {
      if (!this.validateTestEmail()) {
        return
      }

      this.testEmailData.loading = true

      try {
        await this.executeTestEmailSend(this.testEmailData.email)
        this.modals.testEmail = false
      } catch (error) {
        // Error handling is done in executeTestEmailSend
      } finally {
        this.testEmailData.loading = false
      }
    },

    async executeTestEmailSend(email) {
      try {
        // Validate template is saved
        // if (!this.templateData.id) {
        //   await this.saveTemplate('draft')
        // }

        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }

        // The editor's current content and layout go with the request so the
        // test reflects unsaved changes without saving them.
        const payload = {
          recipientEmail: email,
          content_json: JSON.stringify(this.generateTemplateJSON()),
          styles_json: JSON.stringify(this.templateData.layout)
        }

        // Call the test email API endpoint with template ID in path
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates/${this.templateData.id}/send-test`

        const response = await axios.post(url, payload, config)

        if (response.data.success) {
          this.showAlert({
            title: 'Test email sent',
            message: `Email sent to ${email}. Check your inbox and spam folder, it may take a few minutes to arrive.`,
            variant: 'success',
          })
        } else {
          throw new Error(response.data.message || 'Unknown error occurred')
        }

      } catch (error) {
        console.error('Failed to send test email:', error)

        let errorMessage = 'Failed to send test email'

        if (error.response) {
          const status = error.response.status
          const data = error.response.data

          switch (status) {
            case 400:
              errorMessage = data.error || 'Invalid request parameters'
              break
            case 401:
              errorMessage = 'Not authorized to send test emails'
              break
            case 404:
              errorMessage = 'Template not found. Please save the template first.'
              break
            case 500:
              errorMessage = data.message || 'Server error occurred'
              break
            default:
              errorMessage = data.error || data.message || 'Unknown error occurred'
          }
        } else if (error.request) {
          errorMessage = 'Unable to connect to the server'
        }

        this.showAlert({
          title: 'Test email failed',
          message: errorMessage,
          variant: 'danger',
        })
      }
    },
    async getAuthToken() {
      return new Promise(function(resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },

    goBack() {
      this.$router.push('/email-templates')
    },

    goBackToTemplates() {
      if (this.templateData.id) {
        // If editing existing template, just close modal
        this.modals.templateSettings = false
      } else {
        // If creating new template, go back to templates list
        this.$router.push('/mail/custom/landing')
      }
    },

    getTypeName(type) {
      const names = {
        transactional: 'Transactional',
        customer: 'Customer',
        marketing: 'Marketing',
        automation: 'Automation'
      }
      return names[type] || type
    },

    handleMenuAction(command) {
      switch (command) {
        case 'template-settings':
          this.modals.templateSettings = true
          break
        case 'template-availability':  // Add this case
          this.openTemplateAvailability()
          break
        case 'test-email':
          this.sendTestEmail()
          break
      }
    },
    openTemplateAvailability() {
      if (!this.templateData.id) {
        this.showAlert({
          title: 'Template not saved',
          message: 'Save the template before configuring availability settings.',
        })
        return
      }

      this.modals.templateAvailability = true
    },
    async saveTemplateAvailability() {
      this.templateAvailability.loading = true

      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }

        const payload = {
          availability_settings: {
            show_on_transaction_details: this.templateAvailability.showOnTransactionDetails
          }
        }

        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates/${this.templateData.id}/availability`

        const response = await axios.put(url, payload, config)

        if (response.data.success) {
          this.templateData.availabilitySettings = {
            showOnTransactionDetails: this.templateAvailability.showOnTransactionDetails
          }

          this.showAlert({
            title: 'Availability updated',
            message: 'Template availability settings have been updated.',
            variant: 'success',
          })

          this.modals.templateAvailability = false
        } else {
          throw new Error(response.data.message || 'Failed to update availability settings')
        }

      } catch (error) {
        console.error('Failed to update template availability:', error)

        let errorMessage = 'Failed to update template availability settings'

        if (error.response) {
          const status = error.response.status
          const data = error.response.data

          switch (status) {
            case 400:
              errorMessage = data.error || 'Invalid request parameters'
              break
            case 401:
              errorMessage = 'Not authorized to update this template'
              break
            case 404:
              errorMessage = 'Template not found'
              break
            case 500:
              errorMessage = 'Server error occurred'
              break
            default:
              errorMessage = data.error || data.message || 'Unknown error occurred'
          }
        } else if (error.request) {
          errorMessage = 'Unable to connect to the server'
        }

        this.showAlert({
          title: 'Update failed',
          message: errorMessage,
          variant: 'danger',
        })
      } finally {
        this.templateAvailability.loading = false
      }
    },
    // Fixed device view setter to prevent unwanted changes
    setDeviceView(view) {
      this.deviceView = view
    },
    getEmailContainerStyles() {
      const fullBleed = this.templateData.layout.mode === 'fullBleed'
      const baseStyles = fullBleed
        ? {
          maxWidth: '100%',
          width: '100%',
          backgroundColor: 'transparent'
        }
        : {
          maxWidth: this.templateData.layout.contentWidth + 'px',
          width: '100%',
          backgroundColor: this.templateData.layout.containerBackground || '#ffffff'
        }
      if (this.deviceView === 'mobile') {
        baseStyles.maxWidth = '375px'
      }
      return baseStyles
    },
    onComponentDragStart(event, component) {
      if (this.attachmentComponents.some(att => att.type === component.type)) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.setData('application/json', JSON.stringify(component))
      event.dataTransfer.effectAllowed = 'copy'
    },

    onCanvasDrop(event) {
      this.onSectionDrop(event, 'body')
    },

    onSectionDrop(event, section) {
      event.preventDefault()
      event.stopPropagation()

      // Remove drag over styling
      event.target.classList.remove('drag-over')

      try {
        const componentData = JSON.parse(event.dataTransfer.getData('application/json'))

        if (componentData.type === 'header') {
          this.addHeaderComponent(componentData)
        } else if (componentData.type === 'footer') {
          this.addFooterComponent(componentData)
        } else {
          this.addComponentToSection(componentData, section)
        }
      } catch (error) {
        console.error('Error parsing dropped component:', error)
      }
    },

    onDragEnter(event) {
      event.preventDefault()
      event.target.classList.add('drag-over')
    },

    onDragLeave(event) {
      event.preventDefault()
      event.target.classList.remove('drag-over')
    },


    addComponent(componentTemplate) {
      // The canvas reloads from the saved row when generation completes;
      // additions made meanwhile would be silently lost.
      if (this.aiLoading) return

      if (this.attachmentComponents.some(att => att.type === componentTemplate.type)) {
        this.toggleAttachment(componentTemplate.type);
        return;
      }

      if (componentTemplate.type === 'header') {
        this.addHeaderComponent(componentTemplate)
      } else if (componentTemplate.type === 'footer') {
        this.addFooterComponent(componentTemplate)
      } else {
        this.addComponentToSection(componentTemplate, 'body')
      }
    },
    addFooterComponent(componentTemplate) {
      if(this.templateComponents.footer.length > 0){
        this.selectedComponent = this.templateComponents.footer[0].id
        return
      }

      const newComponent = {
        id: 'comp_' + Date.now(),
        type: componentTemplate.type,
        ...componentTemplate.defaultData
      }
      if ('language' in newComponent) {
        newComponent.language = this.defaultComponentLanguage()
      }

      this.templateComponents.footer.push(newComponent)
      this.selectedComponent = newComponent.id
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },
    addHeaderComponent(componentTemplate) {
      if(this.templateComponents.header.length > 0){
        this.selectedComponent = this.templateComponents.header[0].id
        return
      }
      const newComponent = {
        id: 'comp_' + Date.now(),
        type: componentTemplate.type,
        ...componentTemplate.defaultData
      }
      if ('language' in newComponent) {
        newComponent.language = this.defaultComponentLanguage()
      }

      // Always add headers to the beginning of the header section
      this.templateComponents.header.unshift(newComponent)
      this.selectedComponent = newComponent.id
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },

    addRow() {
      const newRow = {
        id: 'row_' + Date.now(),
        type: 'row',
        layout: this.layoutOptions[0], // Default to single column
        columns: [[]] // Start with one empty column
      }

      this.templateComponents.body.push(newRow)
      this.selectedComponent = newRow.id
      // Ensure device view stays unchanged
      this.$nextTick(() => {
        // Force update without changing device view
        this.$forceUpdate()
      })
    },

    defaultComponentLanguage() {
      const map = { en: 'english', fr: 'french', es: 'spanish', sv: 'swedish', el: 'greek', da: 'danish' }
      try {
        const group = JSON.parse(localStorage.getItem('group'))
        return map[group && group.default_locale] || 'english'
      } catch (e) {
        return 'english'
      }
    },
    addComponentToSection(componentTemplate, section) {
      if (componentTemplate.type === 'header') {
        this.addHeaderComponent(componentTemplate)
        return
      }

      const newComponent = {
        id: 'comp_' + Date.now(),
        type: componentTemplate.type,
        ...componentTemplate.defaultData
      }
      if ('language' in newComponent) {
        newComponent.language = this.defaultComponentLanguage()
      }

      this.templateComponents[section].push(newComponent)
      this.selectedComponent = newComponent.id
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },


    onColumnDrop(event, rowId, columnIndex) {
      event.preventDefault()
      event.stopPropagation()

      try {
        const componentData = JSON.parse(event.dataTransfer.getData('application/json'))

        // Don't allow headers or footers to be dropped into columns
        if (componentData.type === 'header' || componentData.type === 'footer') {
          this.$notify({
            title: 'Invalid drop',
            message: `${componentData.type === 'header' ? 'Headers' : 'Footers'} must be placed at the top level, not inside columns`,
            type: 'warning',
            duration: 3000
          })
          return
        }

        const newComponent = {
          id: 'comp_' + Date.now(),
          type: componentData.type,
          ...componentData.defaultData
        }
        if ('language' in newComponent) {
          newComponent.language = this.defaultComponentLanguage()
        }

        // Find the row and add component to specific column
        const row = this.findRowById(rowId)
        if (row) {
          row.columns[columnIndex].push(newComponent)
          this.selectedComponent = newComponent.id
          this.$forceUpdate()
        }
      } catch (error) {
        console.error('Error parsing dropped component:', error)
      }
    },

    findRowById(rowId) {
      for (const section of ['header', 'body', 'footer']) {
        const item = this.templateComponents[section].find(item => item.id === rowId)
        if (item && item.type === 'row') return item
      }
      return null
    },

    updateRowLayout(layout) {
      const selectedRow = this.getSelectedItem()
      if (selectedRow && selectedRow.type === 'row') {
        selectedRow.layout = layout

        // Adjust columns array to match new layout
        const currentColumns = selectedRow.columns
        const newColumns = []

        for (let i = 0; i < layout.columns; i++) {
          newColumns.push(currentColumns[i] || [])
        }

        // If reducing columns, merge extra components into last column
        if (currentColumns.length > layout.columns) {
          for (let i = layout.columns; i < currentColumns.length; i++) {
            newColumns[layout.columns - 1].push(...currentColumns[i])
          }
        }

        selectedRow.columns = newColumns
        this.$forceUpdate()
      }
    },

    deleteRow(section, index) {
      this.templateComponents[section].splice(index, 1)
      this.selectedComponent = null
    },

    deleteFromColumn(rowId, columnIndex, componentIndex) {
      const row = this.findRowById(rowId)
      if (row) {
        row.columns[columnIndex].splice(componentIndex, 1)
        this.selectedComponent = null
        this.$forceUpdate()
      }
    },

    getSelectedItem() {
      if (!this.selectedComponent) return null

      for (const section of ['header', 'body', 'footer']) {
        // Check for direct components
        const component = this.templateComponents[section].find(c => c.id === this.selectedComponent)
        if (component) return component

        // Check for components within rows
        for (const item of this.templateComponents[section]) {
          if (item.type === 'row') {
            if (item.id === this.selectedComponent) return item
            for (const column of item.columns) {
              const nestedComponent = column.find(c => c.id === this.selectedComponent)
              if (nestedComponent) return nestedComponent
            }
          }
        }
      }
      return null
    },

    // Fixed selectComponent method to prevent device view changes
    selectComponent(componentIdOrData) {
      // Handle both component ID string and component data object
      let componentId;

      if (typeof componentIdOrData === 'string') {
        // Direct component ID
        componentId = componentIdOrData;
      } else if (componentIdOrData && componentIdOrData.id) {
        // Component data object
        componentId = componentIdOrData.id;
      } else {
        // Fallback - might be an event object, ignore
        console.warn('Invalid component data passed to selectComponent:', componentIdOrData);
        return;
      }

      this.selectedComponent = componentId;

      // Add image-specific initialization without breaking other components
      const component = this.getSelectedComponent();
      if (component && component.type === 'image') {
        // Initialize image-specific properties if they don't exist
        if (!component.sourceType) {
          component.sourceType = component.uploadedUrl ? 'upload' : 'url';
        }

        if (!component.styles) {
          component.styles = {
            width: '300px',
            height: 'auto',
            marginTop: '16px',
            marginBottom: '16px',
            borderRadius: '0px',
            textAlign: 'center'
          };
        }

        // Ensure alignment is set and synced with styles
        if (!component.alignment) {
          component.alignment = component.styles?.textAlign || 'center';
        }

        // Sync alignment with styles.textAlign
        if (component.styles) {
          component.styles.textAlign = component.alignment;
        }
      }
    },

    getSelectedComponent() {
      return this.getSelectedItem()
    },

    deleteComponent(section, index) {
      this.templateComponents[section].splice(index, 1)
      this.selectedComponent = null
    },

    deleteSelectedComponent() {
      if (!this.selectedComponent) return

      for (const section of ['header', 'body', 'footer']) {
        const index = this.templateComponents[section].findIndex(c => c.id === this.selectedComponent)
        if (index >= 0) {
          this.templateComponents[section].splice(index, 1)
          this.selectedComponent = null
          break
        }
      }
    },

    editComponent(component) {
      this.selectedComponent = component.id
    },

    getComponentType(type) {
      const typeMap = {
        text: 'TextComponent',
        button: 'ButtonComponent',
        image: 'ImageComponent',
        billing: 'BillingInfoComponent',
        header: 'HeaderComponent',
        footer: 'FooterComponent',
        lineItems: 'LineItemsComponent',
        paymentSummary: 'PaymentSummaryComponent',
        appliedPayments: 'AppliedPaymentsComponent',
        customFields: 'CustomerResponsesComponent',
        divider: 'DividerComponent',
        game: 'GameComponent',
        gameMaster: 'GameMasterComponent',
        giftCard: 'GiftCardComponent',
        projectTicket: 'ProjectTicketComponent',
        changeLog: 'ChangeLogComponent',
        html: 'HtmlComponent',
      }
      return typeMap[type] || 'TextComponent'
    },
    setChangeLogBackgroundInherit() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'changeLog') {
        component.styles.backgroundColor = 'inherit'
        this.updateComponentStyles()
      }
    },
    generateGiftCardHTML(component) {
      const styles = component.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      const containerStyles = `
    background-color: ${backgroundColor};
    padding: ${styles.paddingTop || '20'}px ${styles.paddingRight || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingLeft || '20'}px;
    margin: ${styles.marginTop || '16'}px 0 ${styles.marginBottom || '16'}px 0;
    border-radius: ${styles.borderRadius || '8'}px;
    font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    ${styles.showBorder ? `border: 1px solid ${styles.borderColor || '#e5e7eb'};` : ''}
  `

      const layout = component.layout || 'image-top'
      let html = `<div style="${containerStyles}">`

      // Wrapper with layout
      let wrapperStyles = ''
      if (layout === 'image-top') {
        wrapperStyles = 'display: block;'
      } else {
        wrapperStyles = `display: flex; flex-direction: ${layout === 'image-left' ? 'row' : 'row-reverse'}; gap: 20px; align-items: flex-start;`
      }

      html += `<div style="${wrapperStyles}">`

      // Image (if enabled and in side layout)
      if (component.showImage && (layout === 'image-left' || layout === 'image-right')) {
        const imageStyles = `
      flex-shrink: 0;
      width: ${styles.imageWidth || '40'}%;
      min-width: 150px;
    `
        html += `<div style="${imageStyles}">`
        html += `<img src="{{gift_card.design_url}}" alt="Gift Card Design" style="width: 100%; border-radius: ${styles.imageBorderRadius || '8'}px; display: block;" />`
        html += '</div>'
      }

      // Content
      html += '<div style="flex: 1; min-width: 0;">'

      // Image (if enabled and top layout)
      if (component.showImage && layout === 'image-top') {
        const imageAlign = component.imageAlignment || 'center'
        html += `<div style="text-align: ${imageAlign}; margin-bottom: 16px;">`
        html += `<img src="{{gift_card.design_url}}" alt="Gift Card Design" style="width: ${styles.imageWidth || '100'}%; max-width: 100%; border-radius: ${styles.imageBorderRadius || '8'}px; display: block; ${imageAlign === 'center' ? 'margin: 0 auto;' : ''}" />`
        html += '</div>'
      }

      // Gift Card Code Section (always shown)
      const codeSectionStyles = `
    text-align: center;
    padding: ${styles.codePadding || '16'}px;
    background-color: ${styles.codeBackgroundColor || 'rgba(225, 78, 202, 0.1)'};
    border-radius: ${styles.codeBorderRadius || '8'}px;
    margin-bottom: 16px;
    ${styles.showCodeBorder ? `border: 2px solid ${styles.codeBorderColor || '#e14eca'};` : ''}
  `

      const labels = {
        english: { giftCardCode: 'Gift Card Code' },
        spanish: { giftCardCode: 'Código de Tarjeta de Regalo' },
        french: { giftCardCode: 'Code de Carte Cadeau' },
        swedish: { giftCardCode: 'Presentkortskod' },
        danish: { giftCardCode: 'Gavekortkode' },
        greek: { giftCardCode: 'Κωδικός Δωροκάρτας' }
      }
      const currentLabels = labels[component.language || 'english'] || labels.english

      html += `<div style="${codeSectionStyles}">`
      html += `<div style="font-size: ${styles.codeLabelFontSize || '12'}px; font-weight: ${styles.codeLabelFontWeight || '600'}; color: ${styles.codeLabelColor || '#6b7280'}; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">${currentLabels.giftCardCode}</div>`
      html += `<div style="font-size: ${styles.codeFontSize || '24'}px; font-weight: ${styles.codeFontWeight || '700'}; color: ${styles.codeColor || '#e14eca'}; font-family: ${styles.codeFont || 'Monaco, Courier, monospace'}; letter-spacing: 2px;">{{gift_card.code}}</div>`
      html += '</div>'

      // Personal Message (if enabled)
      if (component.showPersonalMessage) {
        const messageSectionStyles = `
      padding: ${styles.messagePadding || '12'}px;
      background-color: ${styles.messageBackgroundColor || 'rgba(249, 250, 251, 0.5)'};
      border-radius: ${styles.messageBorderRadius || '6'}px;
      margin-bottom: 16px;
      border-left: 3px solid ${styles.messageBorderColor || '#e14eca'};
    `

        const messageLabels = {
          english: 'Personal Message',
          spanish: 'Mensaje Personal',
          french: 'Message Personnel',
          swedish: 'Personligt Meddelande',
          danish: 'Personlig besked',
          greek: 'Προσωπικό Μήνυμα'
        }
        const messageLabel = messageLabels[component.language || 'english'] || messageLabels.english

        html += `<div style="${messageSectionStyles}">`
        html += `<div style="font-size: ${styles.messageLabelFontSize || '11'}px; font-weight: ${styles.messageLabelFontWeight || '600'}; color: ${styles.messageLabelColor || '#6b7280'}; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">${messageLabel}</div>`
        html += `<div style="font-size: ${styles.messageFontSize || '14'}px; font-weight: ${styles.messageFontWeight || '400'}; color: ${styles.messageColor || '#374151'}; line-height: 1.6; font-family: ${styles.fontFamily || 'Arial, sans-serif'}; font-style: italic;">{{gift_card.personal_message}}</div>`
        html += '</div>'
      }

      // Details Section
      const hasDetails = component.showTotal || component.showRemaining || component.showActivationDate || component.showExpirationDate

      if (hasDetails) {
        const detailsContainerStyles = `
      display: grid;
      grid-template-columns: ${styles.detailsLayout === 'vertical' ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))'};
      gap: 12px;
      margin-top: 8px;
    `

        html += `<div style="${detailsContainerStyles}">`

        const detailItemStyles = `
      padding: ${styles.detailPadding || '12'}px;
      background-color: ${styles.detailBackgroundColor || 'transparent'};
      border-radius: ${styles.detailBorderRadius || '6'}px;
      ${styles.showDetailBorder ? `border: 1px solid ${styles.detailBorderColor || '#e5e7eb'};` : ''}
    `

        const detailLabels = {
          english: { total: 'Total Value', remaining: 'Remaining Value', activation: 'Activation Date', expiration: 'Expiration Date' },
          spanish: { total: 'Valor Total', remaining: 'Valor Restante', activation: 'Fecha de Activación', expiration: 'Fecha de Expiración' },
          french: { total: 'Valeur Totale', remaining: 'Valeur Restante', activation: "Date d'Activation", expiration: "Date d'Expiration" },
          swedish: { total: 'Totalt Värde', remaining: 'Återstående Värde', activation: 'Aktiveringsdatum', expiration: 'Utgångsdatum' },
          danish: { total: 'Samlet værdi', remaining: 'Resterende værdi', activation: 'Aktiveringsdato', expiration: 'Udløbsdato' },
          greek: { total: 'Συνολική Αξία', remaining: 'Υπόλοιπο', activation: 'Ημερομηνία Ενεργοποίησης', expiration: 'Ημερομηνία Λήξης' }
        }
        const detailCurrentLabels = detailLabels[component.language || 'english'] || detailLabels.english

        // Total Value
        if (component.showTotal) {
          html += `<div style="${detailItemStyles}">`
          html += `<div style="font-size: ${styles.labelFontSize || '11'}px; font-weight: ${styles.labelFontWeight || '600'}; color: ${styles.labelColor || '#9ca3af'}; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">${detailCurrentLabels.total}</div>`
          html += `<div style="font-size: ${styles.valueFontSize || '16'}px; font-weight: ${styles.valueFontWeight || '600'}; color: ${styles.valueColor || '#111827'}; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">{{gift_card.total}}</div>`
          html += '</div>'
        }

        // Remaining Value
        if (component.showRemaining) {
          html += `<div style="${detailItemStyles}">`
          html += `<div style="font-size: ${styles.labelFontSize || '11'}px; font-weight: ${styles.labelFontWeight || '600'}; color: ${styles.labelColor || '#9ca3af'}; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">${detailCurrentLabels.remaining}</div>`
          html += `<div style="font-size: ${styles.valueFontSize || '16'}px; font-weight: ${styles.valueFontWeight || '600'}; color: ${styles.valueColor || '#111827'}; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">{{gift_card.remaining}}</div>`
          html += '</div>'
        }

        // Activation Date
        if (component.showActivationDate) {
          html += `<div style="${detailItemStyles}">`
          html += `<div style="font-size: ${styles.labelFontSize || '11'}px; font-weight: ${styles.labelFontWeight || '600'}; color: ${styles.labelColor || '#9ca3af'}; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">${detailCurrentLabels.activation}</div>`
          html += `<div style="font-size: ${styles.valueFontSize || '16'}px; font-weight: ${styles.valueFontWeight || '600'}; color: ${styles.valueColor || '#111827'}; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">{{gift_card.activation_date}}</div>`
          html += '</div>'
        }

        // Expiration Date
        if (component.showExpirationDate) {
          html += `<div style="${detailItemStyles}">`
          html += `<div style="font-size: ${styles.labelFontSize || '11'}px; font-weight: ${styles.labelFontWeight || '600'}; color: ${styles.labelColor || '#9ca3af'}; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">${detailCurrentLabels.expiration}</div>`
          html += `<div style="font-size: ${styles.valueFontSize || '16'}px; font-weight: ${styles.valueFontWeight || '600'}; color: ${styles.valueColor || '#111827'}; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">{{gift_card.expiration_date}}</div>`
          html += '</div>'
        }

        html += '</div>' // Close details container
      }

      html += '</div>' // Close content
      html += '</div>' // Close wrapper
      html += '</div>' // Close container

      return html
    },
    setCustomerResponsesBackgroundInherit() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'customFields') {
        component.styles.backgroundColor = 'inherit'
        this.updateComponentStyles()
      }
    },
    // Component Properties
    updateComponent() {
      this.$forceUpdate()
    },

    updateComponentStyles() {
      this.$forceUpdate()
    },

    setAlignment(alignment) {
      const component = this.getSelectedComponent()
      if (component) {
        component.styles.textAlign = alignment
        this.$forceUpdate()
      }
    },

    updateMargin(value) {
      const component = this.getSelectedComponent()
      if (component) {
        component.styles.marginTop = value + 'px'
        component.styles.marginBottom = value + 'px'
        this.$forceUpdate()
      }
    },

    updateLayoutWidth() {
      this.$forceUpdate()
    },

    updateLayoutBackground() {
      this.$forceUpdate()
    },

    // Client-side validation methods
    isValidEmail(email) {
      return validateEmail(email)
    },

    validateField(fieldName) {
      this.$set(this.validationErrors, fieldName, null) // Clear previous error

      const value = this.templateData[fieldName]

      switch (fieldName) {
        case 'name':
          if (!value || !value.trim()) {
            this.$set(this.validationErrors, 'name', 'Template name is required')
          } else if (value.length > 255) {
            this.$set(this.validationErrors, 'name', 'Template name must be 255 characters or less')
          }
          break

        case 'template_type':
          if (!value || !value.id) {
            this.$set(this.validationErrors, 'template_type', 'Template type is required')
          }
          break

        case 'subject_line':
          if (!value || !value.trim()) {
            this.$set(this.validationErrors, 'subject_line', 'Subject line is required')
          } else if (value.length > 255) {
            this.$set(this.validationErrors, 'subject_line', 'Subject line must be 255 characters or less')
          }
          break

        case 'preview_text':
          if (!value || !value.trim()) {
            this.$set(this.validationErrors, 'preview_text', 'Preview text is required')
          } else if (value.length > 255) {
            this.$set(this.validationErrors, 'preview_text', 'Preview text must be 255 characters or less')
          }
          break

        case 'from_name':
          if (!value || !value.trim()) {
            this.$set(this.validationErrors, 'from_name', 'From name is required')
          } else if (value.length > 255) {
            this.$set(this.validationErrors, 'from_name', 'From name must be 255 characters or less')
          }
          break

        case 'from_email':
          if (!value || !value.trim()) {
            this.$set(this.validationErrors, 'from_email', 'From email is required')
          } else if (!this.isValidEmail(value)) {
            this.$set(this.validationErrors, 'from_email', 'Please enter a valid email address')
          } else if (value.length > 255) {
            this.$set(this.validationErrors, 'from_email', 'From email must be 255 characters or less')
          } else if (this.isReservedDomainEmail(value)) {
            // Reserved for platform system mail, so it can never be stored as a
            // template sender, not even on a draft.
            this.$set(this.validationErrors, 'from_email', 'offthecouch.io is reserved for platform system emails')
          }
          break

        case 'reply_to_email':
          if (!value || !value.trim()) {
            this.$set(this.validationErrors, 'reply_to_email', 'Reply to email is required')
          } else if (!this.isValidEmail(value)) {
            this.$set(this.validationErrors, 'reply_to_email', 'Please enter a valid email address')
          } else if (value.length > 255) {
            this.$set(this.validationErrors, 'reply_to_email', 'Reply to email must be 255 characters or less')
          }
          break
      }
    },

    clearFieldError(fieldName) {
      if (this.validationErrors[fieldName]) {
        this.$set(this.validationErrors, fieldName, null)
      }
    },

    validateAllFields() {
      const fields = ['name', 'template_type', 'subject_line', 'preview_text', 'from_name', 'from_email', 'reply_to_email']
      fields.forEach(field => this.validateField(field))
      return Object.keys(this.validationErrors).filter(key => this.validationErrors[key]).length === 0
    },
    handleSaveError(error) {
      console.error('Error saving template:', error)

      let title = 'Error!'
      let message = 'Failed to save template.'

      if (error.response) {
        const status = error.response.status
        const data = error.response.data

        switch (status) {
          case 400:
            title = 'Validation error'
            if (data.errors && Array.isArray(data.errors)) {
              message = `Please fix the following:\n${data.errors.map(err => `• ${err}`).join('\n')}`
            } else if (data.error) {
              message = data.error
            } else {
              message = 'Please check your input and try again.'
            }
            break

          case 401:
            title = 'Not authorized'
            message = 'You do not have permission to save this template.'
            break

          case 404:
            title = 'Template not found'
            message = 'The template you are trying to update was not found.'
            break

          case 409:
            title = 'Name already exists'
            message = data.error || 'A template with this name already exists.'
            break

          case 500:
            title = 'Server error'
            message = 'An internal server error occurred. Please try again.'
            break

          default:
            if (data.error) {
              message = data.error
            }
        }
      } else if (error.request) {
        title = 'Connection error'
        message = 'Unable to connect to the server. Please check your connection.'
      }

      this.showAlert({ title, message, variant: 'danger' })
    },

    // Validation helper
    validateTemplateData() {
      const errors = []

      if (!this.templateData.name?.trim()) {
        errors.push('Template name is required')
      }

      if (!this.templateData.template_type) {
        errors.push('Template type is required')
      }

      if (!this.templateData.subject_line?.trim()) {
        errors.push('Subject line is required')
      }

      if (!this.templateData.preview_text?.trim()) {
        errors.push('Preview text is required')
      }

      if (!this.templateData.from_name?.trim()) {
        errors.push('From name is required')
      }

      if (!this.templateData.from_email?.trim()) {
        errors.push('From email is required')
      } else if (this.isReservedDomainEmail(this.templateData.from_email)) {
        errors.push('offthecouch.io is reserved for platform system emails')
      }

      if (!this.templateData.reply_to_email?.trim()) {
        errors.push('Reply to email is required')
      }

      return errors
    },

    async saveTemplateSettings() {
      if (this.isReservedFromDomain) {
        this.promptReservedFromEmailFix(() => this.saveTemplateSettings())
        return
      }
      // Validate all fields first
      if (!this.validateAllFields()) {
        this.showAlert({
          title: 'Validation error',
          message: 'Fix the highlighted errors before continuing.',
        })
        return
      }

      this.loading = true
      try {
        await this.saveTemplate('draft')
        this.modals.templateSettings = false
        this.showAlert({
          title: 'Template created',
          message: 'You can now start building your email.',
          variant: 'success',
        })
      } catch (err) {
        this.handleSaveError(err)
      } finally {
        this.loading = false
      }
    },

    // Updated saveTemplate method
    async saveTemplate(status = 'draft', options = {}) {

      if (!options.quiet) this.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }

        const templateData = {
          ...this.templateData,
          template_type: this.templateData.template_type?.id || this.templateData.template_type_id, // Handle object or direct ID
          status,
          content_json: JSON.stringify(this.generateTemplateJSON()),
          styles_json: JSON.stringify(this.templateData.layout)
        }

        let response
        if (this.templateData.id) {
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates/${this.templateData.id}`
          response = await axios.put(url, templateData, config)
          this.templateData.id = response.data.template.id
        } else {
          const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates`
          response = await axios.post(url, templateData, config)
          this.templateData.id = response.data.template.id
        }
        this.markTemplateSaved()

        if (!options.quiet) {
          this.showAlert({
            title: 'Template saved',
            message: `Template ${status === 'active' ? 'saved and activated' : 'saved as draft'}.`,
            variant: 'success',
          })
        }
        return true
      } catch (err) {
        this.handleSaveError(err)
        return false
      } finally {
        if (!options.quiet) this.loading = false
      }
    },

    saveEmailSettings() {
      // This method is no longer needed since we combined the modals
      // But keeping it for compatibility if called elsewhere
      this.saveTemplateSettings()
    },

    // Template Actions
    generateTemplateJSON() {
      return {
        version: "1.0",
        metadata: {
          name: this.templateData.name,
          description: this.templateData.description,
          template_type: this.templateData.template_type
        },
        components: [
          ...this.templateComponents.header.map((c, i) => ({ ...c, position: { section: 'header', order: i } })),
          ...this.templateComponents.body.map((c, i) => ({ ...c, position: { section: 'body', order: i } })),
          ...this.templateComponents.footer.map((c, i) => ({ ...c, position: { section: 'footer', order: i } })),

        ],
        layout: this.templateData.layout,
        settings: {
          subject_line: this.templateData.subject_line,
          from_name: this.templateData.from_name,
          from_email: this.templateData.from_email,
          reply_to_email: this.templateData.reply_to_email,
          preview_text: this.templateData.preview_text
        },
        attachments: this.selectedAttachments
      }
    },

    generateEmailHTML() {
      let html = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${this.templateData.subject_line}</title>
        </head>
        <body style="background-color: ${this.templateData.layout.backgroundColor}; margin: 0; padding: 20px;">
          <div style="max-width: ${this.templateData.layout.contentWidth}px; margin: 0 auto; background: white; padding: 20px;">
      `

      // Add header components
      this.templateComponents.header.forEach(component => {
        html += this.componentToHTML(component)
      })

      // Add body components
      this.templateComponents.body.forEach(component => {
        html += this.componentToHTML(component)
      })

      // Add footer components
      this.templateComponents.footer.forEach(component => {
        html += this.componentToHTML(component)
      })

      html += `
          </div>
        </body>
        </html>
      `

      return html
    },

    // Enhanced component to HTML conversion with proper font handling
    componentToHTML(component) {
      // Custom HTML blocks contribute their raw HTML. This output is never
      // rendered into the DOM; it feeds the spamScore computed and the template size
      // stat only. Without this branch a template made mostly of custom HTML
      // would report its size as near zero.
      if (component.type === 'html') {
        return component.html || ''
      }
      if (component.type === 'header') {
        const styles = component.styles || {}
        const imageSource = component.uploadedUrl || component.imageSrc || ''

        // Force full width and no margins for HTML output
        const headerStyles = {
          ...styles,
          width: '100%',
          margin: '0',
          padding: `${styles.paddingTop || '20'}px ${styles.paddingLeft || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingRight || '20'}px`,
          backgroundColor: styles.backgroundColor || '#1e3a8a',
          textAlign: styles.textAlign || 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: styles.textAlign === 'left' ? 'flex-start' :
            styles.textAlign === 'right' ? 'flex-end' : 'center',
          minHeight: '60px',
          boxSizing: 'border-box'
        }

        const headerStyleString = Object.entries(headerStyles)
          .map(([key, value]) => {
            if (key.includes('padding')) {
              return `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}px`
            }
            return `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`
          })
          .join('; ')

        let headerContent = ''
        if (imageSource) {
          const imageElement = `<img src="${imageSource}" alt="${component.imageAlt || 'Header Logo'}" style="max-width: ${styles.imageWidth || '200'}px; height: auto; display: block;">`

          if (component.imageLink) {
            headerContent = `<a href="${component.imageLink}" style="text-decoration: none;">${imageElement}</a>`
          } else {
            headerContent = imageElement
          }
        }

        return `<div style="${headerStyleString}">
        ${headerContent}
      </div>`
      }

      if (component.type === 'footer') {
        const styles = component.styles || {}
        const backgroundColor = styles.backgroundColor || '#f8f9fa'
        const padding = `${styles.paddingTop || '20'}px ${styles.paddingLeft || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingRight || '20'}px`
        const topBorder = styles.showTopBorder ? `border-top: 1px solid ${styles.borderColor || '#e5e7eb'};` : ''

        // Typography styles
        const fontFamily = styles.fontFamily || 'Arial, sans-serif'
        const fontSize = styles.fontSize || '13'
        const textColor = styles.textColor || '#666666'

        // Social icon styles
        const socialBgColor = styles.socialBackgroundColor || '#333333'
        const socialIconColor = styles.socialIconColor || '#ffffff'

        let footerContent = '<div style="display: flex; flex-direction: column; align-items: center; justify-content: center; width: 100%; max-width: 600px; margin: 0 auto; gap: 16px;">'

        // Logo section
        if (component.showLogo) {
          const logoSource = component.uploadedUrl || component.logoSrc
          if (logoSource) {
            footerContent += `
        <div style="flex-shrink: 0;">
          <img src="${logoSource}" alt="${component.logoAlt || 'Company Logo'}" style="max-width: ${styles.logoWidth || '120'}px; height: auto; max-height: 60px; display: block;">
        </div>`
          }
        }

        // Social links section
        if (component.showSocialLinks && component.socialLinks) {
          const activeSocialLinks = component.socialLinks.filter(link => link.url && link.url.trim())
          if (activeSocialLinks.length > 0) {
            footerContent += '<div style="display: flex; gap: 12px; flex-shrink: 0;">'
            activeSocialLinks.forEach(social => {
              footerContent += `
          <a href="${social.url}" style="display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; background: ${socialBgColor}; color: ${socialIconColor}; border-radius: 4px; text-decoration: none; font-size: 14px;" title="${this.formatPlatformName(social.platform)}">
            ${this.getSocialIconHTML(social.platform)}
          </a>`
            })
            footerContent += '</div>'
          }
        }

        // Address section
        if (component.addressDisplay && component.addressDisplay !== 'none') {
          let addressText = ''
          if (component.addressDisplay === 'company') {
            addressText = '{{company.address}}'
          } else if (component.addressDisplay === 'group') {
            addressText = '{{company_group.address}}'
          }

          if (addressText) {
            footerContent += `
        <div style="text-align: center; font-family: ${fontFamily}; font-size: ${fontSize}px; line-height: 1.4; color: ${textColor}; max-width: 500px;">
          ${addressText}
        </div>`
          }
        }

        footerContent += '</div>'

        return `<div style="width: 100%; margin: 0; ${topBorder} background-color: ${backgroundColor}; padding: ${padding}; display: flex; align-items: center; justify-content: center; box-sizing: border-box;">
              ${footerContent}
            </div>`
      }

      // Handle other component types with your existing logic
      if (component.type === 'row') {
        let rowHtml = '<table width="100%" cellpadding="0" cellspacing="0" border="0">'
        rowHtml += '<tr>'

        component.columns.forEach((column, index) => {
          const columnWidth = Math.floor(100 / component.columns.length)
          rowHtml += `<td width="${columnWidth}%" valign="top" style="padding: 10px;">`

          column.forEach(comp => {
            rowHtml += this.componentToHTML(comp)
          })

          rowHtml += '</td>'
        })

        rowHtml += '</tr></table>'
        return rowHtml
      }

      const styles = component.styles || {}

      switch (component.type) {
        case 'text':
          const fontStyles = this.getComputedFontStyles(component)
          const backgroundColor = component.styles.backgroundColor === 'inherit' || !component.styles.backgroundColor
            ? 'transparent'
            : component.styles.backgroundColor

          const allStyles = {
            ...fontStyles,
            backgroundColor: backgroundColor,
            borderRadius: backgroundColor !== 'transparent' ? '4px' : '0'
          }

          const styleString = Object.entries(allStyles)
            .filter(([key, value]) => value !== 'transparent' || key === 'backgroundColor')
            .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
            .join('; ')

          // Process content for email rendering:
          // 1. Replace empty <div></div> tags with <div><br></div> to preserve blank lines
          // 2. Replace <div> with only whitespace to also have <br>
          // 3. Convert any remaining newlines to <br> tags
          let textContent = (component.content || '')
            .replace(/<div>\s*<\/div>/gi, '<div><br></div>')
            .replace(/\n/g, '<br>')
          return `<div style="${styleString}">${textContent}</div>`

        case 'button':
          const buttonSize = component.size || 'medium'
          const buttonAlignment = component.alignment || 'center'
          const buttonSizeStyles = this.getButtonSizeStyles(buttonSize)

          const buttonStyles = {
            ...styles,
            ...buttonSizeStyles,
            display: 'inline-block',
            textDecoration: 'none',
            border: 'none',
            borderRadius: styles.borderRadius || '4px',
            fontWeight: '500',
            cursor: 'pointer'
          }

          const buttonStyleString = Object.entries(buttonStyles)
            .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
            .join('; ')

          return `<div style="text-align: ${buttonAlignment}; margin: ${styles.marginTop || '16px'} 0 ${styles.marginBottom || '16px'} 0;">
              <a href="${component.href || '#'}" style="${buttonStyleString}">${component.content || 'Button'}</a>
            </div>`
        case 'divider':
          const dividerStyles = component.styles || {}
          return `<hr style="border: none; border-top: ${dividerStyles.thickness || '1'}px solid ${dividerStyles.lineColor || '#e5e7eb'}; margin: ${dividerStyles.marginTop || '20'}px 0 ${dividerStyles.marginBottom || '20'}px 0;">`
        case 'image':
          const imgSrc = component.uploadedUrl || component.src || 'https://console.offthecouch.io/img/placeholder.jpg'
          const alignment = component.alignment || 'center'

          const imgStyles = Object.entries(component.styles || {})
            .filter(([key]) => key !== 'textAlign') // Remove textAlign from img styles
            .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
            .join('; ')

          let imageHtml = `<img src="${imgSrc}" alt="${component.alt || 'Image'}" style="${imgStyles}; max-width: 100%; height: auto;">`

          // Wrap in link if href is provided
          if (component.href) {
            imageHtml = `<a href="${component.href}" style="text-decoration: none;">${imageHtml}</a>`
          }

          // Apply alignment to the container, not the image itself
          return `<div style="text-align: ${alignment}; margin: ${component.styles?.marginTop || '16px'} 0 ${component.styles?.marginBottom || '16px'} 0;">
                  ${imageHtml}
                </div>`
        case 'divider':
          return `<hr style="border: none; border-top: ${styles.borderTop || '1px solid #e5e7eb'}; margin: ${styles.marginTop || '20px'} 0 ${styles.marginBottom || '20px'} 0;">`

        case 'spacer':
          return `<div style="height: ${styles.height || '20px'}; line-height: ${styles.height || '20px'}; font-size: 1px;">&nbsp;</div>`

        case 'lineItems':
          return this.generateLineItemsHTML(component)

        case 'paymentSummary':
          return this.generatePaymentSummaryHTML(component)

        case 'appliedPayments':
          return this.generateAppliedPaymentsHTML(component)

        case 'customFields':
          return this.generateCustomerResponsesHTML(component)
        case 'game':
          return this.generateGameHTML(component)
        case 'gameMaster':
          return this.generateGameMasterHTML(component)
        case 'giftCard':
          return this.generateGiftCardHTML(component)
        case 'billing':
          return this.generateBillingHTML(component)
        case 'projectTicket':
          return this.generateProjectTicketHTML(component)
        case 'changeLog':
          return this.generateChangeLogHTML(component)

        default:
          return ''
      }
    },
    generateChangeLogHTML(component) {
      const styles = component.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      const containerStyles = `
    background-color: ${backgroundColor};
    padding: ${styles.paddingTop || '20'}px ${styles.paddingRight || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingLeft || '20'}px;
    margin: ${styles.marginTop || '16'}px 0 ${styles.marginBottom || '16'}px 0;
    border-radius: ${styles.borderRadius || '8'}px;
    font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    ${styles.showBorder ? `border: 1px solid ${styles.borderColor || '#e5e7eb'};` : ''}
  `

      let html = `<div style="${containerStyles}">`

      // Header Section
      const headerStyles = `
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  `

      html += `<div style="${headerStyles}">`

      // Event Icon
      const iconStyles = `
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: ${styles.iconBackgroundColor || 'rgba(225, 78, 202, 0.1)'};
    color: ${styles.iconColor || '#e14eca'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
  `
      html += `<div style="${iconStyles}">🔄</div>`

      // Header Content
      html += `<div style="flex: 1; min-width: 0;">`

      // Timestamp
      const timestampStyles = `
    font-size: ${styles.timestampFontSize || '12'}px;
    font-weight: ${styles.timestampFontWeight || '500'};
    color: ${styles.timestampColor || '#6b7280'};
    margin-bottom: 4px;
    font-family: ${styles.fontFamily || 'Arial, sans-serif'};
  `
      html += `<div style="${timestampStyles}">{{event_log.timestamp}}</div>`

      // Description
      const descriptionStyles = `
    font-size: ${styles.descriptionFontSize || '16'}px;
    font-weight: ${styles.descriptionFontWeight || '600'};
    color: ${styles.descriptionColor || '#111827'};
    font-family: ${styles.fontFamily || 'Arial, sans-serif'};
  `
      html += `<div style="${descriptionStyles}">{{event_log.description}}</div>`

      html += '</div>' // Close header content
      html += '</div>' // Close header

      // User Info Section
      const userSectionStyles = `
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background-color: ${styles.userSectionBackgroundColor || 'rgba(249, 250, 251, 0.5)'};
    border-radius: 6px;
    margin-bottom: 12px;
  `

      html += `<div style="${userSectionStyles}">`

      // User Avatar (placeholder or actual image)
      const avatarStyles = `
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: #e5e7eb;
    overflow: hidden;
    flex-shrink: 0;
  `
      html += `<div style="${avatarStyles}">`
      html += `<img src="{{event_log.user_img}}" alt="{{event_log.user_name}}" style="width: 100%; height: 100%; object-fit: cover;" />`
      html += '</div>'

      // User Details
      html += `<div style="flex: 1; min-width: 0;">`

      const userNameStyles = `
    font-size: ${styles.userNameFontSize || '14'}px;
    font-weight: ${styles.userNameFontWeight || '600'};
    color: ${styles.userNameColor || '#111827'};
    margin-bottom: 2px;
    font-family: ${styles.fontFamily || 'Arial, sans-serif'};
  `
      html += `<div style="${userNameStyles}">{{event_log.user_name}}</div>`

      const userRoleStyles = `
    font-size: ${styles.userRoleFontSize || '12'}px;
    color: ${styles.userRoleColor || '#6b7280'};
    font-family: ${styles.fontFamily || 'Arial, sans-serif'};
  `
      html += `<div style="${userRoleStyles}">{{event_log.user_role}}</div>`

      html += '</div>' // Close user details
      html += '</div>' // Close user section

      // Changes Section (if enabled)
      if (component.showAdditionalInfo) {
        const changesSectionStyles = `
      padding: 12px;
      background-color: ${styles.changesSectionBackgroundColor || 'rgba(225, 78, 202, 0.05)'};
      border-radius: 6px;
      border-left: 3px solid ${styles.changesAccentColor || '#e14eca'};
      margin-bottom: 12px;
    `

        html += `<div style="${changesSectionStyles}">`

        const changesLabelStyles = `
      font-size: ${styles.changesLabelFontSize || '11'}px;
      font-weight: 600;
      color: ${styles.changesLabelColor || '#6b7280'};
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 6px;
      font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    `
        html += `<div style="${changesLabelStyles}">Changes made:</div>`

        const changesContentStyles = `
      font-size: ${styles.changesContentFontSize || '13'}px;
      color: ${styles.changesContentColor || '#374151'};
      line-height: 1.5;
      font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    `
        html += `<div style="${changesContentStyles}">{{event_log.additional_information}}</div>`

        html += '</div>' // Close changes section
      }

      // Booking Reference (if enabled)
      if (component.showBookingReference) {
        const bookingRefStyles = `
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: ${styles.bookingRefFontSize || '12'}px;
      color: ${styles.bookingRefColor || '#6b7280'};
      font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    `

        html += `<div style="${bookingRefStyles}">`
        html += `<span style="color: ${styles.bookingRefIconColor || '#9ca3af'}; font-size: 14px;">🔗</span>`
        html += `<span>Booking {{event_log.booking_reference}}</span>`
        html += '</div>'
      }

      html += '</div>' // Close container

      return html
    },
    generateProjectTicketHTML(component) {
      const styles = component.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      const containerStyles = `
    background-color: ${backgroundColor};
    padding: ${styles.paddingTop || '20'}px ${styles.paddingRight || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingLeft || '20'}px;
    margin: ${styles.marginTop || '16'}px 0 ${styles.marginBottom || '16'}px 0;
    border-radius: ${styles.borderRadius || '8'}px;
    font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    ${styles.showBorder ? `border: 1px solid ${styles.borderColor || '#e5e7eb'};` : ''}
  `

      let html = `<div style="${containerStyles}">`

      // Header with ticket number and assignee
      const headerStyles = `
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${styles.dividerColor || '#e5e7eb'};
  `

      html += `<div style="${headerStyles}">`

      // Ticket Number (always shown)
      const ticketNumberStyles = `
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: ${styles.ticketNumberFontSize || '14'}px;
    font-weight: ${styles.ticketNumberFontWeight || '600'};
    color: ${styles.ticketNumberColor || '#6b7280'};
    font-family: ${styles.fontFamily || 'Arial, sans-serif'};
  `
      html += `<div style="${ticketNumberStyles}">📋 {{project_ticket.full_number}}</div>`

      // Assignee (if enabled)
      if (component.showAssignee) {
        const assigneeStyles = `
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: ${styles.assigneeFontSize || '13'}px;
      color: ${styles.assigneeColor || '#6b7280'};
      font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    `
        html += `<div style="${assigneeStyles}">👤 {{project_ticket.assignee_name}}</div>`
      }

      html += '</div>' // Close header

      // Project Info (if enabled)
      if (component.showProjectName || component.showProjectKey) {
        const projectInfoStyles = `
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
    `
        html += `<div style="${projectInfoStyles}">`

        if (component.showProjectKey) {
          const projectKeyStyles = `
        font-size: ${styles.projectKeyFontSize || '12'}px;
        font-weight: ${styles.projectKeyFontWeight || '600'};
        color: ${styles.projectKeyColor || '#e14eca'};
        background-color: ${styles.projectKeyBackgroundColor || 'rgba(225, 78, 202, 0.1)'};
        padding: 2px 8px;
        border-radius: 4px;
        font-family: Monaco, Courier, monospace;
      `
          html += `<span style="${projectKeyStyles}">[{{project_ticket.project_key}}]</span>`
        }

        if (component.showProjectName) {
          const projectNameStyles = `
        font-size: ${styles.projectNameFontSize || '13'}px;
        font-weight: ${styles.projectNameFontWeight || '500'};
        color: ${styles.projectNameColor || '#4b5563'};
        font-family: ${styles.fontFamily || 'Arial, sans-serif'};
      `
          html += `<span style="${projectNameStyles}">{{project_ticket.project_name}}</span>`
        }

        html += '</div>'
      }

      // Title (if enabled)
      if (component.showTitle) {
        const titleStyles = `
      font-size: ${styles.titleFontSize || '20'}px;
      font-weight: ${styles.titleFontWeight || '700'};
      color: ${styles.titleColor || '#111827'};
      margin: 0 0 12px 0;
      line-height: 1.3;
      font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    `
        html += `<h3 style="${titleStyles}">{{project_ticket.title}}</h3>`
      }

      // Description (if enabled)
      if (component.showDescription) {
        const descriptionStyles = `
      font-size: ${styles.descriptionFontSize || '14'}px;
      color: ${styles.descriptionColor || '#4b5563'};
      line-height: 1.6;
      margin-bottom: 12px;
      font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    `
        html += `<div style="${descriptionStyles}">{{project_ticket.description}}</div>`
      }

      // Time Estimate (if enabled)
      if (component.showTimeEstimate) {
        const metaStyles = `
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: ${styles.metaFontSize || '13'}px;
      color: ${styles.metaColor || '#6b7280'};
      margin-bottom: 12px;
      font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    `

        const labels = {
          english: 'Time Estimate',
          spanish: 'Tiempo Estimado',
          french: 'Temps Estimé',
          swedish: 'Tidsuppskattning',
          danish: 'Tidsestimat',
          greek: 'Εκτιμώμενος Χρόνος'
        }
        const label = labels[component.language || 'english'] || labels.english

        html += `<div style="${metaStyles}">⏰ ${label}: {{project_ticket.time_estimate}}</div>`
      }

      // Latest Comment (if enabled)
      if (component.showLatestComment) {
        const commentContainerStyles = `
      background-color: ${styles.commentBackgroundColor || '#f9fafb'};
      border: 1px solid ${styles.commentBorderColor || '#e5e7eb'};
      border-radius: 6px;
      padding: 12px;
      margin-bottom: 16px;
    `

        const commentHeaderStyles = `
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: ${styles.commentHeaderFontSize || '12'}px;
      font-weight: 600;
      color: ${styles.commentHeaderColor || '#6b7280'};
      margin-bottom: 6px;
      font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    `

        const commentTextStyles = `
      font-size: ${styles.commentTextFontSize || '13'}px;
      color: ${styles.commentTextColor || '#4b5563'};
      line-height: 1.5;
      font-style: italic;
      font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    `

        const commentLabels = {
          english: 'Latest Comment',
          spanish: 'Último Comentario',
          french: 'Dernier Commentaire',
          swedish: 'Senaste Kommentar',
          danish: 'Seneste kommentar',
          greek: 'Τελευταίο Σχόλιο'
        }
        const commentLabel = commentLabels[component.language || 'english'] || commentLabels.english

        html += `<div style="${commentContainerStyles}">`
        html += `<div style="${commentHeaderStyles}">💬 ${commentLabel}</div>`
        html += `<div style="${commentTextStyles}">{{project_ticket.latest_comment}}</div>`
        html += '</div>'
      }

      // Button (if enabled)
      if (component.showButton) {
        const buttonAlignment = component.buttonAlignment || 'left'
        const buttonContainerStyles = `
      text-align: ${buttonAlignment};
      margin-top: 16px;
    `

        const size = component.buttonSize || 'medium'
        const sizeMap = {
          small: { padding: '8px 16px', fontSize: '14px' },
          medium: { padding: '12px 24px', fontSize: '16px' },
          large: { padding: '16px 32px', fontSize: '18px' }
        }
        const sizeStyles = sizeMap[size] || sizeMap.medium

        const buttonStyles = `
      display: inline-block;
      padding: ${sizeStyles.padding};
      font-size: ${sizeStyles.fontSize};
      font-weight: 500;
      color: ${styles.buttonTextColor || '#ffffff'};
      background-color: ${styles.buttonBackgroundColor || '#e14eca'};
      border: none;
      border-radius: ${styles.buttonBorderRadius || '6'}px;
      text-decoration: none;
      cursor: pointer;
      font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    `

        html += `<div style="${buttonContainerStyles}">`
        html += `<a href="{{project_ticket.url}}" style="${buttonStyles}">${component.buttonText || 'View Ticket'}</a>`
        html += '</div>'
      }

      html += '</div>' // Close container

      return html
    },
    generateGameHTML(component) {
      const styles = component.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      const containerStyles = `
    background-color: ${backgroundColor};
    padding: ${styles.paddingTop || '20'}px ${styles.paddingRight || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingLeft || '20'}px;
    margin: ${styles.marginTop || '16'}px 0 ${styles.marginBottom || '16'}px 0;
    border-radius: ${styles.borderRadius || '8'}px;
    font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    ${styles.showBorder ? `border: 1px solid ${styles.borderColor || '#e5e7eb'};` : ''}
  `

      let html = `<div style="${containerStyles}">`

      // Image
      if (component.showImage && (component.uploadedUrl || component.imageSrc)) {
        const imgSrc = component.uploadedUrl || component.imageSrc
        const imageAlign = component.imageAlignment || 'center'
        html += `
      <div style="text-align: ${imageAlign}; margin-bottom: 16px;">
        <img src="${imgSrc}" alt="${component.imageAlt || component.name || 'Game'}"
             style="max-width: ${styles.imageWidth || '100'}%; height: auto; border-radius: ${styles.imageBorderRadius || '8'}px; display: block; ${imageAlign === 'center' ? 'margin: 0 auto;' : ''}" />
      </div>`
      }

      // Name
      html += `<h3 style="font-size: ${styles.nameFontSize || '24'}px; font-weight: ${styles.nameFontWeight || '700'}; color: ${styles.nameColor || '#111827'}; margin: 0 0 12px 0; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">${component.name || 'Game Name'}</h3>`

      // Description
      if (component.showDescription && component.description) {
        html += `<p style="font-size: ${styles.descriptionFontSize || '14'}px; color: ${styles.descriptionColor || '#6b7280'}; line-height: 1.6; margin-bottom: 16px; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">${component.description}</p>`
      }

      // Details
      const hasDetails = (component.showDifficulty && component.difficultyLevel) ||
        (component.showDuration && component.durationMinutes) ||
        (component.showGroupSize && (component.minPlayers || component.maxPlayers))

      if (hasDetails) {
        html += `<div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; margin-top: 16px;">`

        // Difficulty
        if (component.showDifficulty && component.difficultyLevel) {
          html += `
        <div style="display: flex; align-items: flex-start; gap: 8px; padding: ${styles.detailPadding || '8'}px; background-color: ${styles.detailBackgroundColor || 'transparent'}; border-radius: ${styles.detailBorderRadius || '4'}px;">
          <span style="font-size: ${styles.iconSize || '18'}px; color: ${styles.iconColor || '#e14eca'};">⭐</span>
          <div>
            <div style="font-size: ${styles.labelFontSize || '11'}px; font-weight: ${styles.labelFontWeight || '600'}; color: ${styles.labelColor || '#9ca3af'}; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 2px; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">
              ${component.difficultyLabel || 'Difficulty'}
            </div>
            <div style="font-size: ${styles.valueFontSize || '14'}px; font-weight: ${styles.valueFontWeight || '500'}; color: ${styles.valueColor || '#111827'}; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">
              ${component.difficultyLevel}/5
            </div>
          </div>
        </div>`
        }

        // Duration
        if (component.showDuration && component.durationMinutes) {
          html += `
        <div style="display: flex; align-items: flex-start; gap: 8px; padding: ${styles.detailPadding || '8'}px; background-color: ${styles.detailBackgroundColor || 'transparent'}; border-radius: ${styles.detailBorderRadius || '4'}px;">
          <span style="font-size: ${styles.iconSize || '18'}px; color: ${styles.iconColor || '#e14eca'};">⏱️</span>
          <div>
            <div style="font-size: ${styles.labelFontSize || '11'}px; font-weight: ${styles.labelFontWeight || '600'}; color: ${styles.labelColor || '#9ca3af'}; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 2px; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">
              Duration
            </div>
            <div style="font-size: ${styles.valueFontSize || '14'}px; font-weight: ${styles.valueFontWeight || '500'}; color: ${styles.valueColor || '#111827'}; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">
              ${component.durationMinutes} min
            </div>
          </div>
        </div>`
        }

        // Group Size
        if (component.showGroupSize && (component.minPlayers || component.maxPlayers)) {
          let groupSizeText = ''
          if (component.minPlayers && component.maxPlayers) {
            groupSizeText = `${component.minPlayers}-${component.maxPlayers} players`
          } else if (component.minPlayers) {
            groupSizeText = `${component.minPlayers}+ players`
          } else if (component.maxPlayers) {
            groupSizeText = `Up to ${component.maxPlayers} players`
          }

          html += `
        <div style="display: flex; align-items: flex-start; gap: 8px; padding: ${styles.detailPadding || '8'}px; background-color: ${styles.detailBackgroundColor || 'transparent'}; border-radius: ${styles.detailBorderRadius || '4'}px;">
          <span style="font-size: ${styles.iconSize || '18'}px; color: ${styles.iconColor || '#e14eca'};">👥</span>
          <div>
            <div style="font-size: ${styles.labelFontSize || '11'}px; font-weight: ${styles.labelFontWeight || '600'}; color: ${styles.labelColor || '#9ca3af'}; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 2px; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">
              Group Size
            </div>
            <div style="font-size: ${styles.valueFontSize || '14'}px; font-weight: ${styles.valueFontWeight || '500'}; color: ${styles.valueColor || '#111827'}; font-family: ${styles.fontFamily || 'Arial, sans-serif'};">
              ${groupSizeText}
            </div>
          </div>
        </div>`
        }

        html += '</div>'
      }

      html += '</div>'
      return html
    },
    generateCustomerResponsesHTML(component) {
      const styles = component.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      const containerStyles = `
    background-color: ${backgroundColor};
    padding: ${styles.paddingTop || '20'}px ${styles.paddingRight || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingLeft || '20'}px;
    margin: ${styles.marginTop || '16'}px 0 ${styles.marginBottom || '16'}px 0;
    border-radius: ${styles.borderRadius || '8'}px;
    font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    ${styles.showBorder ? `border: 1px solid ${styles.borderColor || '#e5e7eb'};` : ''}
  `

      const responses = [
        { question: 'Do you have any dietary restrictions?', answer: 'Vegetarian, no nuts', type: 'text' },
        { question: 'Will you need wheelchair access?', answer: 'Yes', type: 'checkbox' },
        { question: 'Emergency contact phone number', answer: '+1 (555) 123-4567', type: 'phone' },
        { question: 'Additional comments or requests', answer: 'Please seat us near a window if possible', type: 'textarea' }
      ]

      let html = `<div style="${containerStyles}">`

      // Always show header
      html += `
    <div style="margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid ${styles.dividerColor || '#e5e7eb'};">
      <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #111827;">Customer Information</h3>
    </div>`

      responses.forEach((item, index) => {
        const isLast = index === responses.length - 1
        const itemStyles = `
      padding-top: ${index === 0 ? '0' : '12'}px;
      padding-bottom: 12px;
      ${!isLast ? `border-bottom: 1px solid ${styles.itemDividerColor || '#f3f4f6'};` : ''}
    `

        html += `<div style="${itemStyles}">`

        html += `
      <div style="font-size: ${styles.questionFontSize || '13'}px; font-weight: ${styles.questionFontWeight || '600'}; color: ${styles.questionColor || '#4b5563'}; margin-bottom: 4px;">
        ${item.question}
      </div>`

        let answerDisplay = item.answer
        if (item.type === 'checkbox') {
          answerDisplay = item.answer === 'Yes' ? '✓ Yes' : '✗ No'
        } else if (item.type === 'file') {
          answerDisplay = `📎 ${item.answer}`
        }

        html += `
      <div style="font-size: ${styles.answerFontSize || '14'}px; color: ${styles.answerColor || '#111827'}; line-height: 1.5;">
        ${answerDisplay}
      </div>`

        html += '</div>'
      })

      html += '</div>'
      return html
    },
    generateAppliedPaymentsHTML(component) {
      const styles = component.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      const containerStyles = `
    background-color: ${backgroundColor};
    padding: ${styles.paddingTop || '20'}px ${styles.paddingRight || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingLeft || '20'}px;
    margin: ${styles.marginTop || '16'}px 0 ${styles.marginBottom || '16'}px 0;
    border-radius: ${styles.borderRadius || '8'}px;
    font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    ${styles.showBorder ? `border: 1px solid ${styles.borderColor || '#e5e7eb'};` : ''}
  `

      let html = `<div style="${containerStyles}">`

      // Header
      html += `
    <div style="margin-bottom: 16px; padding-bottom: 8px; border-bottom: 1px solid ${styles.dividerColor || '#e5e7eb'};">
      <h3 style="margin: 0; font-size: ${styles.titleFontSize || '16'}px; font-weight: 600; color: ${styles.titleColor || '#111827'};">
        Applied Paym
        ents
      </h3>
    </div>`

      // Sample payment items (simplified without status)
      html += `
    <div style="margin-bottom: 12px; padding: 12px 0; border-bottom: 1px solid ${styles.itemDividerColor || '#f3f4f6'};">
      <div style="display: flex; justify-content: space-between; align-items: center;">
        <div>
          <div style="font-size: ${styles.fontSize || '14'}px; color: ${styles.textColor || '#333333'}; font-weight: 500;">
            💳 Visa ••••4242 - John Doe
          </div>
          <div style="font-size: ${styles.metaFontSize || '12'}px; color: ${styles.metaColor || '#666666'}; margin-top: 4px;">
            Jan 15, 2025
          </div>
        </div>
        <div style="font-size: ${styles.amountFontSize || '15'}px; font-weight: 600; color: ${styles.amountColor || '#111827'};">
          $100.00
        </div>
      </div>
    </div>`

      // Total if enabled
      if (component.showTotal) {
        html += `
      <div style="border-top: 1px solid ${styles.dividerColor || '#e5e7eb'}; margin-top: 16px; padding-top: 12px;">
        <div style="display: flex; justify-content: space-between; align-items: center; font-size: ${styles.totalFontSize || '16'}px; font-weight: 700; color: ${styles.totalColor || '#111827'};">
          <span>Total Applied</span>
          <span>$100.00</span>
        </div>
      </div>`
      }

      html += '</div>'
      return html
    },
    setPaymentSummaryBackgroundInherit() {
      const component = this.getSelectedComponent()
      if (component && component.type === 'paymentSummary') {
        component.styles.backgroundColor = 'inherit'
        this.updateComponentStyles()
      }
    },
    generatePaymentSummaryHTML(component) {
      const styles = component.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      const containerStyles = `
    background-color: ${backgroundColor};
    padding: ${styles.paddingTop || '20'}px ${styles.paddingRight || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingLeft || '20'}px;
    margin: ${styles.marginTop || '16'}px 0 ${styles.marginBottom || '16'}px 0;
    border-radius: ${styles.borderRadius || '8'}px;
    font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    ${styles.showBorder ? `border: 1px solid ${styles.borderColor || '#e5e7eb'};` : ''}
  `

      // Calculate values
      const subtotal = parseFloat(component.subtotal) || 174.00
      const discount = parseFloat(component.discount) || 0
      const fee = parseFloat(component.fee) || 8.70
      const tax = parseFloat(component.tax) || 0
      const total = subtotal - discount + fee + tax
      const amountPaid = parseFloat(component.amountPaid) || 0
      const amountDue = total - amountPaid

      let html = `<div style="${containerStyles}">`

      // Subtotal
      html += `
    <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: ${styles.fontSize || '14'}px; color: ${styles.textColor || '#666666'};">
      <span>Subtotal</span>
      <span style="font-weight: 500;">${component.currencySymbol || '$'}${subtotal.toFixed(2)}</span>
    </div>`

      // Discount
      if (component.showDiscounts && discount > 0) {
        html += `
      <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: ${styles.fontSize || '14'}px; color: ${styles.textColor || '#666666'};">
        <span>Discount</span>
        <span style="font-weight: 500; color: #10b981;">-${component.currencySymbol || '$'}${discount.toFixed(2)}</span>
      </div>`
      }

      // Fee
      if (component.showFees) {
        html += `
      <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: ${styles.fontSize || '14'}px; color: ${styles.textColor || '#666666'};">
        <span>Fee</span>
        <span style="font-weight: 500;">${component.currencySymbol || '$'}${fee.toFixed(2)}</span>
      </div>`
      }

      // Tax
      if (component.showTaxes && tax > 0) {
        html += `
      <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: ${styles.fontSize || '14'}px; color: ${styles.textColor || '#666666'};">
        <span>Tax</span>
        <span style="font-weight: 500;">${component.currencySymbol || '$'}${tax.toFixed(2)}</span>
      </div>`
      }

      // Divider
      html += `<div style="border-top: 1px solid ${styles.dividerColor || '#d1d5db'}; margin: 12px 0;"></div>`

      // Total
      html += `
    <div style="display: flex; justify-content: space-between; margin-bottom: 12px; font-size: ${styles.totalFontSize || '18'}px; font-weight: 700; color: ${styles.totalColor || '#111827'};">
      <span>Total</span>
      <span>${component.currencySymbol || '$'}${total.toFixed(2)}</span>
    </div>`

      // Payment Status
      if (component.showPaymentStatus) {
        html += `
      <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid rgba(0, 0, 0, 0.1);">
        <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: ${styles.fontSize || '14'}px; font-weight: 500;">
          <span style="color: ${styles.textColor || '#666666'};">Amount paid</span>
          <span style="color: #10b981;">${component.currencySymbol || '$'}${amountPaid.toFixed(2)}</span>
        </div>
        <div style="display: flex; justify-content: space-between; font-size: ${styles.fontSize || '14'}px; font-weight: 500;">
          <span style="color: ${styles.textColor || '#666666'};">Amount due</span>
          <span style="color: #ef4444;">${component.currencySymbol || '$'}${amountDue.toFixed(2)}</span>
        </div>
      </div>`
      }

      html += '</div>'
      return html
    },
    getSampleLineItemDateTime(withEnd = false) {
      // Format the placeholder date the same way sent emails do: the group's
      // date/time formats, weekday prefix only when show_day_of_week is on.
      let group = {}
      try {
        group = JSON.parse(localStorage.getItem('group')) || {}
      } catch (e) {
        group = {}
      }
      const sample = moment('2025-08-28 11:45', 'YYYY-MM-DD HH:mm')
      const dateFormat = (group.show_day_of_week ? 'dddd, ' : '') + (group.date_format || 'MM-DD-YYYY')
      const text = sample.format(dateFormat) + ' • ' + sample.format(pickerTimeFormat(group.time_format))
      return withEnd ? text + ' - ' + sample.clone().add(90, 'minutes').format(pickerTimeFormat(group.time_format)) : text
    },
    generateLineItemsHTML(component) {
      const styles = component.styles || {}
      const language = component.language || 'english'
      const showPrice = component.showPrice !== false
      const showTicketCategories = component.showTicketCategories === true
      const showEndTime = component.showEndTime === true
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor
      const fontFamily = styles.fontFamily || 'Arial, sans-serif'

      // Translation labels
      const translations = {
        english: { item: 'ITEM', quantity: 'QUANTITY', price: 'PRICE' },
        spanish: { item: 'ARTÍCULO', quantity: 'CANTIDAD', price: 'PRECIO' },
        french: { item: 'ARTICLE', quantity: 'QUANTITÉ', price: 'PRIX' },
        swedish: { item: 'ARTIKEL', quantity: 'ANTAL', price: 'PRIS' },
        danish: { item: 'VARE', quantity: 'ANTAL', price: 'PRIS' },
        greek: { item: 'ΣΤΟΙΧΕΙΟ', quantity: 'ΠΟΣΟΤΗΤΑ', price: 'ΤΙΜΗ' }
      }

      const labels = translations[language] || translations.english

      // Info label translations
      const infoTranslations = {
        english: { dateTime: 'Date/time', bookingReference: 'Booking reference', code: 'Code' },
        spanish: { dateTime: 'Fecha/hora', bookingReference: 'Referencia de reserva', code: 'Código' },
        french: { dateTime: 'Date/heure', bookingReference: 'Référence de réservation', code: 'Code' },
        swedish: { dateTime: 'Datum/tid', bookingReference: 'Bokningsreferens', code: 'Kod' },
        danish: { dateTime: 'Dato/tidspunkt', bookingReference: 'Bookingreference', code: 'Kode' },
        greek: { dateTime: 'Ημερομηνία/ώρα', bookingReference: 'Αναφορά κράτησης', code: 'Κωδικός' }
      }

      const infoLabels = infoTranslations[language] || infoTranslations.english

      const items = component.items && component.items.length > 0 ? component.items : [
        {
          name: 'My game',
          info: [
            { label: 'dateTime', value: this.getSampleLineItemDateTime(showEndTime) },
            { label: 'bookingReference', value: '#20227577' }
          ],
          quantity: 'x6',
          tickets: [{ category: 'Adult', number_of_players: 4 }, { category: 'Child', number_of_players: 2 }],
          price: '$174.00'
        }
      ]

      let containerStyles = `padding: ${styles.paddingTop || '20'}px ${styles.paddingRight || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingLeft || '20'}px; margin: ${styles.marginTop || '16'}px 0 ${styles.marginBottom || '16'}px 0;`

      if (backgroundColor !== 'transparent') {
        containerStyles += ` background-color: ${backgroundColor};`
      }

      if (styles.showBorder) {
        containerStyles += ` border: 1px solid ${styles.borderColor || '#e5e7eb'}; border-radius: ${styles.borderRadius || '0'}px;`
      }

      let html = `<div style="${containerStyles}">`

      // Table structure
      html += `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="font-family: ${fontFamily};">`

      // Header - adjust columns based on showPrice
      const headerColspan = showPrice ? '' : ' colspan="2"'
      html += `<tr style="border-bottom: 2px solid ${styles.dividerColor || '#e5e7eb'};">`
      html += `<td style="padding-bottom: 12px; font-size: ${styles.headerFontSize || '12'}px; font-weight: ${styles.headerFontWeight || '600'}; color: ${styles.headerColor || '#666666'}; text-transform: uppercase; letter-spacing: 0.5px; font-family: ${fontFamily};">${labels.item}</td>`

      if (showPrice) {
        html += `<td style="padding-bottom: 12px; font-size: ${styles.headerFontSize || '12'}px; font-weight: ${styles.headerFontWeight || '600'}; color: ${styles.headerColor || '#666666'}; text-transform: uppercase; letter-spacing: 0.5px; text-align: right; font-family: ${fontFamily};">${labels.quantity}</td>`
        html += `<td style="padding-bottom: 12px; font-size: ${styles.headerFontSize || '12'}px; font-weight: ${styles.headerFontWeight || '600'}; color: ${styles.headerColor || '#666666'}; text-transform: uppercase; letter-spacing: 0.5px; text-align: right; font-family: ${fontFamily};">${labels.price}</td>`
      } else {
        html += `<td style="padding-bottom: 12px; font-size: ${styles.headerFontSize || '12'}px; font-weight: ${styles.headerFontWeight || '600'}; color: ${styles.headerColor || '#666666'}; text-transform: uppercase; letter-spacing: 0.5px; text-align: right; font-family: ${fontFamily};">${labels.quantity}</td>`
      }

      html += '</tr>'

      // Items
      items.forEach((item, index) => {
        const isLast = index === items.length - 1
        const borderStyle = isLast ? '' : `border-bottom: 1px solid ${styles.dividerColor || '#f0f0f0'};`
        const ticketLines = showTicketCategories && Array.isArray(item.tickets)
          ? item.tickets
            .filter(t => t && Number(t.number_of_players) > 0)
            .map(t => (t.category ? `${t.category}: ${Number(t.number_of_players)}` : String(Number(t.number_of_players))))
          : []
        const quantityCell = ticketLines.length > 0
          ? `<div style="display: inline-block; text-align: left;">${ticketLines.join('<br>')}</div>`
          : item.quantity

        html += `<tr style="${borderStyle}">`
        html += `<td style="padding: 12px 0; font-size: ${styles.fontSize || '14'}px; color: ${styles.textColor || '#333333'}; font-family: ${fontFamily};">`
        html += `<div style="font-weight: 500;">${item.name}</div>`

        if (item.info) {
          item.info.forEach(info => {
            const labelText = infoLabels[info.label] || info.label
            html += `<div style="font-size: 0.9em; color: #666666; margin-top: 4px;">${labelText}: ${info.value}</div>`
          })
        }

        html += '</td>'

        if (showPrice) {
          html += `<td style="padding: 12px 0; font-size: ${styles.fontSize || '14'}px; color: ${styles.textColor || '#333333'}; text-align: right; font-family: ${fontFamily};">${quantityCell}</td>`
          html += `<td style="padding: 12px 0; font-size: ${styles.fontSize || '14'}px; color: ${styles.textColor || '#333333'}; text-align: right; font-weight: 500; font-family: ${fontFamily};">${item.price}</td>`
        } else {
          html += `<td style="padding: 12px 0; font-size: ${styles.fontSize || '14'}px; color: ${styles.textColor || '#333333'}; text-align: right; font-family: ${fontFamily};">${quantityCell}</td>`
        }

        html += '</tr>'
      })

      html += '</table></div>'
      return html
    },

    formatPriceForLanguage(price, currencySymbol, language) {
      const formatted = price.toFixed(2)

      // Format based on language conventions
      if (language === 'french' || language === 'spanish') {
        // European format: 174,00 €
        const europeanFormatted = formatted.replace('.', ',')
        return language === 'french' ?
          `${europeanFormatted} ${currencySymbol}` :
          `${currencySymbol}${europeanFormatted}`
      } else if (language === 'swedish' || language === 'danish') {
        // Swedish and Danish format: 174,00 kr
        const swedishFormatted = formatted.replace('.', ',')
        return `${swedishFormatted} ${currencySymbol}`
      } else if (language === 'greek') {
        // Greek format: 174,00 €
        const greekFormatted = formatted.replace('.', ',')
        return `${greekFormatted} ${currencySymbol}`
      }

      // Default format: $174.00
      return `${currencySymbol}${formatted}`
    },
    getButtonSizeStyles(size) {
      const sizeMap = {
        small: {
          padding: '8px 16px',
          fontSize: '14px'
        },
        medium: {
          padding: '12px 24px',
          fontSize: '16px'
        },
        large: {
          padding: '16px 32px',
          fontSize: '18px'
        }
      }

      return sizeMap[size] || sizeMap.medium
    },
    generateBillingHTML(component) {
      const styles = component.styles || {}
      const language = component.language || 'english'
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      const containerStyles = `
    font-family: ${styles.fontFamily || 'Arial, sans-serif'};
    font-size: ${styles.fontSize || '14'}px;
    color: ${styles.textColor || '#666666'};
    background-color: ${backgroundColor};
    ${styles.showBorder ? `border: 1px solid ${styles.borderColor || '#e9ecef'};` : ''}
    border-radius: ${styles.borderRadius || '8'}px;
    padding: ${styles.paddingTop || '16'}px ${styles.paddingRight || '16'}px ${styles.paddingBottom || '16'}px ${styles.paddingLeft || '16'}px;
    margin: ${styles.marginTop || '16'}px 0 ${styles.marginBottom || '16'}px 0;
  `

      const labels = {
        english: { transactionReference: 'Transaction reference', transactionDate: 'Transaction date', paymentStatus: 'Payment status' },
        spanish: { transactionReference: 'Referencia de transacción', transactionDate: 'Fecha de transacción', paymentStatus: 'Estado del pago' },
        french: { transactionReference: 'Référence de transaction', transactionDate: 'Date de transaction', paymentStatus: 'Statut du paiement' },
        swedish: { transactionReference: 'Transaktionsreferens', transactionDate: 'Transaktionsdatum', paymentStatus: 'Betalningsstatus' },
        danish: { transactionReference: 'Transaktionsreference', transactionDate: 'Transaktionsdato', paymentStatus: 'Betalingsstatus' },
        greek: { transactionReference: 'Αναφορά συναλλαγής', transactionDate: 'Ημερομηνία συναλλαγής', paymentStatus: 'Κατάσταση πληρωμής' }
      }

      const currentLabels = labels[language] || labels.english

      return `
    <div style="${containerStyles}">
      <div style="text-align: right;">
        <div style="margin-bottom: 4px; font-size: ${styles.fontSize || '14'}px; color: ${styles.textColor || '#666666'};">
          <span style="font-weight: 500;">${currentLabels.transactionReference}: </span>#92100277723
        </div>
        <div style="margin-bottom: 4px; font-size: ${styles.fontSize || '14'}px; color: ${styles.textColor || '#666666'};">
          <span style="font-weight: 500;">${currentLabels.transactionDate}: </span>September 05, 2025
        </div>
        <div style="font-size: ${styles.fontSize || '14'}px; color: ${styles.textColor || '#666666'};">
          <span style="font-weight: 500;">${currentLabels.paymentStatus}: </span>
          <span style="color: ${styles.statusColorUnpaid || '#dc3545'}; font-weight: 600; text-transform: uppercase;">
            UNPAID
          </span>
        </div>
      </div>
    </div>
  `
    },
    async saveAsDraft() {
      if (this.isReservedFromDomain) {
        this.promptReservedFromEmailFix(() => this.saveAsDraft())
        return
      }
      // Validate all fields before saving
      if (!this.validateAllFields()) {
        this.showAlert({
          title: 'Validation error',
          message: 'Fix the highlighted errors before saving.',
        })
        return
      }
      await this.saveTemplate('draft')
    },

    // The reserved sending domain blocks every save, not just activation, so
    // each save path routes through the same one-click fix.
    // Single-button notice. `onClose` runs once the dialog is dismissed, for
    // notices that need to act afterwards (a redirect, for example).
    showAlert({ title, message, variant = 'warning', confirmText = 'OK', onClose = null }) {
      this.alertDialog = { show: true, title, message, variant, confirmText, onClose }
    },

    closeAlert() {
      const onClose = this.alertDialog.onClose
      this.alertDialog.show = false
      this.alertDialog.onClose = null
      if (onClose) {
        onClose()
      }
    },

    promptReservedFromEmailFix(retry) {
      this.reservedEmailPrompt = {
        show: true,
        message: `offthecouch.io is reserved for platform system emails. Use ${this.suggestedPlatformEmail} instead, or pick an address on a domain you have verified under email and messaging settings.`,
        retry,
      }
    },

    confirmReservedFromEmailFix() {
      const retry = this.reservedEmailPrompt.retry
      this.reservedEmailPrompt.show = false
      this.useSuggestedPlatformEmail()
      if (retry) {
        retry()
      }
    },

    cancelReservedFromEmailFix() {
      this.reservedEmailPrompt.show = false
      this.modals.templateSettings = true
    },

    isReservedDomainEmail(email) {
      const value = String(email || '').trim().toLowerCase()
      const at = value.lastIndexOf('@')
      return at !== -1 && value.slice(at + 1) === 'offthecouch.io'
    },

    useSuggestedPlatformEmail() {
      this.templateData.from_email = this.suggestedPlatformEmail
      this.clearFieldError('from_email')
    },

    async saveAndActivate() {
      if (this.isReservedFromDomain) {
        this.promptReservedFromEmailFix(() => this.saveAndActivate())
        return
      }
      // Must save template first before activating
      if (!this.templateData.id) {
        this.showAlert({
          title: 'Template not saved',
          message: 'Complete the template settings and save as draft before activating.',
        })
        this.modals.templateSettings = true
        return
      }
      // Validate all fields before activating
      if (!this.validateAllFields()) {
        this.showAlert({
          title: 'Validation error',
          message: 'Fix the highlighted errors before activating.',
        })
        return
      }
      if (this.activationBlockReason) {
        this.showAlert({
          title: 'Cannot activate',
          message: this.activationBlockReason,
        })
        this.modals.templateSettings = true
        return
      }
      await this.saveTemplate('active')
    },

    sendTestEmail() {
      this.testEmailData = {
        email: '',
        loading: false
      }
      this.testEmailErrors = {
        email: null
      }
      this.modals.testEmail = true
    },
    hexToRgba(hex, alpha) {
      if (!hex) return `rgba(229, 231, 235, ${alpha})`

      // Remove # if present
      hex = hex.replace('#', '')

      // Convert to RGB
      const r = parseInt(hex.substring(0, 2), 16)
      const g = parseInt(hex.substring(2, 4), 16)
      const b = parseInt(hex.substring(4, 6), 16)

      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    // Stats calculations
    getTotalComponents() {
      return this.templateComponents.header.length +
        this.templateComponents.body.length +
        this.templateComponents.footer.length
    },

    getEstimatedSize() {
      // Calculate HTML size
      const htmlSize = this.generateEmailHTML().length

      // Calculate attachment sizes (estimated based on typical attachment sizes)
      let attachmentSize = 0

      if (this.selectedAttachments && this.selectedAttachments.length > 0) {
        this.selectedAttachments.forEach(attachmentType => {
          switch(attachmentType) {
            case 'attachment_invoice':
              // PDF invoices are typically 50-200KB
              attachmentSize += 100 * 1024 // 100KB average
              break
            case 'attachment_photo':
              // Photos can vary but let's estimate 500KB for email-optimized images
              attachmentSize += 500 * 1024 // 500KB average
              break
            default:
              // Default attachment size estimate
              attachmentSize += 50 * 1024 // 50KB
          }
        })
      }

      const totalSize = htmlSize + attachmentSize
      const totalSizeKB = Math.round(totalSize / 1024)

      // Add warning for large emails (many providers limit to 25MB)
      const warningThreshold = 20 * 1024 // 20MB in KB
      const isLarge = totalSizeKB > warningThreshold

      // Format the size appropriately
      let sizeString = ''
      if (totalSize < 1024) {
        sizeString = totalSize + 'B'
      } else if (totalSize < 1024 * 1024) {
        sizeString = totalSizeKB + 'KB'
      } else {
        sizeString = (totalSize / (1024 * 1024)).toFixed(1) + 'MB'
      }

      // Add warning emoji if too large
      return isLarge ? '⚠️ ' + sizeString : sizeString
    },

    getMobileReadiness() {
      let score = 100  // Start at 100% instead of 90%

      // Check all sections
      const allComponents = [
        ...this.templateComponents.header,
        ...this.templateComponents.body,
        ...this.templateComponents.footer
      ]

      allComponents.forEach(component => {
        // Responsive images (using % or max-width)
        if (component.type === 'image') {
          const width = component.styles?.width
          if (width && !width.includes('%') && parseInt(width) > 320) {
            score -= 10  // Fixed width > 320px
          }
        }

        // Single column layouts are mobile-friendly
        if (component.type === 'row' && component.columns?.length > 1) {
          score -= 5  // Multi-column might not stack well
        }

        // Check font sizes (14px minimum for mobile)
        if (component.styles?.fontSize && parseInt(component.styles.fontSize) < 14) {
          score -= 5
        }

        // Button sizes (medium or large are better for mobile)
        if (component.type === 'button' && component.size === 'small') {
          score -= 5
        }
      })

      // Container width (600px or less is mobile-friendly)
      if (this.templateData.layout.contentWidth > 600) {
        score -= 10
      }

      return Math.max(0, Math.min(100, score))
    },

    // Load template types from API
    async loadTemplateTypes() {
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-template-types?count=100&offset=0&init=true`

        const response = await axios.get(url, config)

        // Extract template types from API response
        if (response.data && response.data.template_types) {
          this.templateTypes = response.data.template_types
        } else {
          console.warn('No template types found in API response')
          this.templateTypes = []
        }

        // Keep the group's configured sender around for the from email warning
        if (response.data && response.data.email_settings) {
          this.groupEmailSettings = response.data.email_settings
        }

        // Set default email settings for new templates only
        if (!this.templateData.id && response.data && response.data.email_settings) {
          const emailSettings = response.data.email_settings
          const configuredFrom = (emailSettings.from_email || '').trim().toLowerCase()
          const configuredAt = configuredFrom.lastIndexOf('@')
          const configuredDomain = configuredAt === -1 ? '' : configuredFrom.slice(configuredAt + 1)
          // offthecouch.io is the platform system sender and cannot be a template
          // sender, so leave the builder default in place for groups still on it.
          const canSeedAddresses = configuredDomain !== 'offthecouch.io'

          if (emailSettings.sender_name) {
            this.templateData.from_name = emailSettings.sender_name
          }
          if (canSeedAddresses && emailSettings.from_email) {
            this.templateData.from_email = emailSettings.from_email
          }
          if (canSeedAddresses && emailSettings.reply_to_email) {
            this.templateData.reply_to_email = emailSettings.reply_to_email
          }
        }
      } catch (err) {
        console.error('Error loading template types:', err)
        // Fallback to empty array if API fails
        this.templateTypes = []
      }
    },
    async loadExistingTemplate(templateId, options = {}) {
      if (!options.quiet) this.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-templates/${templateId}`

        const response = await axios.get(url, config)

        if (response.data.success && response.data.template) {
          const template = response.data.template

          // Load basic template data
          this.templateData = {
            id: template.id,
            name: template.name,
            description: template.description,
            template_type: {
              id: template.template_type_id,
              name: template.template_type,
              color: template.template_type_color,
              icon: template.template_type_icon
            },
            subject_line: template.subject_line,
            from_name: template.from_name,
            from_email: template.from_email,
            reply_to_email: template.reply_to_email,
            template_type_icon: template.template_type_icon,
            template_type_color: template.template_type_color,
            template_type_id: template.template_type_id,
            preview_text: template.preview_text,
            layout: template.styles_json?.layout || {
              contentWidth: 600,
              backgroundColor: '#f5f5f5'
            }
          }

          // Load template components if they exist
          if (template.content_json && template.content_json.components) {
            this.loadTemplateComponents(template.content_json.components)
          }

          if (template.content_json && template.content_json.attachments) {
            this.selectedAttachments = template.content_json.attachments || []
          }

          // Load global styles
          if (template.styles_json) {
            this.templateData.layout = {
              ...this.templateData.layout,
              ...template.styles_json
            }
          }

          if (template.availability_settings) {
            this.templateData.availabilitySettings = template.availability_settings
            this.templateAvailability.showOnTransactionDetails =
              template.availability_settings.show_on_transaction_details || false
          }

          this.markTemplateSaved()

        } else {
          throw new Error('Template data not found in response')
        }

      } catch (error) {
        console.error('Failed to load template:', error)

        let errorMessage = 'Failed to load template'

        if (error.response) {
          const status = error.response.status
          const data = error.response.data

          switch (status) {
            case 401:
              errorMessage = 'Not authorized to view this template'
              break
            case 404:
              errorMessage = 'Template not found'
              break
            case 500:
              errorMessage = 'Server error occurred while loading template'
              break
            default:
              errorMessage = data.error || data.message || 'Unknown error occurred'
          }
        } else if (error.request) {
          errorMessage = 'Unable to connect to the server'
        }

        this.showAlert({
          title: 'Could not load template',
          message: errorMessage,
          variant: 'danger',
          // Redirect back to templates list on error
          onClose: () => this.$router.push('/email-templates'),
        })
      } finally {
        if (!options.quiet) this.loading = false
      }
    },
    loadTemplateComponents(components) {
      this.templateComponents = {
        header: [],
        body: [],
        footer: []
      }

      components.forEach(component => {
        let section = component.position?.section || 'body'

        // Force non-footer components to body section
        if (section === 'footer' && component.type !== 'footer') {
          console.warn(`Fixing misplaced ${component.type} component from footer to body`);
          section = 'body';
        }

        // Force non-header components out of header section
        if (section === 'header' && component.type !== 'header') {
          console.warn(`Fixing misplaced ${component.type} component from header to body`);
          section = 'body';
        }

        const processedComponent = {
          id: component.id || 'comp_' + Date.now() + Math.random(),
          type: component.type,
          ...component
        }

        if (this.templateComponents[section]) {
          this.templateComponents[section].push(processedComponent)
        } else {
          this.templateComponents.body.push(processedComponent)
        }
      })

      // Sort by order if exists
      Object.keys(this.templateComponents).forEach(section => {
        this.templateComponents[section].sort((a, b) => {
          const orderA = a.position?.order || 0
          const orderB = b.position?.order || 0
          return orderA - orderB
        })
      })
    }
  },
  beforeDestroy() {
    if (this.aiPollTimer) clearTimeout(this.aiPollTimer)
  },
  async mounted() {
    // Load existing template if ID provided in route
    const templateId = this.$route.params.id

    // Load template types first (needed for the settings modal dropdown)
    try {
      await this.loadTemplateTypes()
    } catch (error) {
      console.error('Error loading template types:', error)
    }

    if (templateId) {
      await this.loadExistingTemplate(templateId)
    } else {
      // Show template settings modal for new template (if not skipping settings)
      if (!this.$route.query.skipSettings) {
        this.modals.templateSettings = true
      }

      // Set up default template based on type
      if (this.$route.query.type) {
        this.templateData.template_type = this.$route.query.type
        this.templateData.name = `New ${this.getTypeName(this.$route.query.type)} Template`
      }
    }
  }
}
</script>

<style scoped>

/* From email mismatch warning (non-blocking) — matches info-hint warning palette */
.has-from-email-warning .form-control {
  border-color: rgba(255, 178, 54, 0.5);
}

.has-from-email-warning .form-control:focus {
  border-color: #ffb236;
}

/* Content Editor */
.content-editor {
  position: relative;
}

.content-editor textarea {
  background: #22233a;
  border: 1px solid #393c54;
  color: rgba(255, 255, 255, 0.9);
  border-radius: 0.375rem;
  padding: 0.75rem;
  font-size: 0.875rem;
  line-height: 1.4;
  resize: vertical;
  min-height: 180px;
}

.content-editor textarea:focus {
  border-color: #e14eca;
  box-shadow: 0 0 0 3px rgba(225, 78, 202, 0.1);
  outline: none;
}

.formatting-toolbar {
  display: flex;
  gap: 0.25rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.375rem;
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.format-btn {
  padding: 0.5rem;
  background: transparent;
  border: 1px solid #393c54;
  border-radius: 0.25rem;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
}

.format-btn:hover {
  background: #393c54;
  color: #e14eca;
  border-color: #e14eca;
}

.format-btn.active {
  background: #e14eca;
  color: white;
  border-color: #e14eca;
}

.font-option {
  font-size: 14px !important;
  padding: 8px 20px !important;
}

.font-option:hover {
  background: rgba(225, 78, 202, 0.1) !important;
}

/* Font Size Controls */
.size-control {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.size-presets {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.25rem;
}

.preset-btn {
  padding: 0.375rem 0.5rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.25rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.preset-btn:hover {
  background: #393c54;
  border-color: #e14eca;
  color: #e14eca;
}

.preset-btn.active {
  background: #e14eca;
  border-color: #e14eca;
  color: white;
  font-weight: 600;
}

.custom-size {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.custom-size input {
  flex: 1;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.375rem;
  padding: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
}

.custom-size input:focus {
  border-color: #e14eca;
  outline: none;
  box-shadow: 0 0 0 2px rgba(225, 78, 202, 0.2);
}

.custom-size .unit {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
  font-weight: 500;
}

/* Range Inputs */
.range-input {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.slider {
  flex: 1;
  height: 6px;
  background: #393c54;
  border-radius: 3px;
  outline: none;
  cursor: pointer;
}

.slider::-webkit-slider-thumb {
  appearance: none;
  width: 18px;
  height: 18px;
  background: #e14eca;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
}

.slider::-webkit-slider-thumb:hover {
  background: #d63bb7;
  transform: scale(1.1);
}

.slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  background: #e14eca;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.slider::-moz-range-thumb:hover {
  background: #d63bb7;
  transform: scale(1.1);
}

/* Enhanced Alignment Buttons */
.alignment-buttons {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.25rem;
}

.align-btn {
  padding: 0.625rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.375rem;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.align-btn:hover {
  background: #393c54;
  border-color: #e14eca;
  color: #e14eca;
  transform: translateY(-1px);
}

.align-btn.active {
  background: #e14eca;
  border-color: #e14eca;
  color: white;
  box-shadow: 0 2px 8px rgba(225, 78, 202, 0.3);
}

.align-btn i {
  font-size: 1rem;
}

/*!* Enhanced Color Picker *!*/
/*.el-color-picker {*/
/*  width: 100% !important;*/
/*}*/

/*.el-color-picker__trigger {*/
/*  width: 100% !important;*/
/*  height: 40px !important;*/
/*  border-radius: 0.375rem !important;*/
/*  border: 1px solid #393c54 !important;*/
/*  background: #27293d !important;*/
/*}*/

/*.el-color-picker__trigger:hover {*/
/*  border-color: #e14eca !important;*/
/*}*/

/* Section Spacing */
.typography-section,
.appearance-section,
.spacing-section {
  padding-bottom: 1rem;
}

/* Form Group Enhancements */
.text-properties .form-group {
  margin-bottom: 1rem;
}

/*.text-properties .form-group label {*/
/*  display: block;*/
/*  margin-bottom: 0.5rem;*/
/*  font-weight: 500;*/
/*  color: rgba(255, 255, 255, 0.8);*/
/*  font-size: 0.875rem;*/
/*}*/

/* Enhanced Text Component in Canvas */
.email-component.text-component {
  min-height: 40px;
  padding: 8px 12px;
  word-wrap: break-word;
  white-space: pre-wrap;
  line-height: 1.5;
}

.email-component.text-component.selected {
  outline: 2px solid #e14eca;
  outline-offset: 2px;
}

.email-component.text-component .component-content {
  padding: 0;
  min-height: 20px;
}

.email-component.text-component:empty::before {
  content: "Enter your text here...";
  color: #9ca3af;
  font-style: italic;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .size-presets {
    grid-template-columns: repeat(2, 1fr);
  }

  .alignment-buttons {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Modal offset for left sidebar */
.modal-offset-left .modal-dialog {
  margin-left: 140px; /* Adjust based on your sidebar width */
}

/* Form Validation Styles */
.has-danger .form-control {
  border-color: #dc3545 !important;
}

.is-invalid {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Email Builder Wrapper */
.email-builder-wrapper {
  height: 100vh;
  display: flex;
  flex-direction: column;
  /* Page backdrop, not a card surface: the builder's own panels paint the card
     colour, and the breadcrumb sits on this. */
  background: #171826;
}

/* Breadcrumb Section */
.breadcrumb-section {
  /* Transparent so the breadcrumb reads as page chrome above the card rather
     than as the card's first row. */
  background: transparent;
  flex-shrink: 0;
}

/* Bootstrap's .breadcrumb carries its own bottom margin on top of its padding,
   which stacked into a large gap above the card. */
.breadcrumb-section .breadcrumb {
  margin-bottom: 0;
}

.template-builder {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Builder Header */
.builder-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #171826;
  /*background: transparent !important;*/
  border-bottom: 1px solid #393c54;
  padding: 1rem 1.5rem 0.5rem 1.5rem;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.template-info {
  display: flex;
  flex-direction: column;
}

.template-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #ffffff;
}

.template-type {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

/* Main Builder Container */
.builder-container {
  display: grid;
  grid-template-columns: 280px 1fr 280px;
  gap: 0;
  flex: 1;
  overflow: hidden;
}

/* Sidebars */
.components-sidebar,
.properties-sidebar {
  background: #171826;
  border-right: 1px solid #393c54;
  overflow-y: auto;
  padding: 1.5rem;
}

.properties-sidebar {
  border-left: 1px solid #393c54;
  border-right: none;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

/* Layout Options */
.layout-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.layout-option {
  background: #22233a;
  border: 2px solid #393c54;
  border-radius: 0.5rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.layout-option:hover {
  border-color: #e14eca;
  transform: translateY(-1px);
}

.layout-option.active {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
}

.layout-preview {
  display: flex;
  gap: 2px;
  margin-bottom: 0.5rem;
  height: 20px;
}

.layout-column {
  background: #e14eca;
  border-radius: 2px;
  opacity: 0.7;
}

.layout-option span {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

/* Components Grid */
.components-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.component-item {
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  color: rgba(255, 255, 255, 0.8);
}

.component-item:hover {
  border-color: #e14eca;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(225, 78, 202, 0.3);
}

.component-item i {
  display: block;
  font-size: 1.5rem;
  color: #e14eca;
  margin-bottom: 0.5rem;
}

.component-item span {
  font-size: 0.875rem;
  font-weight: 500;
}

/* Columns/Row component - Full width with special styling */
.component-item.columns-item {
  grid-column: span 2;
  background: linear-gradient(135deg, #22233a 0%, #1e2035 100%);
  border: 1px dashed #e14eca;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
}

.component-item.columns-item i {
  display: inline-block;
  font-size: 1.25rem;
  margin-bottom: 0;
  color: #e14eca;
}

.component-item.columns-item span {
  font-size: 0.875rem;
  font-weight: 600;
  color: #e14eca;
}

.component-item.columns-item:hover {
  border-style: solid;
  background: linear-gradient(135deg, #342850 0%, #22233a 100%);
}

/* Variables */
.variables-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.variable-item {
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.375rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.variable-item:hover {
  border-color: #e14eca;
  background: #2b2c40;
}

.variable-item code {
  display: block;
  color: #e14eca;
  font-size: 0.75rem;
  margin-bottom: 0.25rem;
}

.variable-item span {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
}

/* Canvas Area */
.canvas-area {
  background: #22233a;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.canvas-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #171826;
  border-bottom: 1px solid #393c54;
  flex-shrink: 0;
}

.canvas-tabs {
  display: flex;
  gap: 0.5rem;
}

.tab {
  padding: 0.5rem 1rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: all 0.2s;
}

.tab.active {
  background: linear-gradient(45deg, #e14eca, #ba54f5);
  color: white;
  border-color: #e14eca;
  box-shadow: 0 4px 20px rgba(225, 78, 202, 0.4);
}

.canvas-tools {
  display: flex;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.6);
}

.canvas-tools i {
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
}

.canvas-tools i:hover,
.canvas-tools i.active {
  background: #393c54;
  color: #e14eca;
}

/* Canvas Content */
.canvas-content {
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  background: #f5f5f5;
}

.email-canvas {
  min-height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 2rem;
  transition: background-color 0.3s;
}

/* Fixed canvas mobile view - only affects email container, not the entire canvas */
.email-canvas.canvas-mobile .email-container {
  max-width: 375px !important;
}

.email-container {
  background: white;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  min-height: 600px;
  transition: max-width 0.3s ease;
  /* Ensure container maintains its size during updates */
  display: flex;
  flex-direction: column;
  width: 100%;
}

.email-header-section,
.email-body-section,
.email-footer-section {
  position: relative;
}

/* Drag and Drop Styles */
.drag-zone {
  min-height: 60px;
  position: relative;
  padding: 1rem;
  transition: all 0.2s;
}

.drag-zone.drag-over {
  background: rgba(225, 78, 202, 0.1);
  border: 2px dashed #e14eca;
  border-radius: 0.5rem;
}

.empty-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  border: 2px dashed #d1d5db;
  border-radius: 0.5rem;
  color: #6b7280;
  text-align: center;
  transition: all 0.2s;
}

.empty-zone:hover {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.05);
}

.main-empty {
  min-height: 200px;
  padding: 1rem 2rem 2rem 2rem;
}

.main-empty h4 {
  margin: 1rem 0 0.5rem 0;
  color: #374151;
}

.main-empty p {
  color: #6b7280;
  margin: 0;
}

.empty-zone i {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #d1d5db;
}

/* Email Components */
.email-component {
  position: relative;
  margin: 0.5rem 0;
  border: 2px solid transparent;
  border-radius: 0.375rem;
  transition: all 0.2s;
  cursor: pointer;
}

.email-component:hover,
.email-component.selected {
  border-color: #e14eca;
  box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.1);
}

/* Email Rows */
.email-row {
  position: relative;
  margin: 1rem 0;
  border: 2px solid transparent;
  border-radius: 0.375rem;
  transition: all 0.2s;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.02);
}

.email-row:hover,
.email-row.selected {
  border-color: #e14eca;
  box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.1);
}

.row-columns {
  gap: 1rem;
  padding: 1rem;
}

.row-column {
  border: 1px dashed #d1d5db;
  border-radius: 0.375rem;
  min-height: 60px;
  padding: 0.5rem;
  transition: all 0.2s;
}

.row-column:hover {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.05);
}

.empty-column {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  color: #9ca3af;
  font-size: 0.875rem;
}

.row-controls {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s;
}

.email-row:hover .row-controls,
.email-row.selected .row-controls {
  opacity: 1;
}

.row-control-btn {
  background: rgba(220, 38, 38, 0.9);
  border: none;
  border-radius: 0.25rem;
  padding: 0.25rem;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
}

.row-control-btn:hover {
  background: rgba(220, 38, 38, 1);
  transform: scale(1.1);
}

.row-control-btn.drag-handle {
  background: rgba(59, 130, 246, 0.9);
  cursor: move;
  cursor: grab;
}

.row-control-btn.drag-handle:hover {
  background: rgba(59, 130, 246, 1);
}

.row-control-btn.drag-handle:active {
  cursor: grabbing;
}

/* Component wrapper for non-row items */
.component-wrapper {
  position: relative;
  margin-bottom: 1rem;
}

.component-wrapper.selected {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.component-controls {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 10;
}

.component-wrapper:hover .component-controls,
.component-wrapper.selected .component-controls {
  opacity: 1;
}

.component-control-btn {
  background: rgba(220, 38, 38, 0.9);
  border: none;
  border-radius: 0.25rem;
  padding: 0.25rem;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.component-control-btn:hover {
  background: rgba(220, 38, 38, 1);
  transform: scale(1.1);
}

.component-control-btn.drag-handle {
  background: rgba(59, 130, 246, 0.9);
  cursor: move;
  cursor: grab;
}

.component-control-btn.drag-handle:hover {
  background: rgba(59, 130, 246, 1);
}

.component-control-btn.drag-handle:active {
  cursor: grabbing;
}

/* Ghost element during dragging */
.ghost {
  opacity: 0.5;
  background: rgba(59, 130, 246, 0.1);
  border: 2px dashed #3b82f6;
}

.component-content {
  padding: 0.5rem;
}

/* Column component wrapper for components inside row columns */
.column-component-wrapper {
  position: relative;
  margin-bottom: 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.2s;
}

.column-component-wrapper:last-child {
  margin-bottom: 0;
}

.column-component-wrapper.selected {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.column-component-wrapper:hover {
  background: rgba(59, 130, 246, 0.05);
}

.column-component-controls {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  display: flex;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 15;
}

.column-component-wrapper:hover .column-component-controls,
.column-component-wrapper.selected .column-component-controls {
  opacity: 1;
}

.column-control-btn {
  background: rgba(220, 38, 38, 0.9);
  border: none;
  border-radius: 0.25rem;
  padding: 0.2rem;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.75rem;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.column-control-btn:hover {
  background: rgba(220, 38, 38, 1);
  transform: scale(1.1);
}

/* Email Elements */
.email-button {
  display: inline-block;
  text-decoration: none;
  font-weight: 500;
  border: none;
  cursor: pointer;
}

.email-image {
  max-width: 100%;
  height: auto;
  border-radius: 0.25rem;
}

/* Form Elements */
.form-group {
  margin-bottom: 1rem;
}

/*.form-group label {*/
/*  display: block;*/
/*  margin-bottom: 0.5rem;*/
/*  font-weight: 500;*/
/*  color: rgba(255, 255, 255, 0.8);*/
/*  font-size: 0.5rem;*/
/*}*/

.range-value {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  float: right;
  margin-top: 0.5rem;
}

/* Stats */
.stats-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #22233a;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.stat-value {
  font-weight: 600;
  color: #e14eca;
}

/* Responsive */
@media (max-width: 1024px) {
  .builder-container {
    grid-template-columns: 1fr;
  }

  .components-sidebar,
  .properties-sidebar {
    display: none;
  }
}

/* Custom Scrollbars */
.components-sidebar::-webkit-scrollbar,
.properties-sidebar::-webkit-scrollbar,
.canvas-content::-webkit-scrollbar {
  width: 6px;
}

.components-sidebar::-webkit-scrollbar-track,
.properties-sidebar::-webkit-scrollbar-track,
.canvas-content::-webkit-scrollbar-track {
  background: #171826;
  border-radius: 3px;
}

.components-sidebar::-webkit-scrollbar-thumb,
.properties-sidebar::-webkit-scrollbar-thumb,
.canvas-content::-webkit-scrollbar-thumb {
  background: #393c54;
  border-radius: 3px;
  transition: background 0.2s;
}

.components-sidebar::-webkit-scrollbar-thumb:hover,
.properties-sidebar::-webkit-scrollbar-thumb:hover,
.canvas-content::-webkit-scrollbar-thumb:hover {
  background: #555770;
}

/* Override any inherited scrollbar styles */
.components-sidebar,
.properties-sidebar,
.canvas-content {
  scrollbar-width: thin;
  scrollbar-color: #393c54 #171826;
}

.components-sidebar:hover,
.properties-sidebar:hover,
.canvas-content:hover {
  scrollbar-color: #555770 #171826;
}

/* Smart Values */
.smart-values-trigger {
  display: flex;
}

.smart-values-btn {
  width: 100%;
  transition: all 0.2s;
}

.sv-btn-icon {
  font-family: 'Monaco', 'Menlo', monospace;
  font-weight: 700;
  margin-right: 0.375rem;
  font-size: 0.8rem;
  letter-spacing: -0.5px;
}

/* Enhanced Text Component with Smart Values Support */
.text-properties .content-editor {
  position: relative;
}

.text-properties .content-editor textarea {
  background: #22233a;
  border: 1px solid #393c54;
  color: rgba(255, 255, 255, 0.9);
  border-radius: 0.375rem;
  padding: 0.75rem;
  font-size: 0.875rem;
  line-height: 1.4;
  resize: vertical;
  min-height: 80px;
  transition: all 0.2s;
}

.text-properties .content-editor textarea:focus {
  border-color: #e14eca;
  box-shadow: 0 0 0 3px rgba(225, 78, 202, 0.1);
  outline: none;
}



/* Ensure headers take full width within email container */
.email-header-section {
  margin: 0;
  padding: 0;
}

.email-header-section .drag-zone {
  padding: 0; /* Remove default padding for header section */
}

.email-header-section .email-component.header-component {
  margin: 0 !important;
  width: 100% !important;
  border-radius: 0 !important;
}

/* Ensure email container doesn't add unwanted spacing */
.email-container {
  overflow: hidden; /* Ensure headers can go full width */
}

/* Style the drag zone for headers differently */
.email-header-section .drag-zone.empty-zone {
  margin: 0;
  border-radius: 0;
  min-height: 40px;
  background: rgba(225, 78, 202, 0.05);
  border: 2px dashed #e14eca;
  color: #e14eca;
}

.email-header-section .drag-zone.empty-zone:hover {
  background: rgba(225, 78, 202, 0.1);
}

.footer-style-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.style-option-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 0.5rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.375rem;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.75rem;
}

.style-option-btn:hover {
  background: #393c54;
  border-color: #e14eca;
  color: #e14eca;
  transform: translateY(-1px);
}

.style-option-btn.active {
  background: #e14eca;
  border-color: #e14eca;
  color: white;
  box-shadow: 0 2px 8px rgba(225, 78, 202, 0.3);
}

/* Style preview elements */
.style-preview {
  width: 32px;
  height: 8px;
  background: currentColor;
  border-radius: 1px;
}

.style-preview.solid-line {
  background: currentColor;
}

.style-preview.dashed-line {
  background: transparent;
  border-bottom: 2px dashed currentColor;
  height: 4px;
}

.style-preview.no-line {
  background: transparent;
  border: 1px dashed rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

/* Toggle Controls */
.toggle-control {
  margin-bottom: 1rem;
}

.toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
}

.toggle-label input[type="checkbox"] {
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
  accent-color: #e14eca;
}

.toggle-text {
  user-select: none;
}

/* Social Links Editor */
.social-links-editor {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.social-link-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.social-platform {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

.social-platform i {
  width: 16px;
  color: #e14eca;
}

/* Enhanced Range Inputs */
.footer-properties .range-input {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.footer-properties .slider {
  flex: 1;
  height: 6px;
  background: #393c54;
  border-radius: 3px;
  outline: none;
  cursor: pointer;
  transition: background 0.2s;
}

.footer-properties .slider:hover {
  background: #4a4d6b;
}

.footer-properties .slider::-webkit-slider-thumb {
  appearance: none;
  width: 18px;
  height: 18px;
  background: #e14eca;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.footer-properties .slider::-webkit-slider-thumb:hover {
  background: #d63bb7;
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(225, 78, 202, 0.4);
}

.footer-properties .slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  background: #e14eca;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.footer-properties .slider::-moz-range-thumb:hover {
  background: #d63bb7;
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(225, 78, 202, 0.4);
}

/* Color Picker Styling */
.footer-properties .el-color-picker {
  width: 100% !important;
}

.footer-properties .el-color-picker__trigger {
  width: 100% !important;
  height: 36px !important;
  border-radius: 0.375rem !important;
  border: 1px solid #393c54 !important;
  background: #22233a !important;
  transition: border-color 0.2s !important;
}

.footer-properties .el-color-picker__trigger:hover {
  border-color: #e14eca !important;
}

/* Form Controls */
.footer-properties .form-control {
  background: #22233a !important;
  border: 1px solid #393c54 !important;
  color: rgba(255, 255, 255, 0.9) !important;
  border-radius: 0.375rem !important;
  font-size: 0.875rem !important;
}

.footer-properties .form-control:focus {
  border-color: #e14eca !important;
  box-shadow: 0 0 0 2px rgba(225, 78, 202, 0.2) !important;
}

.footer-properties .form-control::placeholder {
  color: rgba(255, 255, 255, 0.5) !important;
}

/* Textarea Styling */
.footer-properties .el-textarea__inner {
  background: #22233a !important;
  border: 1px solid #393c54 !important;
  color: rgba(255, 255, 255, 0.9) !important;
  border-radius: 0.375rem !important;
}

.footer-properties .el-textarea__inner:focus {
  border-color: #e14eca !important;
  box-shadow: 0 0 0 2px rgba(225, 78, 202, 0.2) !important;
}

/* Footer Section Styling in Canvas */
.email-footer-section {
  margin-top: auto; /* Push to bottom */
  margin-bottom: 0;
  flex-shrink: 0; /* Don't shrink */
}

.email-footer-section .drag-zone {
  padding: 0; /* Remove default padding for footer section */
}

.email-footer-section .drag-zone.empty-zone {
  margin: 0;
  background: rgba(225, 78, 202, 0.05);
  border: 2px dashed #e14eca;
  color: #e14eca;
  min-height: 60px;
  font-size: 0.875rem;
  border-radius: 0;
}

.email-footer-section .drag-zone.empty-zone:hover {
  background: rgba(225, 78, 202, 0.1);
}

/* Footer Component Canvas Styling */
.email-footer-section .footer-component {
  margin: 0 !important;
  width: 100% !important;
  border-radius: 0 !important;
}

/* Range Value Display */
.footer-properties .range-value {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  min-width: 40px;
  text-align: right;
  font-weight: 500;
}

/* Section Spacing */
.footer-properties .sidebar-section {
  padding-bottom: 1rem;
  margin-bottom: 1rem;
}

.footer-properties .sidebar-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

/* Form Group Styling */
.footer-properties .form-group {
  margin-bottom: 1rem;
}

.footer-properties .form-group:last-child {
  margin-bottom: 0;
}

/* Responsive Adjustments */
@media (max-width: 1200px) {
  .social-links-editor {
    gap: 0.75rem;
  }
}

@media (max-width: 768px) {
  .footer-properties .range-input {
    gap: 0.5rem;
  }

  .footer-properties .range-value {
    min-width: 35px;
    font-size: 0.7rem;
  }

  .social-platform {
    font-size: 0.8rem;
  }
}

/* Button Size Controls */
.size-buttons {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.25rem;
}

.size-btn {
  padding: 0.5rem 0.75rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.375rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.size-btn:hover {
  background: #393c54;
  border-color: #e14eca;
  color: #e14eca;
  transform: translateY(-1px);
}

.size-btn.active {
  background: #e14eca;
  border-color: #e14eca;
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(225, 78, 202, 0.3);
}

/* Button Alignment Controls */
.button-properties .alignment-buttons {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.25rem;
}

.button-properties .align-btn {
  padding: 0.625rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.375rem;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.button-properties .align-btn:hover {
  background: #393c54;
  border-color: #e14eca;
  color: #e14eca;
  transform: translateY(-1px);
}

.button-properties .align-btn.active {
  background: #e14eca;
  border-color: #e14eca;
  color: white;
  box-shadow: 0 2px 8px rgba(225, 78, 202, 0.3);
}

.button-properties .align-btn i {
  font-size: 0.9rem;
}

/* Button Appearance Section */
.button-properties .appearance-section {
  padding-bottom: 1rem;
  border-bottom: 1px solid #393c54;
  margin-bottom: 1rem;
}

/* Enhanced Color Picker for Buttons */
.button-properties .el-color-picker {
  width: 100% !important;
}

.button-properties .el-color-picker__trigger:hover {
  border-color: #e14eca !important;
}

/* Range Inputs */
.button-properties .range-input {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.button-properties .slider {
  flex: 1;
  height: 6px;
  background: #393c54;
  border-radius: 3px;
  outline: none;
  cursor: pointer;
  transition: background 0.2s;
}

.button-properties .slider:hover {
  background: #4a4d6b;
}

.button-properties .slider::-webkit-slider-thumb {
  appearance: none;
  width: 18px;
  height: 18px;
  background: #e14eca;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.button-properties .slider::-webkit-slider-thumb:hover {
  background: #d63bb7;
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(225, 78, 202, 0.4);
}

.button-properties .slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  background: #e14eca;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.button-properties .slider::-moz-range-thumb:hover {
  background: #d63bb7;
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(225, 78, 202, 0.4);
}

.button-properties .range-value {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  min-width: 40px;
  text-align: right;
  font-weight: 500;
}

/* Form Controls */
.button-properties .form-control {
  background: #22233a !important;
  border: 1px solid #393c54 !important;
  color: rgba(255, 255, 255, 0.9) !important;
  border-radius: 0.375rem !important;
  font-size: 0.875rem !important;
}

.button-properties .form-control:focus {
  border-color: #e14eca !important;
  box-shadow: 0 0 0 2px rgba(225, 78, 202, 0.2) !important;
}

.button-properties .form-control::placeholder {
  color: rgba(255, 255, 255, 0.5) !important;
}

/* Button Preview in Canvas */
.button-component {
  transition: all 0.2s ease;
}

.button-component.selected {
  outline: 2px solid #e14eca;
  outline-offset: 2px;
}

.button-component .email-button {
  transition: all 0.2s ease;
}

.button-component:hover .email-button {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .size-buttons {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .button-properties .alignment-buttons {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .size-btn,
  .button-properties .align-btn {
    padding: 0.75rem;
  }
}

@media (max-width: 768px) {
  .button-properties .range-input {
    gap: 0.5rem;
  }

  .button-properties .range-value {
    min-width: 35px;
    font-size: 0.7rem;
  }
}

/* Template Type Dropdown Styling */
.template-type-dropdown-item {
  display: flex;
  align-items: center;
  width: 100%;
}

.type-icon-badge {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.type-icon-badge i {
  font-size: 14px;
}

.thickness-buttons {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.25rem;
}

.thickness-btn {
  padding: 0.5rem 0.75rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.375rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.thickness-btn:hover {
  background: #393c54;
  border-color: #e14eca;
  color: #e14eca;
  transform: translateY(-1px);
}

.thickness-btn.active {
  background: #e14eca;
  border-color: #e14eca;
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(225, 78, 202, 0.3);
}

.color-input-group {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.inherit-btn {
  white-space: nowrap;
}


/* Image Properties Styles */
.subsection-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.7);
  /*margin: 1.5rem 0 0.75rem 0;*/
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #393c54;
}

/* Source Toggle */
.source-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.25rem;
  margin-bottom: 1rem;
}

.toggle-btn {
  padding: 0.5rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.375rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.toggle-btn:hover {
  background: #393c54;
  border-color: #e14eca;
  color: #e14eca;
}

.toggle-btn.active {
  background: #e14eca;
  border-color: #e14eca;
  color: white;
  box-shadow: 0 2px 8px rgba(225, 78, 202, 0.3);
}

/* Upload Section */
.upload-section {
  margin-top: 1rem;
}

.current-image {
  text-align: center;
}

.current-image-preview {
  width: 100%;
  max-height: 120px;
  object-fit: cover;
  border-radius: 0.375rem;
  border: 1px solid #393c54;
  margin-bottom: 0.5rem;
}

.image-actions {
  display: flex;
  gap: 0.2rem;
}


.upload-help-text {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 0.5rem;
  line-height: 1.4;
}

/* URL Section */
.url-section {
  margin-top: 1rem;
}

/* Help Text */
.help-text {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: -0.25rem;
  margin-bottom: 0;
}

/* Width Control */
.width-control {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.width-input-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.width-input-group input {
  width: 80px;
}

.width-input-group .unit {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
}

/* Width Presets */
.width-presets {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.25rem;
  margin-top: 0.5rem;
}

.preset-btn {
  padding: 0.375rem 0.5rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.25rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.7rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.preset-btn:hover {
  background: #393c54;
  border-color: #e14eca;
  color: #e14eca;
}

.preset-btn.active {
  background: #e14eca;
  border-color: #e14eca;
  color: white;
  font-weight: 600;
}

/* Alignment Buttons */
.image-properties .alignment-buttons {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.25rem;
}

.image-properties .align-btn {
  padding: 0.625rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 0.375rem;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-properties .align-btn:hover {
  background: #393c54;
  border-color: #e14eca;
  color: #e14eca;
  transform: translateY(-1px);
}

.image-properties .align-btn.active {
  background: #e14eca;
  border-color: #e14eca;
  color: white;
  box-shadow: 0 2px 8px rgba(225, 78, 202, 0.3);
}

/* Slider Styles */
.image-properties .slider {
  flex: 1;
  height: 6px;
  background: #393c54;
  border-radius: 3px;
  outline: none;
  cursor: pointer;
}

.image-properties .slider::-webkit-slider-thumb {
  appearance: none;
  width: 18px;
  height: 18px;
  background: #e14eca;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
}

.image-properties .slider::-webkit-slider-thumb:hover {
  background: #d63bb7;
  transform: scale(1.1);
}

.image-properties .slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  background: #e14eca;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.image-properties .slider::-moz-range-thumb:hover {
  background: #d63bb7;
  transform: scale(1.1);
}

.image-properties .range-value {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  min-width: 40px;
  text-align: right;
}

/* Form Controls */
.image-properties .form-control {
  background: #22233a !important;
  border: 1px solid #393c54 !important;
  color: rgba(255, 255, 255, 0.9) !important;
  border-radius: 0.375rem !important;
  font-size: 0.875rem !important;
}

.image-properties .form-control:focus {
  border-color: #e14eca !important;
  box-shadow: 0 0 0 2px rgba(225, 78, 202, 0.2) !important;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .width-presets {
    grid-template-columns: 1fr;
  }

  .image-properties .alignment-buttons {
    grid-template-columns: 1fr;
  }
}

.x-twitter-custom {
  font-style: normal !important;
  font-weight: bold;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.x-twitter-custom::before {
  content: "𝕏";
  font-size: 16px;
  line-height: 1;
  display: inline-block;
}

/* For the properties panel preview */
.social-platform i.x-twitter-custom::before {
  content: "𝕏";
  font-size: 14px;
}

/* Ensure it works in both light and dark themes */
.footer-component .social-link i.x-twitter-custom::before {
  content: "𝕏";
  font-size: 16px;
}

/* Spam Score Tooltip Styles */
.spam-score-item {
  position: relative;
}

.spam-score-tooltip {
  padding: 0.5rem;
  /* Element's el-popover base style justifies text and breaks words anywhere
     (text-align: justify + word-break: break-all); undo both for the whole
     tooltip so children inherit sane wrapping. */
  text-align: left;
  word-break: normal;
  overflow-wrap: break-word;
}

.spam-score-tooltip .tooltip-title {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  color: #333;
}

.spam-score-tooltip .tooltip-description {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.75rem;
}

.spam-score-tooltip .spam-triggers {
  background: #f5f5f5;
  border-radius: 0.25rem;
  padding: 0.5rem;
  margin-top: 0.5rem;
}

.spam-score-tooltip .triggers-title {
  font-weight: 600;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
  color: #333;
}

.spam-score-tooltip .triggers-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.spam-score-tooltip .triggers-list {
  word-break: normal;
  overflow-wrap: break-word;
}

.spam-score-tooltip .triggers-list li {
  font-size: 0.8rem;
  color: #666;
  padding: 0.25rem 0;
  padding-left: 1rem;
  position: relative;
}

.spam-score-tooltip .triggers-list li:before {
  content: "•";
  position: absolute;
  left: 0;
  color: #ef4444;
}

.spam-score-tooltip .no-issues {
  color: #10b981;
  font-size: 0.7rem;
  font-weight: 500;
  margin: 0;
}

/* Layout Options Grid - Game Component Specific */
.game-properties .layout-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.375rem;
  margin-bottom: 1rem;
}

.game-properties .layout-option {
  background: #22233a;
  border: 2px solid #393c54;
  border-radius: 0.375rem;
  padding: 0.5rem 0.25rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.game-properties .layout-option:hover {
  border-color: #e14eca;
  transform: translateY(-1px);
}

.game-properties .layout-option.active {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
}

.game-properties .layout-preview {
  margin-bottom: 0.375rem;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.25rem;
}

.game-properties .preview-content {
  width: 100%;
  max-width: 60px;
}

.game-properties .layout-option span {
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  display: block;
  line-height: 1.2;
}

/* Game Master Layout Options */
.game-master-layout-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.gm-layout-option {
  background: #22233a;
  border: 2px solid #393c54;
  border-radius: 0.5rem;
  padding: 0.75rem 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.gm-layout-option:hover {
  border-color: #e14eca;
  transform: translateY(-1px);
}

.gm-layout-option.active {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
}

.gm-layout-option span {
  display: block;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  margin-top: 0.5rem;
}

/* Layout Preview Container */
.gm-layout-preview {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0.5rem;
  justify-content: center;
  min-height: 40px;
}

.gm-layout-preview.gm-layout-vertical {
  flex-direction: column;
  gap: 8px;
}

/* Avatar Circle */
.gm-avatar {
  width: 24px;
  height: 24px;
  background: #e14eca;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Text Lines Container */
.gm-text-lines {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
}

.gm-text-lines-center {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: center;
  width: 100%;
}

/* Individual Text Lines */
.gm-line {
  height: 3px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

.gm-line-long {
  width: 100%;
}

.gm-line-short {
  width: 60%;
}

.gm-text-lines-center .gm-line-long {
  width: 80%;
}

.gm-text-lines-center .gm-line-short {
  width: 50%;
}

/* Responsive */
@media (max-width: 1200px) {
  .game-master-layout-options {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .gm-layout-option {
    padding: 0.75rem;
  }

  .gm-layout-preview {
    min-height: 50px;
  }

  .gm-avatar {
    width: 28px;
    height: 28px;
  }
}
/* Gift Card Layout Options Grid */
.gift-card-properties .layout-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.375rem;
  margin-bottom: 1rem;
}

.gift-card-properties .layout-option {
  background: #22233a;
  border: 2px solid #393c54;
  border-radius: 0.375rem;
  padding: 0.5rem 0.25rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.gift-card-properties .layout-option:hover {
  border-color: #e14eca;
  transform: translateY(-1px);
}

.gift-card-properties .layout-option.active {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
}

.gift-card-properties .layout-preview {
  margin-bottom: 0.375rem;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.25rem;
}

.gift-card-properties .preview-content {
  width: 100%;
  max-width: 60px;
}

.gift-card-properties .layout-option span {
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  display: block;
  line-height: 1.2;
}

/* Responsive */
@media (max-width: 1200px) {
  .gift-card-properties .layout-options {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .gift-card-properties .layout-option {
    padding: 0.75rem;
  }

  .gift-card-properties .layout-preview {
    min-height: 50px;
  }
}

/* ==========================================================================
   Light mode
   The builder chrome is painted with hardcoded dark values above, which the
   global `.white-content` flip cannot reach. These rules flip the same
   surfaces so the editor follows the app theme instead of staying a dark
   island. Two tiers: page-level backdrop (#f5f6fa) and panel/control surfaces
   (#ffffff), with #e3e3e3 borders, matching the rest of the light theme.

   Active states are deliberately absent: every `.active` control sits on a
   magenta or red fill in both themes, so its white label stays correct.
   ========================================================================== */
.white-content .email-builder-wrapper,
.white-content .canvas-area {
  background: #f5f6fa;
}

.white-content .builder-header,
.white-content .canvas-header,
.white-content .components-sidebar,
.white-content .properties-sidebar,
.white-content .formatting-toolbar,
.white-content .stat-item,
.white-content .component-item,
.white-content .variable-item,
.white-content .layout-option,
.white-content .gm-layout-option,
.white-content .game-properties .layout-option,
.white-content .gift-card-properties .layout-option,
.white-content .tab,
.white-content .preset-btn,
.white-content .align-btn,
.white-content .button-properties .align-btn,
.white-content .image-properties .align-btn,
.white-content .size-btn,
.white-content .thickness-btn,
.white-content .toggle-btn,
.white-content .style-option-btn,
.white-content .custom-size input,
.white-content .content-editor textarea,
.white-content .text-properties .content-editor textarea,
.white-content .footer-properties .form-control,
.white-content .button-properties .form-control,
.white-content .image-properties .form-control,
.white-content .footer-properties .el-textarea__inner,
.white-content .footer-properties .el-color-picker__trigger {
  background: #ffffff !important;
  border-color: #e3e3e3 !important;
  color: #344675 !important;
}

.white-content .properties-sidebar,
.white-content .builder-header,
.white-content .canvas-header,
.white-content .components-sidebar,
.white-content .button-properties .appearance-section,
.white-content .subsection-title,
.white-content .current-image-preview,
.white-content .style-preview.no-line {
  border-color: #e3e3e3;
}

.white-content .template-title {
  color: #344675;
}

/* Hover states repaint the same surfaces, so they need the light equivalent
   rather than the dark hover fill. */
.white-content .format-btn:hover,
.white-content .preset-btn:hover,
.white-content .align-btn:hover,
.white-content .button-properties .align-btn:hover,
.white-content .image-properties .align-btn:hover,
.white-content .size-btn:hover,
.white-content .thickness-btn:hover,
.white-content .toggle-btn:hover,
.white-content .style-option-btn:hover,
.white-content .canvas-tools i:hover {
  background: #f5f6fa !important;
}

/* The active device chip keeps its magenta icon, on a tint instead of the dark
   fill it uses in dark mode. */
.white-content .canvas-tools i.active {
  background: rgba(225, 78, 202, 0.12);
  color: #e14eca;
}

/* Range tracks and scrollbars read as gaps when they stay dark. */
.white-content .slider,
.white-content .footer-properties .slider,
.white-content .button-properties .slider,
.white-content .image-properties .slider,
.white-content .components-sidebar::-webkit-scrollbar-track,
.white-content .properties-sidebar::-webkit-scrollbar-track,
.white-content .canvas-area::-webkit-scrollbar-track {
  background: #e9ecef;
}

.white-content .components-sidebar::-webkit-scrollbar-thumb,
.white-content .properties-sidebar::-webkit-scrollbar-thumb,
.white-content .canvas-area::-webkit-scrollbar-thumb {
  background: #c8cbd4;
}

/* Text colors. The rules above repaint the surfaces; these repaint the copy on
   them, which is set in rgba white throughout and would otherwise be white on
   white. Three tiers matching the light theme: body, secondary, muted. */
.white-content .section-title,
.white-content .layout-option span,
.white-content .game-properties .layout-option span,
.white-content .gm-layout-option span,
.white-content .gift-card-properties .layout-option span,
.white-content .toggle-label,
.white-content .social-platform {
  color: #344675;
}

.white-content .format-btn,
.white-content .subsection-title {
  color: #525f7f;
}

.white-content .template-type,
.white-content .variable-item span,
.white-content .canvas-tools,
.white-content .custom-size .unit,
.white-content .width-input-group .unit,
.white-content .range-value,
.white-content .footer-properties .range-value,
.white-content .button-properties .range-value,
.white-content .image-properties .range-value,
.white-content .upload-help-text,
.white-content .help-text {
  color: #9a9a9a;
}

.white-content .footer-properties .form-control::placeholder,
.white-content .button-properties .form-control::placeholder,
.white-content .image-properties .form-control::placeholder {
  color: #9a9a9a !important;
}

.canvas-ai-prompt {
  margin: 0;
}

.canvas-content {
  position: relative;
}

/* The canvas scrolls, so a fixed-size overlay cannot cover it; instead the
   whole canvas is dimmed, blurred, and made inert while a pinned spinner
   floats in the scrollport. */
.canvas-content.generating .email-canvas {
  pointer-events: none;
  user-select: none;
  filter: blur(2px);
  opacity: 0.55;
  transition: opacity 0.2s ease;
}

.canvas-generating-veil {
  position: sticky;
  top: 120px;
  z-index: 20;
  height: 0;
  overflow: visible;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

/* Self contained pill so the label is legible over any template color in
   both themes; the canvas surface itself is always light */
.canvas-generating-veil-pill {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(30, 30, 47, 0.92);
  color: #ffffff;
  font-size: 0.9rem;
  padding: 16px 22px;
  border-radius: 999px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
}

.white-content .canvas-generating-veil-pill {
  background: rgba(255, 255, 255, 0.96);
  color: #344675;
  border: 1px solid #e3e3e3;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.canvas-generating-veil i {
  font-size: 1.3rem;
  color: #e14eca;
}

.ai-toggle {
  width: 26px;
  height: 26px;
  cursor: pointer;
  color: rgba(125, 125, 125, 0.9);
  transition: color 0.2s ease;
}

/* AI trigger sits left; device sizing icons are a different concern, pushed right */
.canvas-tools {
  display: flex;
  align-items: center;
  width: 100%;
}

.canvas-tools .device-icons-start {
  margin-left: auto;
}

.ai-toggle:hover,
.ai-toggle.active {
  color: #e14eca;
}

</style>

<style>
/* Smart values popover - unscoped because el-popover renders in body */
.smart-values-popover {
  background: #171826 !important;
  border: 1px solid #393c54 !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4) !important;
  padding: 0 !important;
  max-height: 500px;
  display: flex;
  flex-direction: column;
}

.smart-values-popover .popper__arrow {
  border-left-color: #393c54 !important;
}

.smart-values-popover .popper__arrow::after {
  border-left-color: #171826 !important;
}

.smart-values-popover .smart-values-panel {
  height: 480px;
}

.smart-values-popover .sv-list::-webkit-scrollbar {
  width: 4px;
}
.smart-values-popover .sv-list::-webkit-scrollbar-track {
  background: transparent;
}
.smart-values-popover .sv-list::-webkit-scrollbar-thumb {
  background: #393c54;
  border-radius: 2px;
}
.smart-values-popover .sv-list::-webkit-scrollbar-thumb:hover {
  background: #555770;
}
.smart-values-popover .sv-list {
  scrollbar-width: thin;
  scrollbar-color: #393c54 transparent;
}

/* Light mode. Unscoped like the rules above, because el-popover renders the
   panel on the body, outside this component's scope attribute. */
.white-content .smart-values-popover {
  background: #ffffff !important;
  border-color: #e3e3e3 !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12) !important;
}

.white-content .smart-values-popover .popper__arrow {
  border-left-color: #e3e3e3 !important;
}

.white-content .smart-values-popover .popper__arrow::after {
  border-left-color: #ffffff !important;
}

.white-content .smart-values-popover .sv-list::-webkit-scrollbar-thumb {
  background: #c8cbd4;
}

.white-content .smart-values-popover .sv-list::-webkit-scrollbar-thumb:hover {
  background: #9a9a9a;
}

.white-content .smart-values-popover .sv-list {
  scrollbar-color: #c8cbd4 transparent;
}
</style>
